import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Checkbox, Col, Empty, Progress, Row, Skeleton, Space, Table, Tabs, Tag, Form, Input, DatePicker, Modal, Upload, Dropdown, } from "antd";
import Select from "../../shared/components/searchable-select.component";
import {
  CheckCircleFilled,
  DeleteOutlined,
  DownOutlined,
  DownloadOutlined,
  FileTextOutlined, ExclamationCircleOutlined,
  EditOutlined,
  FolderOpenOutlined,
  GiftOutlined,
  LeftOutlined,
  LinkOutlined,
  PlusOutlined,
  StarFilled,
  UploadOutlined,
  BankOutlined,
  DollarOutlined,
  DownloadOutlined as ExportOutlined,
  TeamOutlined,
  UpOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import moment from "moment";
import { Country, State } from "country-state-city";
import { useForm } from "antd/es/form/Form"; // Correct import for useForm
import { useDispatch, useSelector } from "react-redux"; // Assuming useDispatch is from react-redux
import { Client } from "../../core/api-client";
import {
  API_COMMON_GET_BY_MODULE,
  API_GET_ALL_ACCOUNTS_LIST, API_GET_ALL_DEPARTMENT_LIST, API_GET_ALL_PROJECT_LIST, API_GET_ALL_STAFF_LIST, API_GET_ALL_VENDOR_LIST, API_GET_SUBDEPARTMENTS_BY_DEPARTMENT, API_GET_ACCOUNT_LIST_BY_ENTITY,
  API_GET_ACTIVITY_LOG, API_UPDATE_STAFF,
  API_GET_STAFF_DETAILS,
  API_GET_STAFF_EMPLOYEE_ID_HISTORY,
  API_GET_ALL_DOCUMENT_TYPE_ACTIVELIST,
  API_GET_DOCUMENTS,
  API_GET_ALL_DOCUMENT_SUB_TYPE_LIST,
  API_DELETE_DOCUMENT,
  API_GET_DOCUMENT_SIGNED_URL,
  API_RESOLVE_STAFF_EMPLOYEE_ID,
  ROUTE_MANAGE_STAFF,
  ROUTE_STAFF_PROFILE,
} from "../../shared/routes/private.routes";
import { formatDate, showNotification } from "../../shared/utils/utils";
import { DocumentType } from "../../shared/types/common";

import CreateEditStaffComponent from "./components/create-edit-staff.component";
import { defaultStaffProfileMock } from "./data/staff-profile.mock";
import { employeeOverviewMock } from "./data/employee-overview.mock";
import { NATIONALITY_OPTIONS } from "./data/nationality-options";
import {
  StaffProfileDocumentCategory,
  StaffProfileDependent,
  StaffProfileField,
  StaffProfileMetric,
  CareerHistoryEvent,
  EmployeeOverview,
  StaffProfilePositionHistory,
  StaffProfilePreviousEmployment,
  StaffProfileProjectTimeline,
  StaffProfileTabKey,
} from "./types/staff-profile.types"; // Ensure this path is correct
import "./staff-profile.less"; // Assuming this is the correct path for the less file
import { accessAllowed } from "../../shared/utils/utils";
import { AppDispatch, RootState } from "../../core/store"; // Assuming AppDispatch is exported from your store configuration
import { updateStaff } from "../../core/store/reducers/staff.slice";
import UploadDocumentModal from "./components/upload-document-modal.component";
import { NavigationBlockerContext } from "../../App";
import { allPermissions } from "../../shared/constants/constants";
import { missingStaffFields } from "../dashboard/missing-fields";

const { TabPane } = Tabs;

// Tabs commented out below are hidden until the system has real data for them.
// Their render functions and `renderTab` cases are left intact - uncomment the
// line here to bring a tab back, nothing else needs to change.



const emptyStaff = {
  staffName: "",
  employeeId: "",
  staffStatusId: "",
};

export const normalizeEmployeeIdLinks = (payload: any = {}) => {
  const primaryStaffId = firstRealValue(
    payload?.primaryStaffId,
    payload?.primaryStaff?.staffId,
    payload?.staffId,
    payload?.currentStaffId
  );
  const primaryEmployeeId = firstRealValue(
    payload?.primaryEmployeeId,
    payload?.primaryStaff?.employeeId,
    payload?.employeeId,
    payload?.currentEmployeeId
  );
  const rawLinks = Array.isArray(payload?.records)
    ? payload.records
    : Array.isArray(payload?.linkedEmployeeIds)
      ? payload.linkedEmployeeIds
      : Array.isArray(payload?.employeeIdHistory)
        ? payload.employeeIdHistory
        : Array.isArray(payload?.linkedStaffs)
          ? payload.linkedStaffs
          : [];

  const normalized = new Map<string, any>();

  const addLink = (staffId: any, employeeId: any, relationship: string = "CHILD", isPrimary: boolean = false) => {
    if (!employeeId || !staffId) return;
    const key = `${String(staffId)}::${String(employeeId)}`;
    if (normalized.has(key)) return;
    normalized.set(key, {
      staffId: String(staffId),
      employeeId: String(employeeId),
      relationship: relationship || (isPrimary ? "PRIMARY" : "CHILD"),
      isPrimary,
    });
  };

  if (primaryEmployeeId && primaryStaffId) {
    addLink(primaryStaffId, primaryEmployeeId, "PRIMARY", true);
  }

  rawLinks.forEach((item: any, index: number) => {
    const relationship = firstRealValue(item?.relationship, item?.type, item?.status, item?.linkType, item?.kind);
    const rawEmployeeId = firstRealValue(
      item?.employeeId,
      item?.employee_number,
      item?.employeeNumber,
      item?.linkedEmployeeId,
      item?.linkedEmployeeNumber,
      item?.id,
      item?.employee_code,
      item?.employeeCode,
      item?.currentEmployeeId
    );
    const rawStaffId = firstRealValue(
      item?.staffId,
      item?.linkedStaffId,
      item?.id,
      item?.staff_id,
      item?.linkedStaff?.staffId,
      item?.primaryStaffId,
      item?.primaryStaff?.staffId,
      index
    );

    if (rawEmployeeId && rawStaffId) {
      const isPrimary = String(relationship || "").toUpperCase() === "PRIMARY" || String(rawStaffId) === String(primaryStaffId);
      addLink(rawStaffId, rawEmployeeId, String(relationship || "").toUpperCase() || (isPrimary ? "PRIMARY" : "CHILD"), isPrimary);
    }
  });

  const ordered = Array.from(normalized.values()).sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    return a.employeeId.localeCompare(b.employeeId);
  });

  return ordered;
};

// Kept in sync with create-edit-staff.component.tsx so the inline profile edit
// enforces exactly the same rules as the create/edit drawer.
const NOTICE_PERIOD_STATUS = "NOTICE PERIOD";
const DEFAULT_STAFF_EMAIL_DOMAIN = "@roboticodigital.com";
const INNOTHRUST_EMAIL_DOMAIN = "@innothrust.com";
const INNOTHRUST_ENTITY_NAME = "innothrust";
const VENDOR_CONSULTANT_MODE = "Vendor Consultant";
const PERMANENT_EMPLOYMENT_MODE = "Permanent";
const normalizeModeName = (value: any) => String(value || "").trim().toLowerCase();
const WORK_LOCATION_OPTIONS = [
  { label: "Client", value: "Client" },
  { label: "WFH", value: "WFH" },
  { label: "WFO", value: "WFO" },
  { label: "Hybrid", value: "Hybrid" },
];
const INDIA_OFFICE_LOCATION_OPTIONS = [
  { label: "Bangalore", value: "Bangalore" },
  { label: "Coimbatore", value: "Coimbatore" },
];
const MALAYSIA_OFFICE_LOCATION_OPTIONS = [
  { label: "Kuala Lumpur", value: "Kuala Lumpur" },
];
const GRADE_BAND_OPTIONS = [
  { label: "A+", value: "A+" },
  { label: "A", value: "A" },
  { label: "B+", value: "B+" },
  { label: "B", value: "B" },
  { label: "C+", value: "C+" },
  { label: "C", value: "C" },
  { label: "D", value: "D" },
];
const NATIVE_LANGUAGE_OPTIONS = [
  "Kannada",
  "Tamil",
  "Telugu",
  "Malayalam",
  "Marathi",
  "Punjabi",
  "Bengali",
  "Other",
].map((language) => ({ label: language, value: language }));

const mock = defaultStaffProfileMock;


const getValidOptionValue = (value: any, options: { value: string }[]) =>
  options.some((option) => option.value === value) ? value : undefined;

const getOfficeLocationOptionsForEntity = (entityName: any) =>
  /malaysia|myr/i.test(String(entityName))
    ? MALAYSIA_OFFICE_LOCATION_OPTIONS
    : INDIA_OFFICE_LOCATION_OPTIONS;

const textOnlyRule = (fieldName: string) => ({
  pattern: /^[A-Za-z\s.'/-]+$/,
  message: `Please enter a valid ${fieldName}. Only letters, spaces, apostrophes ('), dots (.), hyphens (-), and slashes (/) are allowed.`,
});

const numberOnlyRule = (fieldName: string) => ({
  pattern: /^\d+$/,
  message: `Please enter a valid ${fieldName}. Only numbers are allowed.`,
});

type StaffProfileInputPolicy =
  | "alpha"
  | "number"
  | "alphanumeric"
  | "freeText"
  | "amount"
  | "email"
  | "phone"
  | "aadhaar"
  | "pan"
  | "pfMemberId"
  | "myKad"
  | "taxId"
  | "epfLike";

const DEFAULT_TEXT_MAX_LENGTH = 50;
const DEFAULT_NUMBER_MAX_LENGTH = 2;
const MAX_SKILLS_PER_CATEGORY = 15;
const SALARY_REVISION_DOCUMENT_TYPE = "SALARY_REVISION";
const BONUS_DOCUMENT_TYPE = "BONUS";
const EXIT_INFORMATION_DOCUMENT_TYPE = "EXIT_INFORMATION";
const PASSPORT_COPY_DOCUMENT_TYPE = "PASSPORT_COPY";
const VISA_COPY_DOCUMENT_TYPE = "VISA_COPY";
const VISA_TYPE_OPTIONS = [
  { label: "EP (Employment Pass)", value: "EP" },
  { label: "RPT (Residence Pass–Talent)", value: "RPT" },
];
const EXIT_INFORMATION_RECORD_ID = "exit-information";

const createLocalRecordId = (prefix: string) => {
  const randomId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}-${randomId}`;
};

const createLegacyRecordId = (prefix: string, parts: any[], index: number) =>
  `${prefix}-legacy-${parts.map((part) => String(part || "empty").replace(/[^A-Za-z0-9-]/g, "-")).join("-")}-${index}`;

const sanitizeAmount = (value: any) => {
  const rawValue = String(value ?? "").replace(/[^\d.]/g, "");
  const [whole = "", ...decimalParts] = rawValue.split(".");
  const decimals = decimalParts.join("").slice(0, 2);
  return decimalParts.length ? `${whole}.${decimals}` : whole;
};

const sanitizeStaffInput = (
  value: any,
  policy: StaffProfileInputPolicy,
  maxLength?: number
) => {
  const rawValue = String(value ?? "");
  const length = maxLength ?? DEFAULT_TEXT_MAX_LENGTH;

  switch (policy) {
    case "alpha":
      return rawValue.replace(/[^A-Za-z\s]/g, "").slice(0, length);
    case "number":
      return rawValue.replace(/\D/g, "").slice(0, maxLength ?? DEFAULT_NUMBER_MAX_LENGTH);
    case "alphanumeric":
      return rawValue.replace(/[^A-Za-z0-9]/g, "").slice(0, length);
    case "freeText":
      return rawValue.replace(/[^A-Za-z0-9\s.,'/#&-]/g, "").slice(0, length);
    case "amount":
      return sanitizeAmount(rawValue);
    case "email":
      return rawValue.replace(/\s/g, "").slice(0, length);
    case "phone":
      return rawValue.replace(/\D/g, "").slice(0, maxLength ?? 15);
    case "aadhaar":
      return formatAadhaar(rawValue);
    case "pan":
      return rawValue.replace(/[^A-Za-z0-9]/g, "").slice(0, 10).toUpperCase();
    case "pfMemberId":
      return rawValue.replace(/[^A-Za-z0-9/]/g, "").slice(0, 27).toUpperCase();
    case "myKad":
      return rawValue.replace(/[^A-Za-z\d-]/g, "").slice(0, 14).toUpperCase();
    case "taxId":
      return rawValue.replace(/[^A-Za-z0-9-]/g, "").slice(0, 15).toUpperCase();
    case "epfLike":
      return rawValue.replace(/[^A-Za-z0-9/-]/g, "").slice(0, length).toUpperCase();
    default:
      return rawValue.slice(0, length);
  }
};

const getStaffInputPolicy = (fieldKey: string): StaffProfileInputPolicy => {
  if (["previousSalary", "bonusAmount", "ctc", "costToRd"].includes(fieldKey)) return "amount";
  if (fieldKey === "aadhaar") return "aadhaar";
  if (fieldKey === "pan") return "pan";
  if (fieldKey === "pfMemberId") return "pfMemberId";
  if (fieldKey === "myKadNumber") return "myKad";
  if (fieldKey === "taxIdentificationNumber") return "taxId";
  if (["epfNumber", "eisNumber"].includes(fieldKey)) return "epfLike";
  if (["email", "personalEmail"].includes(fieldKey)) return "email";
  if (
    [
      "noticePeriod",
      "yearOfPassing",
      "communicationPostalCode",
      "permanentPostalCode",
      "indiaBankAccountNumber",
      "malaysiaBankAccountNumber",
      "uan",
      "esicIpNumber",
      "socsoNumber",
    ].includes(fieldKey)
  ) {
    return "number";
  }
  if (
    [
      "employeeId",
      "vendorEmpId",
      "passportNumber",
      "indiaIfscCode",
      "malaysiaSwiftBicCode",
    ].includes(fieldKey)
  ) {
    return "alphanumeric";
  }
  if (
    [
      "communicationAddressLine",
      "permanentAddressLine",
    ].includes(fieldKey)
  ) {
    return "freeText";
  }
  return "alpha";
};

const getStaffInputMaxLength = (fieldKey: string, configuredMaxLength?: number) => {
  if (configuredMaxLength) return configuredMaxLength;
  if (fieldKey === "aadhaar") return 14;
  if (fieldKey === "uan") return 12;
  if (fieldKey === "pan") return 10;
  if (fieldKey === "noticePeriod") return 3;
  if (fieldKey === "yearOfPassing") return 4;
  if (["communicationPostalCode", "permanentPostalCode"].includes(fieldKey)) return 7;
  if (["indiaBankAccountNumber", "malaysiaBankAccountNumber"].includes(fieldKey)) return 18;
  if (fieldKey === "esicIpNumber") return 10;
  if (fieldKey === "socsoNumber") return 12;
  if (fieldKey === "myKadNumber") return 14;
  if (["previousSalary", "bonusAmount", "ctc", "costToRd"].includes(fieldKey)) return undefined;
  return DEFAULT_TEXT_MAX_LENGTH;
};

const valueOrFallback = (value: any, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

const dateOrFallback = (value: any, fallback = "-") => {
  if (!value || !moment(value).isValid()) return fallback;
  return formatDate(value, "DD-MMM-YYYY");
};

const toFormDate = (value: any) => {
  if (value === null || value === undefined || value === "") return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }
  if (typeof value === "string" || typeof value === "number") {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }
  return null;
};

const sanitizeInitialStaffFormDates = (source: Record<string, any> = {}) => {
  const dateFields = [
    "doj",
    "probationEndDate",
    "contractStartDate",
    "contractEndDate",
    "lwd",
    "resignationDate",
    "previousSalaryRevisionDate",
    "nextReviewDate",
    "nextSalaryRevisionDate",
    "nextPerformanceReviewDate",
    "nextAppraisalSalaryRevisionDate",
    "passportExpiryDate",
    "visaExpiryDate",
    "effectiveFrom",
    "issuedDate",
    "dateOfBirth",
    "projectReleaseDate",
    "currentProjectStartDate",
    "projectStartDate",
    "internStartDate",
    "internEndDate",
    "traineeStartDate",
    "traineeEndDate",
  ];

  const safeSource = { ...source };
  dateFields.forEach((fieldKey) => {
    if (Object.prototype.hasOwnProperty.call(safeSource, fieldKey)) {
      safeSource[fieldKey] = toFormDate(safeSource[fieldKey]);
    }
  });

  return safeSource;
};

const firstRealValue = (...values: any[]) =>
  values.find((value) => value !== null && value !== undefined && value !== "");

const toBooleanValue = (value: any) =>
  value === true || value === "true" || value === "Yes" || value === 1 || value === "1";

const getDateSortValue = (value: any) =>
  value && dayjs(value).isValid() ? dayjs(value).valueOf() : -Infinity;

const isUuidLike = (value: any) =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value
  );

const formatDays = (value: any) => {
  const realValue = firstRealValue(value);
  return realValue ? `${realValue} days` : "-";
};

const parseDependents = (value: any): StaffProfileDependent[] => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parsePositionHistory = (value: any): StaffProfilePositionHistory[] => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parseProjectTimeline = (value: any): StaffProfileProjectTimeline[] => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parseStringArray = (value: any): string[] => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
  } catch {
    return value.split(",").map((item) => item.trim()).filter(Boolean);
  }
};

type StaffProfileSkillDetail = {
  skill?: string;
  proficiencyLevel?: string;
};

const toArray = (value: any) => (Array.isArray(value) ? value : []);

const normalizeSkillItems = (rows: any[] = []) =>
  toArray(rows)
    .filter((row) => row?.skill || row?.proficiencyLevel)
    .map((row) => ({
      skill: row?.skill || "",
      proficiencyLevel: row?.proficiencyLevel || "",
    }));

const parseSkillDetails = (value: any): {
  primarySkills: StaffProfileSkillDetail[];
  secondarySkills: StaffProfileSkillDetail[];
} => {
  const normalizeParsedValue = (parsedValue: any) => {
    if (Array.isArray(parsedValue)) {
      return {
        primarySkills: normalizeSkillItems(
          parsedValue
            .filter((row) => row?.primarySkill || row?.primaryProficiencyLevel)
            .map((row) => ({
              skill: row?.primarySkill,
              proficiencyLevel: row?.primaryProficiencyLevel,
            }))
        ),
        secondarySkills: normalizeSkillItems(
          parsedValue
            .filter((row) => row?.secondarySkill || row?.secondaryProficiencyLevel)
            .map((row) => ({
              skill: row?.secondarySkill,
              proficiencyLevel: row?.secondaryProficiencyLevel,
            }))
        ),
      };
    }

    return {
      primarySkills: normalizeSkillItems(parsedValue?.primarySkills),
      secondarySkills: normalizeSkillItems(parsedValue?.secondarySkills),
    };
  };

  if (Array.isArray(value) || (value && typeof value === "object")) {
    return normalizeParsedValue(value);
  }
  if (!value || typeof value !== "string") {
    return { primarySkills: [], secondarySkills: [] };
  }
  try {
    const parsed = JSON.parse(value);
    return normalizeParsedValue(parsed);
  } catch {
    return { primarySkills: [], secondarySkills: [] };
  }
};

const normalizeSkillDetails = (value: any) => {
  const parsed = parseSkillDetails(value);
  return {
    primarySkills: normalizeSkillItems(parsed.primarySkills),
    secondarySkills: normalizeSkillItems(parsed.secondarySkills),
  };
};

const buildSkillDetails = (
  skillDetails: any,
  primarySkills: any,
  secondarySkills: any,
  skillProficiencyLevel: any
) => {
  const parsedDetails = normalizeSkillDetails(skillDetails);
  if (parsedDetails.primarySkills.length || parsedDetails.secondarySkills.length) {
    return parsedDetails;
  }

  const primary = parseStringArray(primarySkills);
  const secondary = parseStringArray(secondarySkills);
  return {
    primarySkills: primary.map((skill) => ({
      skill,
      proficiencyLevel: valueOrFallback(skillProficiencyLevel, ""),
    })),
    secondarySkills: secondary.map((skill) => ({
      skill,
      proficiencyLevel: valueOrFallback(skillProficiencyLevel, ""),
    })),
  };
};

const formatSkillItemsSummary = (rows: any[]) => {
  const normalized = normalizeSkillItems(rows);
  if (!normalized.length) return "-";
  return normalized
    .map((row) =>
      row.skill
        ? `${row.skill}${row.proficiencyLevel ? ` (${row.proficiencyLevel})` : ""}`
        : ""
    )
    .filter(Boolean)
    .join(", ");
};

const formatSkillDetailsSummary = (value: any) => {
  const normalized = normalizeSkillDetails(value);
  const primarySummary = formatSkillItemsSummary(normalized.primarySkills);
  const secondarySummary = formatSkillItemsSummary(normalized.secondarySkills);
  if (primarySummary === "-" && secondarySummary === "-") return "-";
  return `Primary: ${primarySummary}; Secondary: ${secondarySummary}`;
};

const parsePreviousEmploymentHistory = (value: any): StaffProfilePreviousEmployment[] => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeDependents = (dependents: any[] = []) =>
  toArray(dependents)
    .filter((dependent) =>
      ["name", "relationship", "dateOfBirth"].some(
        (key) => dependent?.[key]
      )
    )
    .map((dependent) => ({
      ...dependent,
      name: dependent?.name || "",
      relationship: dependent?.relationship || "",
      // Keep the API value as a date string for display and payload comparison.
      // Passing a Day.js instance to Moment (used by dateOrFallback) can make
      // Moment construct a partial date using today's values.
      dateOfBirth:
        dependent?.dateOfBirth && typeof dependent.dateOfBirth?.format === "function"
          ? dependent.dateOfBirth.format("YYYY-MM-DD")
          : dependent?.dateOfBirth || null,
      coveredInInsurance: dependent?.coveredInInsurance || "No",
      passportNumber: dependent?.passportNumber || "",
      passportExpiryDate:
        dependent?.passportExpiryDate && typeof dependent.passportExpiryDate?.format === "function"
          ? dependent.passportExpiryDate.format("YYYY-MM-DD")
          : dependent?.passportExpiryDate || null,
      visaType: dependent?.visaType || "",
      visaExpiryDate:
        dependent?.visaExpiryDate && typeof dependent.visaExpiryDate?.format === "function"
          ? dependent.visaExpiryDate.format("YYYY-MM-DD")
          : dependent?.visaExpiryDate || null,
    }));

const toDependentsFormValues = (dependents: any[] = []) =>
  normalizeDependents(dependents).map((dependent) => ({
    ...dependent,
    dateOfBirth:
      dependent.dateOfBirth && dayjs(dependent.dateOfBirth).isValid()
        ? dayjs(dependent.dateOfBirth)
        : null,
    passportExpiryDate:
      dependent.passportExpiryDate && dayjs(dependent.passportExpiryDate).isValid()
        ? dayjs(dependent.passportExpiryDate)
        : null,
    visaExpiryDate:
      dependent.visaExpiryDate && dayjs(dependent.visaExpiryDate).isValid()
        ? dayjs(dependent.visaExpiryDate)
        : null,
  }));

const normalizePositionHistory = (history: any[] = []) =>
  toArray(history)
    .filter((item) =>
      ["effectiveFrom", "designation", "reportingTo"].some(
        (key) => item?.[key]
      )
    )
    .map((item) => ({
      effectiveFrom:
        item?.effectiveFrom && typeof item.effectiveFrom?.format === "function"
          ? item.effectiveFrom.format("YYYY-MM-DD")
          : item?.effectiveFrom && dayjs(item.effectiveFrom).isValid()
            ? dayjs(item.effectiveFrom).format("YYYY-MM-DD")
            : item?.effectiveFrom || "",
      designation: item?.designation || "",
      reportingTo: item?.reportingTo || "",
      createdDate: item?.createdDate || new Date().toISOString(),

    }))
    .sort(
      (a, b) =>
        getDateSortValue(b.effectiveFrom) - getDateSortValue(a.effectiveFrom)
    );

const parseSalaryRevisionHistory = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeSalaryRevisionHistory = (history: any[] = []) =>
  toArray(history)
    .filter((item) =>
      [
        "effectiveFrom",
        "previousSalary",
        "nextSalaryRevisionDate",
      ].some((key) => item?.[key])
    )
    .map((item, index) => {
      const effectiveFrom =
        item?.effectiveFrom && typeof item.effectiveFrom?.format === "function"
          ? item.effectiveFrom.format("YYYY-MM-DD")
          : item?.effectiveFrom && dayjs(item.effectiveFrom).isValid()
            ? dayjs(item.effectiveFrom).format("YYYY-MM-DD")
            : item?.effectiveFrom || "";
      const previousSalary = item?.previousSalary || "";
      const nextSalaryRevisionDate =
        item?.nextSalaryRevisionDate && typeof item.nextSalaryRevisionDate?.format === "function"
          ? item.nextSalaryRevisionDate.format("YYYY-MM-DD")
          : item?.nextSalaryRevisionDate && dayjs(item.nextSalaryRevisionDate).isValid()
            ? dayjs(item.nextSalaryRevisionDate).format("YYYY-MM-DD")
            : item?.nextSalaryRevisionDate || "";

      return {
        id: item?.id || createLegacyRecordId("salary-revision", [effectiveFrom, previousSalary], index),
        previousSalaryRevisionDate: "",
        previousSalaryRevisionAmount: "",
        effectiveFrom,
        previousSalary,
        nextSalaryRevisionDate,
        createdDate: item?.createdDate || new Date().toISOString(),
      };
    })
    .sort(
      (a, b) =>
        getDateSortValue(b.effectiveFrom) - getDateSortValue(a.effectiveFrom)
    );

const buildSalaryRevisionHistory = (staffData: any) => {
  const normalized = normalizeSalaryRevisionHistory(
    parseSalaryRevisionHistory(staffData?.salaryRevisionHistory)
  );
  if (normalized.length) return normalized;
  return normalizeSalaryRevisionHistory([
    {
      effectiveFrom: staffData?.effectiveFrom,
      previousSalary: staffData?.previousSalary,
      nextSalaryRevisionDate: staffData?.nextSalaryRevisionDate,
    },
  ]);
};

const toSalaryRevisionFormValues = (history: any[] = []) =>
  normalizeSalaryRevisionHistory(toArray(history)).map((item) => ({
    ...item,
    effectiveFrom: item.effectiveFrom ? dayjs(item.effectiveFrom) : null,
    nextSalaryRevisionDate: item.nextSalaryRevisionDate
      ? dayjs(item.nextSalaryRevisionDate)
      : null,
  }));

const parseBonusHistory = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeBonusHistory = (history: any[] = []) =>
  toArray(history)
    .filter((item) => ["bonusAmount", "issuedDate"].some((key) => item?.[key]))
    .map((item, index) => {
      const bonusAmount = item?.bonusAmount || "";
      const issuedDate =
        item?.issuedDate && typeof item.issuedDate?.format === "function"
          ? item.issuedDate.format("YYYY-MM-DD")
          : item?.issuedDate && dayjs(item.issuedDate).isValid()
            ? dayjs(item.issuedDate).format("YYYY-MM-DD")
            : item?.issuedDate || "";

      return {
        id: item?.id || createLegacyRecordId("bonus", [issuedDate, bonusAmount], index),
        bonusAmount,
        issuedDate,
        createdDate: item?.createdDate || new Date().toISOString(),
      };
    })
    .sort((a, b) => getDateSortValue(b.issuedDate) - getDateSortValue(a.issuedDate));

const formatSalaryRevisionHistoryChange = (history: any) => {
  const rows = normalizeSalaryRevisionHistory(history);
  if (!rows.length) return "-";
  return rows
    .map((row, index) => {
      const parts = [
        row.previousSalary ? `Salary: ${formatDecimalAmount(row.previousSalary)}` : "",
        row.effectiveFrom ? `Effective From: ${dateOrFallback(row.effectiveFrom)}` : "",
      ].filter(Boolean);
      return parts.length ? `${index + 1}. ${parts.join(", ")}` : "";
    })
    .filter(Boolean)
    .join("; ");
};

const formatBonusHistoryChange = (history: any) => {
  const rows = normalizeBonusHistory(history);
  if (!rows.length) return "-";
  return rows
    .map((row, index) => {
      const parts = [
        row.bonusAmount ? `Amount: ${formatDecimalAmount(row.bonusAmount)}` : "",
        row.issuedDate ? `Issued Date: ${dateOrFallback(row.issuedDate)}` : "",
      ].filter(Boolean);
      return parts.length ? `${index + 1}. ${parts.join(", ")}` : "";
    })
    .filter(Boolean)
    .join("; ");
};

const buildBonusHistory = (staffData: any) => {
  const normalized = normalizeBonusHistory(parseBonusHistory(staffData?.bonusHistory));
  if (normalized.length) return normalized;
  return normalizeBonusHistory([
    {
      bonusAmount: staffData?.bonusAmount,
      issuedDate: staffData?.issuedDate,
    },
  ]);
};

const toBonusFormValues = (history: any[] = []) =>
  normalizeBonusHistory(toArray(history)).map((item) => ({
    ...item,
    issuedDate: item.issuedDate ? dayjs(item.issuedDate) : null,
  }));

const syncCompensationLegacyFields = (formState: any) => {
  const salaryRows = normalizeSalaryRevisionHistory(formState.salaryRevisionHistory);
  const latestSalaryRevision = salaryRows[0];
  formState.salaryRevisionHistory = salaryRows;
  formState.previousSalaryRevisionDate = null;
  formState.previousSalaryRevisionAmount = null;
  formState.effectiveFrom = latestSalaryRevision?.effectiveFrom || null;
  formState.previousSalary = latestSalaryRevision?.previousSalary || null;
  formState.ctc = calculateCtcFromSalaryRevisionHistory(salaryRows) ?? formState.ctc ?? null;

  const bonusRows = normalizeBonusHistory(formState.bonusHistory);
  const latestBonus = bonusRows[0];
  formState.bonusHistory = bonusRows;
  formState.bonusAmount = latestBonus?.bonusAmount || null;
  formState.issuedDate = latestBonus?.issuedDate || null;
};

const normalizeProjectTimeline = (timeline: any[] = []) =>
  toArray(timeline)
    .filter((item) =>
      ["date", "accountId", "accountName"].some(
        (key) => item?.[key]
      )
    )
    .map((item) => ({
      date:
        item?.date && typeof item.date?.format === "function"
          ? item.date.format("YYYY-MM-DD")
          : item?.date && dayjs(item.date).isValid()
            ? dayjs(item.date).format("YYYY-MM-DD")
            : item?.date || "",
      accountId: item?.accountId || "",
      projectId: item?.projectId || "",
      accountName: item?.accountName || "",
      projectName: item?.projectName || "",
      createdDate: item?.createdDate || new Date().toISOString(),
    }))
    .sort((a, b) => getDateSortValue(b.date) - getDateSortValue(a.date));

const normalizePreviousEmploymentHistory = (history: any[] = []) =>
  toArray(history)
    .filter((item) =>
      [
        "previousEmployerName",
        "previousDesignation",
        "durationYears",
        "durationMonths",
      ].some((key) => item?.[key])
    )
    .map((item) => ({
      previousEmployerName: item?.previousEmployerName || "",
      previousDesignation: item?.previousDesignation || "",
      durationYears: item?.durationYears || "",
      durationMonths: item?.durationMonths || "",
    }));

const normalizeComparableValue = (value: any, type: string, fieldKey?: string) => {
  if (fieldKey === "aadhaar") {
    return unformatAadhaar(value) || null;
  }
  if (type === "date") {
    return value ? dayjs(value).format("YYYY-MM-DD") : null;
  }
  if (type === "select") {
    return value === undefined ? null : value;
  }
  if (type === "multiselect" || type === "tags") {
    return JSON.stringify(Array.isArray(value) ? value : []);
  }
  if (type === "checkbox") {
    return Boolean(value);
  }
  if (type === "json") {
    return JSON.stringify(normalizeDependents(parseDependents(value)));
  }
  if (type === "positionHistory") {
    return JSON.stringify(normalizePositionHistory(parsePositionHistory(value)));
  }
  if (type === "salaryRevisionHistory") {
    return JSON.stringify(normalizeSalaryRevisionHistory(parseSalaryRevisionHistory(value)));
  }
  if (type === "bonusHistory") {
    return JSON.stringify(normalizeBonusHistory(parseBonusHistory(value)));
  }
  if (type === "projectTimeline") {
    return JSON.stringify(normalizeProjectTimeline(parseProjectTimeline(value)));
  }
  if (type === "previousEmploymentHistory") {
    return JSON.stringify(normalizePreviousEmploymentHistory(parsePreviousEmploymentHistory(value)));
  }
  if (type === "skillDetails") {
    return JSON.stringify(normalizeSkillDetails(parseSkillDetails(value)));
  }
  return value === null || value === undefined || value === "" ? null : String(value);
};

const normalizeDatePayloadValue = (value: any) => {
  if (!value) return null;
  if (typeof value?.format === "function") return value.format("YYYY-MM-DD");
  return dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : value;
};

const formatAadhaar = (value: string) => {
  if (!value) return "";
  const digitsOnly = value.replace(/\D/g, "");
  return digitsOnly
    .slice(0, 12)
    .replace(/(\d{4})/g, "$1 ")
    .trim();
};

const unformatAadhaar = (value: any) => String(value || "").replace(/\D/g, "");
const formatDependentsSummary = (dependents: any[]) => {
  const normalized = normalizeDependents(dependents);
  if (normalized.length === 0) return "-";
  return normalized
    .map((dependent) =>
      [dependent.name, dependent.relationship].filter(Boolean).join(" - ")
    )
    .join(", ");
};

const formatPositionHistorySummary = (history: any[]) => {
  const normalized = normalizePositionHistory(history);
  if (normalized.length === 0) return "-";
  return normalized
    .map((item) =>
      [item.effectiveFrom, item.designation].filter(Boolean).join(" - ")
    )
    .join(", ");
};

const formatProjectTimelineSummary = (timeline: any[]) => {
  const normalized = normalizeProjectTimeline(timeline);
  if (normalized.length === 0) return "-";
  return normalized
    .map((item) =>
      [
        item.date,
        item.accountName,
      ]
        .filter(Boolean)
        .join(" - ")
    )
    .join(", ");
};

const formatPreviousEmploymentSummary = (history: any[]) => {
  const normalized = normalizePreviousEmploymentHistory(history);
  if (normalized.length === 0) return "-";
  return normalized
    .map((item) =>
      [item.previousEmployerName, item.previousDesignation]
        .filter(Boolean)
        .join(" - ")
    )
    .join(", ");
};

const getPreviousEmploymentTotalMonths = (history: any[]) =>
  normalizePreviousEmploymentHistory(parsePreviousEmploymentHistory(history)).reduce(
    (total, item) =>
      total +
      Number(item.durationYears || 0) * 12 +
      Number(item.durationMonths || 0),
    0
  );

const formatExperienceFromMonths = (totalMonths: number) => {
  if (!Number.isFinite(totalMonths) || totalMonths <= 0) return "0 mo";
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  const yearText = years ? `${years} yr${years > 1 ? "s" : ""}` : "";
  const monthText = months ? `${months} mo${months > 1 ? "s" : ""}` : "";
  return [yearText, monthText].filter(Boolean).join(" ") || "0 mo";
};

const getTenureMonths = (doj: any, lwd?: any) => {
  if (!doj || !moment(doj).isValid()) return 0;
  return Math.max(
    0,
    (lwd && moment(lwd).isValid() ? moment(lwd) : moment()).diff(moment(doj), "months")
  );
};

const toPositionHistoryFormValues = (history: any[] = []) =>
  normalizePositionHistory(history).map((item) => ({
    ...item,
    effectiveFrom: item.effectiveFrom && dayjs(item.effectiveFrom).isValid()
      ? dayjs(item.effectiveFrom)
      : null,
  }));

const toProjectTimelineFormValues = (timeline: any[] = []) =>
  normalizeProjectTimeline(timeline).map((item) => ({
    ...item,
    date: item.date && dayjs(item.date).isValid() ? dayjs(item.date) : null,
  }));



const formatMoney = (amount: any, currency: any) => {
  const realAmount = firstRealValue(amount);
  if (!realAmount) return "-";
  const currencyText =
    typeof currency === "object"
      ? firstRealValue(currency?.name, currency?.code, currency?.id)
      : currency;
  const displayCurrency =
    currencyText && currencyText !== "-" && !isUuidLike(currencyText)
      ? currencyText
      : "";
  return displayCurrency ? `${displayCurrency} ${realAmount}` : `${realAmount}`;
};

const parseNumericAmount = (amount: any) => {
  const realAmount = firstRealValue(amount);
  if (realAmount === undefined) return null;
  const numericAmount = Number(String(realAmount).replace(/,/g, ""));
  return Number.isFinite(numericAmount) ? numericAmount : null;
};

const formatDecimalAmount = (amount: any) => {
  const realAmount = firstRealValue(amount);
  if (realAmount === undefined) return "-";
  const numericAmount = parseNumericAmount(realAmount);
  return numericAmount !== null
    ? new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount)
    : valueOrFallback(realAmount);
};

const parseAmountValue = (amount: any) => {
  const realAmount = firstRealValue(amount);
  if (realAmount === undefined) return null;
  const numericAmount = Number(String(realAmount).replace(/,/g, "").match(/-?\d+(\.\d+)?/)?.[0]);
  return Number.isFinite(numericAmount) ? numericAmount : null;
};

const calculateCtcFromSalaryRevisionHistory = (history: any[] = []) => {
  const rows = normalizeSalaryRevisionHistory(history);
  const latestRowWithAmount = rows
    .find((row) => parseAmountValue(row.previousSalary) !== null);

  if (!latestRowWithAmount) return null;

  return parseAmountValue(latestRowWithAmount.previousSalary);
};

const calculateTenure = (doj: any, lwd?: any) => {
  if (!doj || !moment(doj).isValid()) return "-";
  const start = moment(doj);
  const end = lwd && moment(lwd).isValid() ? moment(lwd) : moment();
  const years = end.diff(start, "years");
  start.add(years, "years");
  const months = end.diff(start, "months");
  const yearText = years ? `${years} yr${years > 1 ? "s" : ""}` : "";
  const monthText = months ? `${months} mo${months > 1 ? "s" : ""}` : "";
  return [yearText, monthText].filter(Boolean).join(" ") || "0 mo";
};

/** Current Indian financial year (April - March), e.g. "FY 2026-27". */
const getCurrentFinancialYear = () => {
  const now = moment();
  const startYear = now.month() >= 3 ? now.year() : now.year() - 1;
  return `FY ${startYear}-${String(startYear + 1).slice(-2)}`;
};

// const COUNTRY_CODES = [
//   { label: "IN (+91)", value: "+91", pattern: /^\d{10}$/ },
//   { label: "US (+1)", value: "+1", pattern: /^\d{10}$/ },
//   { label: "UK (+44)", value: "+44", pattern: /^\d{10}$/ },
//   { label: "AU (+61)", value: "+61", pattern: /^\d{9}$/ },
//   { label: "MY (+60)", value: "+60", pattern: /^\d{9,10}$/ },
//   { label: "SG (+65)", value: "+65", pattern: /^\d{8}$/ },
// ];



const resolveMasterName = (value: any, masterList: any[]) => {
  const realValue = firstRealValue(value);
  if (!realValue) return "-";
  const matched = masterList.find(
    (item) =>
      String(item?.id) === String(realValue) ||
      String(item?.value) === String(realValue) ||
      String(item?.vendorId) === String(realValue)
  );
  return matched?.name || matched?.label || matched?.vendorName || (isUuidLike(realValue) ? "-" : realValue);
};

const getEmploymentBaseLocationValue = (record: any, employmentModes: any[]) => {
  const modeValue = record?.employmentModeName || record?.employmentMode;
  const employmentMode = employmentModes.find(
    (mode) =>
      String(mode?.id) === String(record?.employmentMode) ||
      String(mode?.name) === String(modeValue)
  );
  const isVendorConsultant = /vendor consultant/i.test(
    String(employmentMode?.name || record?.employmentModeName || record?.employmentMode || "")
  );
  if (isVendorConsultant) return "NA";

  const savedLocation = firstRealValue(record?.employmentBaseLocation, "Karnataka");
  return savedLocation === "NA" ? "Karnataka" : savedLocation;
};

const getCountryCode = (countryValue: any) => {
  const value = firstRealValue(countryValue);
  if (!value) return "";

  const country = Country.getAllCountries().find(
    (item) =>
      String(item.isoCode).toLowerCase() === String(value).toLowerCase() ||
      String(item.name).toLowerCase() === String(value).toLowerCase()
  );

  return country?.isoCode || "";
};

const getCountryName = (countryValue: any) => {
  const value = firstRealValue(countryValue);
  if (!value) return "-";

  const country = Country.getAllCountries().find(
    (item) =>
      String(item.isoCode).toLowerCase() === String(value).toLowerCase() ||
      String(item.name).toLowerCase() === String(value).toLowerCase()
  );

  return country?.name || value;
};

const normalizeDialCode = (phonecode: any) => {
  const code = String(phonecode || "").trim();
  if (!code) return "";
  return code.startsWith("+") ? code : `+${code}`;
};

const COUNTRY_PHONE_OPTIONS = Country.getAllCountries()
  .map((country) => {
    const dialCode = normalizeDialCode(country.phonecode);
    return {
      label: `${country.isoCode} (${dialCode}) - ${country.name}`,
      value: country.isoCode,
      dialCode,
      searchText: `${country.isoCode} ${country.name} ${dialCode}`.toLowerCase(),
    };
  })
  .filter((country) => country.dialCode)
  .sort((a, b) => a.label.localeCompare(b.label));

const getStateName = (stateValue: any, countryValue: any) => {
  const value = firstRealValue(stateValue);
  if (!value) return "-";

  const countryCode = getCountryCode(countryValue);
  const states = countryCode ? State.getStatesOfCountry(countryCode) : State.getAllStates();
  const state = states.find(
    (item) =>
      String(item.isoCode).toLowerCase() === String(value).toLowerCase() ||
      String(item.name).toLowerCase() === String(value).toLowerCase()
  );

  return state?.name || value;
};

const mergeSelectOptions = (primaryOptions: any[], fallbackOptions: any[]) => {
  const seen = new Set<string>();
  return [...primaryOptions, ...fallbackOptions].filter((option) => {
    const label = option?.label || option?.name || option?.value;
    const key = String(label || "").trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const sortSelectOptionsByLabel = (options: any[]) =>
  [...options].sort((a, b) =>
    String(a?.label || "").localeCompare(String(b?.label || ""))
  );

const isValidPhoneNumberForDialCode = (value: string) => {
  if (!value) return true;
  const normalizedValue = String(value).trim();
  const codeMatch = normalizedValue.match(/^\+\d{1,4}/);
  const code = codeMatch?.[0];
  const number = normalizedValue
    .replace(/^\+\d{1,4}/, "")
    .replace(/\D/g, "");

  if (!code || !number) return false;

  const strictPatterns: Record<string, RegExp> = {
    "+91": /^\d{10}$/,
    "+60": /^\d{9,10}$/,
    "+65": /^\d{8}$/,
    "+1": /^\d{10}$/,
    "+44": /^\d{10}$/,
    "+61": /^\d{9}$/,
  };

  return (strictPatterns[code] || /^\d{5,15}$/).test(number);
};

const isJson = (value: any) => {
  if (!value || typeof value !== "string") return false;
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};

const formatActivityDate = (date: any, fallback = "-") => {
  if (!date || !moment(date).isValid()) return fallback;
  return moment.utc(date).local().format("DD-MMM-YYYY");
};

const getActivityActor = (entry: any) =>
  entry?.updatedUserDetails?.[0]?.fullName || entry?.updatedByName || "System";

const isBlankActivityValue = (value: any) =>
  value === null || value === undefined || value === "";

const shouldSkipStaffNullRemoval = (subject: string, change: any) =>
  subject.includes("Update Staff") &&
  !isBlankActivityValue(change?.oldValue) &&
  isBlankActivityValue(change?.newValue);

const buildActivityEntries = (logs: any[], staffRecord: any) => {
  const entries: { title: string; date: string; description: string }[] = [];

  logs.forEach((entry) => {
    const subject = entry?.subject || "Staff activity";
    const actor = getActivityActor(entry);
    const date = formatActivityDate(entry?.created_date || entry?.createdDate);

    if (subject.includes("Update") && isJson(entry?.newValue)) {
      const changes = JSON.parse(entry.newValue);
      if (Array.isArray(changes)) {
        changes.forEach((change) => {
          if (change?.oldValue === change?.newValue) return;
          if (shouldSkipStaffNullRemoval(subject, change)) return;
          const fieldName = change?.fieldName || change?.fieldCode || "Field";
          const oldValue = valueOrFallback(change?.oldValue, "blank");
          const newValue = valueOrFallback(change?.newValue, "blank");
          entries.push({
            title: `${fieldName} updated`,
            date,
            description: `${actor} changed ${fieldName} from ${oldValue} to ${newValue}.`,
          });
        });
        return;
      }
    }

    const oldValue = firstRealValue(entry?.oldvalue, entry?.oldValue);
    const newValue = firstRealValue(entry?.newvalue, entry?.newValue);
    const description =
      oldValue || newValue
        ? `${actor} changed ${valueOrFallback(oldValue, "blank")} to ${valueOrFallback(newValue, "blank")}.`
        : `by ${actor}`;

    entries.push({
      title: subject,
      date,
      description,
    });
  });

  const hasCreateLog = entries.some((entry) =>
    entry.title.toLowerCase().includes("created")
  );

  if (!hasCreateLog && staffRecord?.createdDate) {
    entries.push({
      title: "Staff Created",
      date: formatActivityDate(staffRecord.createdDate),
      description: "Initial staff profile created.",
    });
  }

  return entries.sort((a, b) => moment(b.date, "DD-MMM-YYYY").valueOf() - moment(a.date, "DD-MMM-YYYY").valueOf());
};

const initialsFor = (name: string) =>
  (name || "Staff Member")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const resizeProfilePhoto = (file: File, size = 256): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("Please upload an image file."));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const context = canvas.getContext("2d");

        if (!context) {
          reject(new Error("Could not process the selected image."));
          return;
        }

        const sourceSize = Math.min(image.width, image.height);
        const sourceX = (image.width - sourceSize) / 2;
        const sourceY = (image.height - sourceSize) / 2;
        context.drawImage(
          image,
          sourceX,
          sourceY,
          sourceSize,
          sourceSize,
          0,
          0,
          size,
          size
        );
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      image.onerror = () => reject(new Error("Could not read the selected image."));
      image.src = String(reader.result);
    };
    reader.onerror = () => reject(new Error("Could not read the selected image."));
    reader.readAsDataURL(file);
  });

const withRealValues = (
  fields: StaffProfileField[],
  replacements: Record<string, string>
) =>
  fields.map((field) => ({
    ...field,
    value: replacements[field.label] || field.value,
  }));

const FieldGrid = ({ fields }: { fields: StaffProfileField[] }) => (
  <div className="staff-profile-field-grid">
    {fields.map((field) => (
      <div className="staff-profile-field" key={field.label}>
        <span>{field.label}</span>
        <strong>{valueOrFallback(field.value)}</strong>
      </div>
    ))}
  </div>
);

const getAddressDisplayLabel = (label: string) =>
  label.replace(/^(Communication|Permanent)\s+/, "");

const toAddressDisplayFields = (fields: StaffProfileField[]) =>
  fields.map((field) => ({
    ...field,
    label: getAddressDisplayLabel(field.label),
  }));

const AddressViewGrid = ({
  communicationFields,
  permanentFields,
  sameAsCommunicationAddress,
}: {
  communicationFields: StaffProfileField[];
  permanentFields: StaffProfileField[];
  sameAsCommunicationAddress: string;
}) => (
  <>
    <Row gutter={16}>
      <Col span={12} />
      <Col span={12}>
        <div className="staff-profile-address-same-view">
          <span>Same as Communication Address</span>
          <strong>{sameAsCommunicationAddress}</strong>
        </div>
      </Col>
    </Row>
    <Row gutter={16}>
      <Col span={12}>
        <h4 className="staff-profile-address-column-title">Communication Address</h4>
        <FieldGrid fields={toAddressDisplayFields(communicationFields)} />
      </Col>
      <Col span={12}>
        <h4 className="staff-profile-address-column-title">Permanent Address</h4>
        <FieldGrid fields={toAddressDisplayFields(permanentFields)} />
      </Col>
    </Row>
  </>
);

// const Section = ({
//   title,
//   children,
//   className = "",
// }: {
//   title: string;
//   children: React.ReactNode;
//   className?: string;
// }) => (
//   <div className={`staff-profile-section ${className}`}>
//     <h3>{title}</h3>
//     {children}
//   </div>
// );

const Section: React.FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
  onEdit?: () => void;
  isEditing?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  canEdit?: boolean;
  loading?: boolean;
}> = ({
  title,
  children,
  className = "",
  onEdit,
  isEditing = false,
  onSave,
  onCancel,
  canEdit = true,
  loading = false,
}) => (
    <div className={`staff-profile-section ${className}`}>
      <div className="staff-profile-section-header">
        <h3>{title}</h3>

        {canEdit && onEdit && !isEditing && (
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={onEdit}
          />
        )}

        {isEditing && (
          <Space>
            <Button
              size="small"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>

            <Button
              size="small"
              type="primary"
              onClick={onSave}
              loading={loading}
            >
              Save
            </Button>
          </Space>
        )}
      </div>

      {children}
    </div>
  );


const PlaceholderSection = ({ title, fields }: { title: string; fields: StaffProfileField[] }) => (
  <Section title={title}>
    <FieldGrid fields={fields} />
  </Section>
);

const StaffProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const missingField = (location.state as any)?.missingField as string | undefined;
  const missingFieldConfig = missingField && Object.prototype.hasOwnProperty.call(missingStaffFields, missingField)
    ? missingStaffFields[missingField] : undefined;
  const requestedReturnTo = (location.state as any)?.returnTo;
  const missingFieldsReturnTo = missingFieldConfig && typeof requestedReturnTo === "string" &&
    requestedReturnTo.startsWith("/dashboard/missing-fields/") ? requestedReturnTo : null;
  const missingFieldOpened = useRef<string | null>(null);
  const routedStaff = (location.state as any)?.staff; // Moved declaration here
  const { staffId } = useParams();
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [activeTab, setActiveTab] = useState<StaffProfileTabKey>(missingFieldConfig?.tab || "job");
  const [overviewYear, setOverviewYear] = useState<"all" | CareerHistoryEvent["year"]>("all");
  const [overviewView, setOverviewView] = useState<"timeline" | "table">("timeline");
  const [expandedOverviewYears, setExpandedOverviewYears] = useState<string[]>([]);
  const defaultOverviewYearApplied = React.useRef(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { setBlocker } = React.useContext(NavigationBlockerContext);
  const [isEditing, setIsEditing] = useState(false);
  const [tempFormData, setTempFormData] = useState<any>({});
  const [pendingTab, setPendingTab] = useState<StaffProfileTabKey | null>(null); // This will store the tab key for pending navigation
  const [pendingLocation, setPendingLocation] = useState<any>(null);
  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [form] = useForm();
  const watchedEntity = Form.useWatch("entity", form);
  const watchedEmploymentMode = Form.useWatch("employmentMode", form);
  const watchedJoiningDate = Form.useWatch("doj", form);
  const watchedVendorEmploymentType = Form.useWatch("vendorEmploymentType", form);
  const watchedWorkLocation = Form.useWatch("workLocation", form);
  const watchedCommunicationCountry = Form.useWatch("communicationCountry", form);
  const watchedPermanentCountry = Form.useWatch("permanentCountry", form);
  const watchedPreviousEmploymentHistory = Form.useWatch("previousEmploymentHistory", form);
  const watchedSalaryRevisionHistory = Form.useWatch("salaryRevisionHistory", form);
  const watchedProfilePhoto = Form.useWatch("profilePhoto", form);
  const watchedSameAsCommunicationAddress = Form.useWatch("sameAsCommunicationAddress", form);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [changesToConfirm, setChangesToConfirm] = useState<any[]>([]);
  const [activeDocumentCategoryId, setActiveDocumentCategoryId] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [documentUploadContext, setDocumentUploadContext] = useState<{
    relatedRecordType?: string;
    relatedRecordId?: string;
    title?: string;
  } | null>(null);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);
  const [staffDocuments, setStaffDocuments] = useState<any[]>([]);
  const [allStaffDocuments, setAllStaffDocuments] = useState<any[]>([]);
  const [documentCounts, setDocumentCounts] = useState<Record<string, number>>({});
  const [documentActionLoading, setDocumentActionLoading] = useState<string | null>(null);
  const [staff, setStaff] = useState<any>(routedStaff ? sanitizeInitialStaffFormDates(routedStaff) : emptyStaff);
  const [employeeIdLinks, setEmployeeIdLinks] = useState<any[]>([]);
  const [loading, setLoading] = useState(Boolean(staffId));
  const [editOpen, setEditOpen] = useState(false);
  const [employeeTypes, setEmployeeTypes] = useState<any[]>([]);
  const [employmentModes, setEmploymentModes] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [lineManagers, setLineManagers] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [maritalStatuses, setMaritalStatuses] = useState<any[]>([]);
  const [bloodGroups, setBloodGroups] = useState<any[]>([]);
  const [loadingStaffDocuments, setLoadingStaffDocuments] = useState(false);
  // const [staffList, setStaffList] = useState<any[]>([]);
  // const [projectList, setProjectList] = useState<any[]>([]);
  const [genderList, setGenderList] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  //  const [departmentList, setDepartmentList] = useState<any[]>([]);   
  const [entityList, setEntityList] = useState<any[]>([]);
  const [vendorList, setVendorList] = useState<any[]>([]);
  const [subDepartmentList, setSubDepartmentList] = useState<any[]>([]);
  const [accountList, setAccountList] = useState<any[]>([]);
  const [allAccountList, setAllAccountList] = useState<any[]>([]);
  const [filteredProjectList, setFilteredProjectList] = useState<any[]>([]);
  const [activityLogData, setActivityLogData] = useState<any[]>([]); // Keep this as any[] or define a specific type for activity logs
  const dispatch = useDispatch<AppDispatch>(); // Type dispatch with AppDispatch
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [focusedFormatField, setFocusedFormatField] = useState<string | null>(null);
  // Conditional field visibility, mirroring the create/edit staff drawer.
  const [showVendorField, setShowVendorField] = useState(false);
  const [showContractFields, setShowContractFields] = useState(false);
  const [isNoticePeriodStatus, setIsNoticePeriodStatus] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const tabLoadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const canUpdateStaff = useMemo(() => accessAllowed(myPermissions, allPermissions.UPDATE_STAFF), [myPermissions]); // Corrected accessAllowed usage


  // Overview tab was gated to a single test user while the feature was in progress.
  // The functionality is complete, so it is now visible to all staff.
  // const TEST_OVERVIEW_USER_ID = "41f48c6b-7fb0-4e91-8b11-9fec3a40ea2e";
  const profileTabs: { key: StaffProfileTabKey; label: string }[] = [

    // ...(staffId === TEST_OVERVIEW_USER_ID
    //   ? [{ key: 'overview' as StaffProfileTabKey, label: 'Overview' }]
    //   : []),
    { key: "job", label: "Employment Details" },
    { key: "personal", label: "Personal Details" },
    { key: "experienceQualifications", label: "Experience & Qualifications" },
    // { key: "onboarding", label: "Onboarding" },
    { key: "documents", label: "Documents" },
    // { key: "letters", label: "Letters" },
    { key: "compensation", label: "Compensation & Banking" },
    // { key: "assets", label: "Assets" },
    // { key: "leave", label: "Leave & Attendance" },
    // { key: "performance", label: "Performance" },
    { key: "timeline", label: "Project Timeline" },
    { key: "exit", label: "Exit" },
    { key: "overview", label: "Overview" },
  ];
  const textOnlyRule = (fieldName: string) => ({
    pattern: /^[A-Za-z\s.'/-]+$/,
    message: `Please enter a valid ${fieldName}. Only letters, spaces, apostrophes ('), dots (.), hyphens (-), and slashes (/) are allowed.`,
  });

  const numberOnlyRule = (fieldName: string) => ({
    pattern: /^\d+$/,
    message: `Please enter a valid ${fieldName}. Only numbers are allowed.`,
  });

  const experienceYearsRule = {
    validator: async (_: any, value: any) => {
      if (value === undefined || value === null || value === "") return Promise.resolve();

      const years = String(value);
      if (!/^\d{1,2}$/.test(years)) {
        return Promise.reject(
          new Error("Experience Years must contain only 1 or 2 digits.")
        );
      }

      const employeeDateOfBirth = form.getFieldValue(["dateOfBirth"]);
      const dateOfBirth =
        employeeDateOfBirth === undefined
          ? staff?.dateOfBirth ?? staff?.dob
          : employeeDateOfBirth;
      const dob = dateOfBirth ? dayjs(dateOfBirth) : null;

      // A missing, invalid, or future DOB must not produce a maximum based on today.
      if (!dob?.isValid() || dob.isAfter(dayjs(), "day")) return Promise.resolve();

      const maximumExperienceYears = dayjs().diff(dob, "year") - 18;
      if (Number(years) > maximumExperienceYears) {
        const message =
          maximumExperienceYears < 0
            ? "Experience Years cannot be entered because the employee is below the minimum working age of 18."
            : `Experience Years cannot exceed ${maximumExperienceYears} based on the employee's Date of Birth.`;
        return Promise.reject(new Error(message));
      }

      return Promise.resolve();
    },
  };



  const getJobDetailRules = (fieldKey: string) => {
    const isContractEmploymentMode = () => {
      const selectedEmploymentMode = form.getFieldValue("employmentMode");
      const permanentEmploymentMode = employmentModes.find((type: any) => type.name === "Permanent");
      const selectedEmploymentModeId =
        form.getFieldValue("employmentMode") || staff?.employmentMode || "";

      const selectedroleEmploymentMode = employmentModes.find(
        (type: any) => type.id === selectedEmploymentModeId
      );

      const employmentModeName = selectedroleEmploymentMode?.name || "";

      const isTraineeOrIntern =
        /trainee|intern/i.test(employmentModeName);

      // If Employment Mode is Vendor Consultant, also consider vendorEmploymentType
      const vendorEmploymentTypeId = form.getFieldValue("vendorEmploymentType") || staff?.vendorEmploymentType || "";
      const vendorEmploymentType = employmentModes.find((m: any) => String(m?.id) === String(vendorEmploymentTypeId) || String(m?.name) === String(vendorEmploymentTypeId));
      const vendorEmpIsTraineeOrIntern = /trainee|intern/i.test(String(vendorEmploymentType?.name || ""));

      const isDirectConsultant =
        /direct consultant/i.test(employmentModeName);



      // Trainee/Intern → contract dates are not mandatory
      if (isTraineeOrIntern || vendorEmpIsTraineeOrIntern) return false;

      // Direct Consultant → contract dates are mandatory
      if (isDirectConsultant) return true;
      return !!selectedEmploymentMode && selectedEmploymentMode !== permanentEmploymentMode?.id;
    };

    switch (fieldKey) {
      case "staffName":
        return [
          { required: true, message: "Please enter Full Name" },
          textOnlyRule("Full Name"),
        ];
      case "entity":
        return [{ required: true, message: "Please select Entity" }];
      case "employeeId":
        return [
          { required: true, message: "Please enter Employee ID" },
          {
            validator: (_: any, value: string) => {
              if (!value) return Promise.resolve();
              const regex = /^[A-Za-z]{2,3}[0-9]{5}$/;
              return regex.test(value)
                ? Promise.resolve()
                : Promise.reject(new Error("Invalid format. Please check the Info."));
            },
          },
        ];
      case "employmentMode":
        return [{ required: true, message: "Please select Employment Mode" }];
      case "employeeType":
        return [{ required: true, message: "Please enter Employee Type" }];
      case "designation":
        return [{ required: true, message: "Please enter Designation" }, textOnlyRule("Designation")];
      case "role":
        return [{ required: false, message: "Please enter Role" }, textOnlyRule("Role")];
      case "doj":
        return [{ required: true, message: "Please enter Date of Joining" }];
      case "nextPerformanceReviewDate":
      case "nextAppraisalSalaryRevisionDate":
        return [
          ({ getFieldValue }: any) => ({
            validator(_: any, value: any) {
              const dateOfJoining = getFieldValue("doj");
              const label = fieldKey === "nextPerformanceReviewDate"
                ? "Next Performance Review Date"
                : "Next Appraisal/Salary Revision Date";
              if (value && dateOfJoining && dayjs(value).isBefore(dayjs(dateOfJoining), "day")) {
                return Promise.reject(new Error(`${label} cannot be earlier than Date of Joining`));
              }
              return Promise.resolve();
            },
          }),
        ];
      case "probationEndDate":
        return [
          ({ getFieldValue }: any) => ({
            validator(_: any, value: any) {
              const dateOfJoining = getFieldValue("doj");

              if (value && dateOfJoining && dayjs(value).isBefore(dayjs(dateOfJoining), "day")) {
                return Promise.reject(
                  new Error("Probation End Date cannot be earlier than Date of Joining")
                );
              }

              return Promise.resolve();
            },
          }),
        ];
      case "contractStartDate":
        return [
          {
            required: isContractEmploymentMode(),
            message: "Please enter Contract Start Date",
          },
          ({ getFieldValue }: any) => ({
            validator(_: any, value: any) {
              const dateOfJoining = getFieldValue("doj");
              const contractEndDate = getFieldValue("contractEndDate");

              if (value && dateOfJoining && dayjs(value).isBefore(dayjs(dateOfJoining), "day")) {
                return Promise.reject(
                  new Error("Contract Start Date cannot be earlier than Date of Joining")
                );
              }

              if (value && contractEndDate && dayjs(value).isAfter(dayjs(contractEndDate), "day")) {
                return Promise.reject(
                  new Error("Contract Start Date cannot be later than Contract End Date")
                );
              }

              return Promise.resolve();
            },
          }),
        ];
      case "contractEndDate":
        return [
          {
            required: isContractEmploymentMode(),
            message: "Please enter Contract End Date",
          },
          ({ getFieldValue }: any) => ({
            validator(_: any, value: any) {
              const contractStartDate = getFieldValue("contractStartDate");
              const lastWorkingDay = getFieldValue("lwd");

              if (value && contractStartDate && dayjs(value).isBefore(dayjs(contractStartDate), "day")) {
                return Promise.reject(
                  new Error("Contract End Date cannot be earlier than Contract Start Date")
                );
              }

              if (value && lastWorkingDay && dayjs(value).isAfter(dayjs(lastWorkingDay), "day")) {
                return Promise.reject(
                  new Error("Contract End Date cannot be later than Last Working Day")
                );
              }

              return Promise.resolve();
            },
          }),
        ];
      case "nextReviewDate":
        return [{ required: true, message: "Please enter Next Review Date" }];
      case "visaExpiryDate":
        return [
          {
            required: isMalaysiaStatutoryEntity,
            message: "Please enter Visa Expiry Date",
          },
          ({ getFieldValue }: any) => ({
            validator(_: any, value: any) {
              const passportExpiryDate = getFieldValue("passportExpiryDate");

              if (
                value &&
                passportExpiryDate &&
                dayjs(value).isAfter(dayjs(passportExpiryDate), "day")
              ) {
                return Promise.reject(
                  new Error("Visa Expiry Date cannot be later than Passport Expiry Date")
                );
              }

              return Promise.resolve();
            },
          }),
        ];
      case "lwd":
        return [
          {
            required: form.getFieldValue("staffStatusId") === NOTICE_PERIOD_STATUS,
            message: "Please enter Last Working Day",
          },
          ({ getFieldValue }: any) => ({
            validator(_: any, value: any) {
              const staffStatus = getFieldValue("staffStatusId");
              const startDate = getFieldValue("doj");
              const contractEndDate = getFieldValue("contractEndDate");

              if (value && startDate && dayjs(value).isBefore(dayjs(startDate), "day")) {
                return Promise.reject(
                  new Error("Last Working Day cannot be earlier than Date of Joining")
                );
              }

              if (value && contractEndDate && dayjs(value).isBefore(dayjs(contractEndDate), "day")) {
                return Promise.reject(
                  new Error("Last Working Day cannot be earlier than Contract End Date")
                );
              }

              return Promise.resolve();
            },
          }),
        ];
      case "departmentId":
        return [{ required: true, message: "Please enter Department" }];
      case "subDepartmentId":
        return [{ required: false, message: "Please select Sub Department" }];
      case "lineManagerId":
        return [{ required: true, message: "Please enter RD Manager" }];
      case "clientManager":
        return [{ required: false, message: "Please enter Client Manager" }, textOnlyRule("Client Manager")];
      case "noticePeriod":
        return [
          { required: false, message: "Please enter Notice Period" },
          numberOnlyRule("Notice Period"),
          { max: 3, message: "Notice Period cannot exceed 3 digits." },
        ];
      case "nextSalaryRevisionDate":
        return undefined;
      default:
        return undefined;
    }
  };

  const getJobDetailDependencies = (fieldKey: string) => {
    if (["nextPerformanceReviewDate", "nextAppraisalSalaryRevisionDate"].includes(fieldKey)) return ["doj"];
    if (fieldKey === "probationEndDate") return ["doj"];
    if (fieldKey === "contractStartDate") return ["employmentMode", "doj", "contractEndDate"];
    if (fieldKey === "contractEndDate") return ["employmentMode", "contractStartDate", "lwd"];
    if (fieldKey === "lwd") return ["doj", "contractEndDate", "staffStatusId"];
    return undefined;
  };

  const getJobDetailDisabledDate = (fieldKey: string) => (current: any) => {
    const dateOfJoining = form.getFieldValue("doj");
    const contractStartDate = form.getFieldValue("contractStartDate");
    const contractEndDate = form.getFieldValue("contractEndDate");
    const lastWorkingDay = form.getFieldValue("lwd");

    if (!current) return false;
    if (fieldKey === "contractStartDate") {
      return !!(
        (dateOfJoining && current < dayjs(dateOfJoining).startOf("day")) ||
        (contractEndDate && current > dayjs(contractEndDate).endOf("day"))
      );
    }
    if (fieldKey === "contractEndDate") {
      return !!(
        (contractStartDate && current < dayjs(contractStartDate).startOf("day")) ||
        (lastWorkingDay && current > dayjs(lastWorkingDay).endOf("day"))
      );
    }
    if (
      (fieldKey === "probationEndDate" ||
        fieldKey === "nextPerformanceReviewDate" ||
        fieldKey === "nextAppraisalSalaryRevisionDate" ||
        fieldKey === "lwd" ||
        fieldKey === "effectiveFrom") &&
      dateOfJoining
    ) {
      return current < dayjs(dateOfJoining).startOf("day");
    }
    return false;
  };

  const isJobDetailField = (fieldKey: string) =>
    [
      "staffName",
      "entity",
      "employeeId",
      "employmentMode",
      "employeeType",
      "designation",
      "role",
      "doj",
      "probationEndDate",
      "nextReviewDate",
      "nextPerformanceReviewDate",
      "nextAppraisalSalaryRevisionDate",
      "contractStartDate",
      "contractEndDate",
      "lwd",
      "departmentId",
      "subDepartmentId",
      "lineManagerId",
      "noticePeriod",
      "workLocation",
      "employmentBaseLocation",
      "gradeBand",
      "nextSalaryRevisionDate",
    ].includes(fieldKey);

  const hasStagedChanges = Object.keys(tempFormData || {}).length > 0;
  const hasPendingStaffChanges = hasUnsavedChanges || hasStagedChanges;

  useEffect(() => {
    const blockerCallback = (location: any) => {
      setPendingLocation(location);
      // onSaveEdit(); // This will trigger the summary modal
      return false; // Prevent navigation
    };

    setBlocker(hasPendingStaffChanges ? blockerCallback : null);

    return () => setBlocker(null); // Cleanup on unmount
  }, [hasPendingStaffChanges, setBlocker]);

  /** Email domain depends on the selected entity - same rule as the drawer. */
  const getRequiredEmailDomain = useCallback(
    (entityId?: string) => {
      const entityName =
        entityList.find((entity) => entity?.id === entityId)?.name ||
        (staff?.entity === entityId ? staff?.entityName : "") ||
        "";

      return String(entityName).trim().toLowerCase() === INNOTHRUST_ENTITY_NAME
        ? INNOTHRUST_EMAIL_DOMAIN
        : DEFAULT_STAFF_EMAIL_DOMAIN;
    },
    [entityList, staff]
  );

  const CountryCodeInput: React.FC<{
    value?: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
  }> = ({ value, onChange, disabled }) => {
    const [code, setCode] = useState("+91");
    const [selectedCountry, setSelectedCountry] = useState(
      COUNTRY_PHONE_OPTIONS.find((country) => country.value === "IN")?.value
    );
    const [number, setNumber] = useState("");

    useEffect(() => {
      if (value) {
        const parts = value.split(" ");
        const foundCode = COUNTRY_PHONE_OPTIONS.find((c) => c.dialCode === parts[0]);
        if (foundCode && parts.length > 1) {
          setCode(foundCode.dialCode);
          setSelectedCountry((currentSelectedCountry) => {
            const currentOption = COUNTRY_PHONE_OPTIONS.find(
              (country) => country.value === currentSelectedCountry
            );
            return currentOption?.dialCode === foundCode.dialCode
              ? currentSelectedCountry
              : foundCode.value;
          });
          setNumber(parts.slice(1).join(" "));
        } else {
          // Fallback for numbers without a country code or with an unknown code
          setCode("+91"); // Default to IN
          setSelectedCountry(
            COUNTRY_PHONE_OPTIONS.find((country) => country.value === "IN")?.value
          );
          setNumber(value);
        }
      } else {
        // Default state for new entries
        setCode("+91");
        setSelectedCountry(
          COUNTRY_PHONE_OPTIONS.find((country) => country.value === "IN")?.value
        );
        setNumber("");
      }
    }, [value]);

    const triggerChange = (newCode: string, newNumber: string) => {
      if (onChange) {
        onChange(newNumber ? `${newCode} ${newNumber}`.trim() : "");
      }
    };

    const onCodeChange = (selectedValue: string) => {
      const selectedOption = COUNTRY_PHONE_OPTIONS.find((country) => country.value === selectedValue);
      const newCode = selectedOption?.dialCode || "+91";
      setSelectedCountry(selectedValue);
      setCode(newCode);
      triggerChange(newCode, number);
    };

    const onNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newNumber = sanitizeStaffInput(e.target.value, "phone", 15);
      setNumber(newNumber);
      triggerChange(code, newNumber);
    };

    const countryCodeSelector = (
      <Select
        value={selectedCountry}
        onChange={onCodeChange}
        style={{ minWidth: 95 }}
        disabled={disabled}
        showSearch
        popupMatchSelectWidth={280}
        optionFilterProp="searchText"
        filterOption={(input, option) =>
          String(option?.searchText || "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        options={COUNTRY_PHONE_OPTIONS}
      />
    );

    return (
      <Input
        addonBefore={countryCodeSelector}
        value={number}
        onChange={onNumberChange}
        disabled={disabled}
        inputMode="numeric"
        maxLength={15}
      />
    );
  };

  /** Contract fields show for every mode except Permanent; vendor fields only for Vendor Consultant. */
  const resolveEmploymentModeFlags = useCallback(
    (employmentModeId: any) => {
      const modeName =
        employmentModes.find((mode) => mode?.id === employmentModeId)?.name ||
        employmentModes.find((mode) => mode?.name === employmentModeId)?.name ||
        String(employmentModeId || "");
      const normalizedModeName = normalizeModeName(modeName);
      return {
        isVendor: normalizedModeName === normalizeModeName(VENDOR_CONSULTANT_MODE),
        isPermanent: normalizedModeName === normalizeModeName(PERMANENT_EMPLOYMENT_MODE),
      };
    },
    [employmentModes]
  );

  const renderEditableField = (config: {
    key: string;
    label: string;
    type: string;
    options?: any[];
    rules?: any[];
    disabled?: boolean;
    disabledDate?: (current: any) => boolean;
    dependencies?: string[];
    help?: string;
    maxLength?: number;
  }) => {
    const {
      key: fieldKey,
      label,
      type,
      options,
      rules: configRules,
      disabled,
      disabledDate: configDisabledDate,
      dependencies: configDependencies,
      help,
      maxLength: configMaxLength,
    } = config;
    const isDropdown = type === "select" || type === "multiselect" || type === "tags";
    const isDate = type === "date";
    const isNumber = type === "number";
    const isTextarea = type === "textarea";
    const isCheckbox = type === "checkbox";
    const isImageUpload = type === "imageUpload";
    const shouldApplyJobRules =
      // The original condition was `editingSection === "work" || editingSection === "all"`.
      // This is now `editingSection === "work" || (editingSection === "all" && activeTab === "job")`.
      // This ensures that job-specific rules are only applied when editing the "job" tab in "all" mode.
      // This prevents job-specific rules from being applied to other tabs when editing "all" sections.
      (editingSection === "work" || (editingSection === "all" && activeTab === "job")) &&
      isJobDetailField(fieldKey);
    const fieldRules = shouldApplyJobRules ? getJobDetailRules(fieldKey) : configRules;
    const fieldDependencies = shouldApplyJobRules
      ? getJobDetailDependencies(fieldKey)
      : configDependencies;
    const fieldDisabledDate = shouldApplyJobRules
      ? getJobDetailDisabledDate(fieldKey)
      : configDisabledDate;
    const fieldDisabled = disabled || (fieldKey === "entity" && activeTab === "job");
    const isPhone = type === "phone";
    const inputPolicy = getStaffInputPolicy(fieldKey);
    const inputMaxLength = getStaffInputMaxLength(fieldKey, configMaxLength);
    const normalizeTextValue =
      !isDropdown && !isDate && !isCheckbox && !isImageUpload && !isPhone
        ? (value: any) => sanitizeStaffInput(value, isNumber ? "number" : inputPolicy, inputMaxLength)
        : undefined;
    const focusFormatField = () => setFocusedFormatField(fieldKey);
    const hideFormatField = () =>
      setFocusedFormatField((currentField) => (currentField === fieldKey ? null : currentField));
    const blurFormatField = () =>
      setFocusedFormatField((currentField) => (currentField === fieldKey ? null : currentField));
    const hasTempFieldValue = Object.prototype.hasOwnProperty.call(tempFormData, fieldKey);
    const imageValue = isImageUpload
      ? hasTempFieldValue
        ? tempFormData[fieldKey]
        : firstRealValue(watchedProfilePhoto, form.getFieldValue(fieldKey), staff?.profilePhoto)
      : undefined;
    const hasFieldError = form.getFieldError(fieldKey).length > 0;
    const hasFieldValue = Boolean(firstRealValue(form.getFieldValue(fieldKey)));
    return (
      <Form.Item
        name={fieldKey}
        label={isCheckbox ? undefined : label}
        key={fieldKey}
        rules={fieldRules}
        dependencies={fieldDependencies}
        valuePropName={isCheckbox ? "checked" : undefined}
        getValueProps={isImageUpload ? () => ({}) : undefined}
        normalize={normalizeTextValue}
        extra={help && focusedFormatField === fieldKey && !hasFieldError && !hasFieldValue ? <span className="staff-profile-field-format-help">{help}</span> : undefined}
      >
        {isImageUpload ? (
          <div className="staff-profile-photo-upload">
            {imageValue ? (
              <img
                className="staff-profile-photo-preview"
                src={imageValue}
                alt={`${staffName} profile`}
              />
            ) : (
              <div className="staff-profile-photo-placeholder">{initialsFor(staffName)}</div>
            )}
            <div className="staff-profile-photo-upload-controls">
              <Space>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={async (file) => {
                    const MAX_SIZE_MB = 2;
                    const MIN_DIMENSION = 200; // px
                    const RECOMMENDED_DIMENSION = 500; // px

                    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
                    if (!isJpgOrPng) {
                      showNotification('You can only upload JPG/PNG file!', 'error');
                      return Upload.LIST_IGNORE;
                    }

                    const isLt2M = file.size / 1024 / 1024 < MAX_SIZE_MB;
                    if (!isLt2M) {
                      showNotification(`Image must be smaller than ${MAX_SIZE_MB}MB!`, 'error');
                      return Upload.LIST_IGNORE;
                    }

                    return new Promise((resolve, reject) => {
                      const reader = new FileReader();
                      reader.readAsDataURL(file);
                      reader.onload = (e) => {
                        const img = new Image();
                        img.src = e.target?.result as string;
                        img.onload = () => {
                          if (img.width < MIN_DIMENSION || img.height < MIN_DIMENSION) {
                            showNotification(`Image dimensions must be at least ${MIN_DIMENSION}x${MIN_DIMENSION}px.`, 'error');
                            reject(Upload.LIST_IGNORE);
                            return;
                          }
                          if (img.width < RECOMMENDED_DIMENSION || img.height < RECOMMENDED_DIMENSION) {
                            showNotification(`Recommended image dimensions are ${RECOMMENDED_DIMENSION}x${RECOMMENDED_DIMENSION}px for best quality.`, 'warning');
                          }
                          resolve(true);
                        };
                        img.onerror = () => {
                          showNotification('Could not load image for dimension check.', 'error');
                          reject(Upload.LIST_IGNORE);
                        };
                      };
                      reader.onerror = () => {
                        showNotification('Failed to read file.', 'error');
                        reject(Upload.LIST_IGNORE);
                      };
                    }).then(async () => {
                      try {
                        const photoDataUrl = await resizeProfilePhoto(file as File);
                        form.setFieldsValue({ [fieldKey]: photoDataUrl });
                        setTempFormData((prev: any) => ({ ...prev, [fieldKey]: photoDataUrl }));
                        setHasUnsavedChanges(true);
                      } catch (error: any) {
                        showNotification(error?.message || "Could not upload photo.", "error");
                      }
                      return Upload.LIST_IGNORE; // Prevent Ant Design's default upload behavior
                    }).catch(() => Upload.LIST_IGNORE);
                  }}
                  disabled={disabled}
                >
                  <Button icon={<UploadOutlined />} disabled={disabled}>
                    Upload Photo
                  </Button>
                </Upload>
                {imageValue && (
                  <Button
                    onClick={() => {
                      form.setFieldsValue({ [fieldKey]: null });
                      setTempFormData((prev: any) => ({ ...prev, [fieldKey]: null }));
                      setHasUnsavedChanges(true);
                    }}
                    disabled={disabled}
                  >
                    Remove
                  </Button>
                )}
              </Space>
              <small className="staff-profile-photo-upload-help">
                JPG/PNG only. Max size 2MB. Minimum 200x200px, recommended 500x500px.
              </small>
            </div>
          </div>
        ) : isDropdown ? (
          <Select
            placeholder={`${type === "tags" ? "Enter" : "Select"} ${label}`}
            allowClear
            showSearch
            optionFilterProp="label"
            mode={type === "multiselect" ? "multiple" : type === "tags" ? "tags" : undefined}
            open={type === "tags" ? false : undefined}
            suffixIcon={type === "tags" ? null : undefined}
            tokenSeparators={type === "tags" ? [","] : undefined}
            options={options}
            disabled={fieldDisabled}
            onFocus={focusFormatField}
            onBlur={blurFormatField}
            onChange={(value) => {
              hideFormatField();
              if (fieldKey === "workLocation" && value !== "WFO") {
                form.setFieldsValue({ officeLocation: undefined });
              }
              if (fieldKey === "entity") {
                form.setFieldsValue({ officeLocation: undefined });
              }
            }}
          />
        ) : isDate ? (
          <DatePicker

            style={{ width: "100%" }}

            format="DD-MMM-YYYY"
            disabled={fieldDisabled}
            disabledDate={fieldDisabledDate}
            onFocus={focusFormatField}
            onBlur={blurFormatField}
            onChange={() => hideFormatField()}
          />
        ) : isNumber ? (
          <Input
            inputMode="numeric"
            disabled={fieldDisabled}
            onFocus={focusFormatField}
            onBlur={blurFormatField}
            onChange={(event) => {
              hideFormatField();
              const onlyNums = event.target.value.replace(/\D/g, "");
              form.setFieldsValue({ [fieldKey]: onlyNums });
            }}
            onKeyDown={(event) => {
              if (
                !/[0-9]/.test(event.key) &&
                event.key !== "Backspace" &&
                event.key !== "Delete" &&
                event.key !== "ArrowLeft" &&
                event.key !== "ArrowRight" &&
                event.key !== "Tab"
              ) {
                event.preventDefault();
              }
            }}
            maxLength={inputMaxLength}
          />
        ) : isCheckbox ? (
          <Checkbox disabled={fieldDisabled}>{label}</Checkbox>
        ) : isTextarea ? (
          <Input.TextArea
            rows={4}
            disabled={fieldDisabled}
            maxLength={inputMaxLength}
            onFocus={focusFormatField}
            onBlur={blurFormatField}
            onChange={() => hideFormatField()}
          />
        ) : isPhone ? (
          <CountryCodeInput disabled={fieldDisabled} />
        ) : (
          <Input
            disabled={fieldDisabled}
            onFocus={focusFormatField}
            onBlur={blurFormatField}
            onChange={(e) => {
              hideFormatField();
              if (fieldKey === "aadhaar") {
                form.setFieldsValue({
                  aadhaar: formatAadhaar(e.target.value),
                });
              }

              if (fieldKey === "pan") {
                form.setFieldsValue({
                  pan: e.target.value.toUpperCase(),
                });
              }
            }}
            inputMode={fieldKey === "aadhaar" ? "numeric" : undefined}
            maxLength={inputMaxLength}
          />
        )}
      </Form.Item>
    );
  };

  const fetchStaff = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      /*  const employeeIdToResolve = firstRealValue(
         routedStaff?.employeeId,
         staff?.employeeId,
         staffId
       );
 
       const resolvedResponse = employeeIdToResolve
         ? await Client.get(`${API_RESOLVE_STAFF_EMPLOYEE_ID}${encodeURIComponent(String(employeeIdToResolve))}`)
         : null;
 
       const resolvedStaffId = firstRealValue(
         resolvedResponse?.data?.data?.staffId,
         resolvedResponse?.data?.data?.id,
         resolvedResponse?.data?.staffId,
         resolvedResponse?.data?.id,
         resolvedResponse?.data?.data?.primaryStaffId,
         resolvedResponse?.data?.primaryStaffId,
         staffId
       );
 
       if (resolvedStaffId && String(resolvedStaffId) !== String(staffId)) {
         navigate(ROUTE_MANAGE_STAFF.replace(":staffId", String(resolvedStaffId)), {
           replace: true,
           state: routedStaff || {},
         });
         return;
       } */
      const requestedStaffId = String(staffId);

      /* const response = await Client.get(`${API_GET_STAFF_DETAILS}/${resolvedStaffId || staffId}`); */
      const response = await Client.get(`${API_GET_STAFF_DETAILS}/${requestedStaffId}`);
      const data = response?.data?.data;
      const entity = Array.isArray(data) ? data[0] : data;
      const resolvedEntityName = entityList.length
        ? resolveMasterName(entity?.entity || routedStaff?.entity, entityList)
        : undefined;

      const nextStaff = {
        ...emptyStaff,
        ...(routedStaff || {}),
        ...(entity || {}),
        entityName: valueOrFallback(
          firstRealValue(
            resolvedEntityName !== "-" ? resolvedEntityName : undefined,
            entity?.entityName,
            routedStaff?.entityName
          ),
          "-"
        ),
        /* staffId: String(resolvedStaffId || staffId), */
        staffId: requestedStaffId,
      };

      setStaff(nextStaff);

      /*   const historyResponse = await Client.get(`${API_GET_STAFF_EMPLOYEE_ID_HISTORY}${String(resolvedStaffId || staffId)}`); */
      const historyResponse = await Client.get(`${API_GET_STAFF_EMPLOYEE_ID_HISTORY}${requestedStaffId}`);
      const historyPayload = historyResponse?.data?.data ?? historyResponse?.data ?? {};
      const linkedEmployeeIds = normalizeEmployeeIdLinks(historyPayload);
      setEmployeeIdLinks(
        linkedEmployeeIds.length > 0
          ? linkedEmployeeIds
          : nextStaff?.employeeId
            ? [{ staffId: String(nextStaff.staffId), employeeId: String(nextStaff.employeeId), relationship: "PRIMARY", isPrimary: true }]
            : []
      );
    } catch {
      setStaff({ ...emptyStaff, ...(routedStaff || {}), staffId });
      setEmployeeIdLinks([]);
      form.setFieldsValue({});
      showNotification("Could not load latest staff details.", "warning");
    } finally {
      setLoading(false);
    }
  }, [entityList, navigate, routedStaff, staffId, form]);

  useEffect(() => {
    if (staff && staff.staffId && Object.keys(staff).length > 0) { // Ensure staff object is not empty
      // Merge original staff data with any temporary unsaved data for the current tab
      const mergedData = { ...staff, ...tempFormData };
      const mergedSameAsCommunicationAddress = toBooleanValue(
        mergedData.sameAsCommunicationAddress
      );
      const mergedOfficeLocationOptions = getOfficeLocationOptionsForEntity(
        firstRealValue(
          entityList.find((entity) => String(entity?.id) === String(mergedData.entity))?.name,
          mergedData.entityName
        )
      );
      const formValues = {
        ...mergedData,
        workLocation: getValidOptionValue(mergedData.workLocation, WORK_LOCATION_OPTIONS),
        officeLocation:
          mergedData.workLocation === "WFO"
            ? getValidOptionValue(mergedData.officeLocation, mergedOfficeLocationOptions)
            : undefined,
        gradeBand: getValidOptionValue(mergedData.gradeBand, GRADE_BAND_OPTIONS),
        presentAddress: mergedData.presentAddress || "",
        permanentAddress: mergedData.permanentAddress || "",
        communicationAddressLine:
          mergedData.communicationAddressLine || mergedData.presentAddress || "",
        communicationCountry: getCountryName(mergedData.communicationCountry) !== "-"
          ? getCountryName(mergedData.communicationCountry)
          : undefined,
        communicationState: getStateName(mergedData.communicationState, mergedData.communicationCountry) !== "-"
          ? getStateName(mergedData.communicationState, mergedData.communicationCountry)
          : undefined,
        permanentAddressLine:
          mergedData.permanentAddressLine ||
          (mergedSameAsCommunicationAddress
            ? mergedData.communicationAddressLine || mergedData.presentAddress
            : "") ||
          mergedData.permanentAddress ||
          "",
        permanentCity:
          mergedData.permanentCity ||
          (mergedSameAsCommunicationAddress ? mergedData.communicationCity : "") ||
          "",
        permanentState:
          getStateName(
            mergedData.permanentState ||
            (mergedSameAsCommunicationAddress ? mergedData.communicationState : ""),
            mergedData.permanentCountry ||
            (mergedSameAsCommunicationAddress ? mergedData.communicationCountry : "")
          ) !== "-"
            ? getStateName(
              mergedData.permanentState ||
              (mergedSameAsCommunicationAddress ? mergedData.communicationState : ""),
              mergedData.permanentCountry ||
              (mergedSameAsCommunicationAddress ? mergedData.communicationCountry : "")
            )
            : undefined,
        permanentCountry:
          getCountryName(
            mergedData.permanentCountry ||
            (mergedSameAsCommunicationAddress ? mergedData.communicationCountry : "")
          ) !== "-"
            ? getCountryName(
              mergedData.permanentCountry ||
              (mergedSameAsCommunicationAddress ? mergedData.communicationCountry : "")
            )
            : undefined,
        permanentPostalCode:
          mergedData.permanentPostalCode ||
          (mergedSameAsCommunicationAddress ? mergedData.communicationPostalCode : "") ||
          "",
        sameAsCommunicationAddress: mergedSameAsCommunicationAddress,
        dependents: toDependentsFormValues(parseDependents(mergedData.dependents)),
        previousEmploymentHistory: normalizePreviousEmploymentHistory(
          parsePreviousEmploymentHistory(mergedData.previousEmploymentHistory)
        ),
        skillDetails: buildSkillDetails(
          mergedData.skillDetails,
          mergedData.primarySkills,
          mergedData.secondarySkills,
          mergedData.skillProficiencyLevel
        ),
        primarySkills: parseStringArray(mergedData.primarySkills),
        secondarySkills: parseStringArray(mergedData.secondarySkills),
        positionHistory: toPositionHistoryFormValues(parsePositionHistory(mergedData.positionHistory)),
        projectTimeline: toProjectTimelineFormValues(parseProjectTimeline(mergedData.projectTimeline)),
        doj: toFormDate(firstRealValue(mergedData.doj, mergedData.joinedDate, mergedData.dateOfJoining)),
        probationEndDate: toFormDate(mergedData.probationEndDate),
        contractStartDate: toFormDate(firstRealValue(mergedData.contractStartDate, mergedData.doj)),
        contractEndDate: toFormDate(mergedData.contractEndDate),
        lwd: toFormDate(mergedData.lwd),
        resignationDate: toFormDate(mergedData.resignationDate),
        previousSalaryRevisionDate: toFormDate(mergedData.previousSalaryRevisionDate),
        nextSalaryRevisionDate: toFormDate(mergedData.nextSalaryRevisionDate),
        nextReviewDate: toFormDate(mergedData.nextReviewDate),
        nextPerformanceReviewDate: toFormDate(mergedData.nextPerformanceReviewDate),
        nextAppraisalSalaryRevisionDate: toFormDate(mergedData.nextAppraisalSalaryRevisionDate),
        passportExpiryDate: toFormDate(mergedData.passportExpiryDate),
        visaExpiryDate: toFormDate(mergedData.visaExpiryDate),
        effectiveFrom: toFormDate(mergedData.effectiveFrom),
        issuedDate: toFormDate(mergedData.issuedDate),
        salaryRevisionHistory: toSalaryRevisionFormValues(buildSalaryRevisionHistory(mergedData)),
        bonusHistory: toBonusFormValues(buildBonusHistory(mergedData)),
        dateOfBirth: toFormDate(mergedData.dateOfBirth),
      };
      form.setFieldsValue(formValues); // Set form values after fetching and merging
    }
  }, [staff, form, entityList]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  useEffect(() => {
    return () => {
      if (tabLoadingTimerRef.current) {
        clearTimeout(tabLoadingTimerRef.current);
      }
    };
  }, []);

  const switchProfileTab = useCallback((key: StaffProfileTabKey) => {
    if (key === activeTab) return;

    if (tabLoadingTimerRef.current) {
      clearTimeout(tabLoadingTimerRef.current);
    }

    setTabLoading(true);
    setActiveTab(key);
    tabLoadingTimerRef.current = setTimeout(() => {
      setTabLoading(false);
      tabLoadingTimerRef.current = null;
    }, 180);
  }, [activeTab]);

  const getDocumentsFromResponse = useCallback((response: any) => {
    const data = response?.data?.data;
    return Array.isArray(data) ? data : [];
  }, []);

  const fetchDocumentCounts = useCallback(async () => {
    if (!staffId || documentTypes.length === 0) {
      setDocumentCounts({});
      setAllStaffDocuments([]);
      return;
    }

    try {
      const documentEntries = await Promise.all(
        documentTypes.map(async (documentType) => {
          const response = await Client.get(`${API_GET_DOCUMENTS}${staffId}`, {
            params: { documentTypeId: documentType.id },
          });
          return [documentType.id, getDocumentsFromResponse(response)] as const;
        })
      );
      const counts = documentEntries.reduce((acc: Record<string, number>, [documentTypeId, documents]) => {
        acc[documentTypeId] = documents.length;
        return acc;
      }, {});

      setDocumentCounts(counts);
      const allDocuments = documentEntries.reduce(
        (documents: any[], [, documentGroup]) => documents.concat(documentGroup),
        []
      );
      setAllStaffDocuments(allDocuments);
    } catch (error) {
      console.error("Failed to fetch staff document counts:", error);
      setDocumentCounts({});
      setAllStaffDocuments([]);
    }
  }, [documentTypes, getDocumentsFromResponse, staffId]);

  const fetchStaffDocuments = useCallback(async (documentTypeId = activeDocumentCategoryId) => {
    if (!staffId || !documentTypeId) {
      setStaffDocuments([]);
      return;
    }
    setLoadingStaffDocuments(true);
    try {
      const response = await Client.get(`${API_GET_DOCUMENTS}${staffId}`, { params: { documentTypeId } });
      const documents = getDocumentsFromResponse(response);
      setStaffDocuments(documents);
      setDocumentCounts((currentCounts) => ({
        ...currentCounts,
        [documentTypeId]: documents.length,
      }));
    } catch (error) {
      console.error("Failed to fetch staff documents:", error);
      setStaffDocuments([]);
      showNotification("Failed to load staff documents.", "error");
    } finally {
      setLoadingStaffDocuments(false);
    }
  }, [activeDocumentCategoryId, getDocumentsFromResponse, staffId]);

  // Fetch Document Types for the left sidebar
  useEffect(() => {
    const fetchDocumentTypes = async () => {
      setLoadingDocumentTypes(true);
      try {
        const response = await Client.get(API_GET_ALL_DOCUMENT_TYPE_ACTIVELIST);
        const fetchedTypes: DocumentType[] = response?.data?.data || [];
        setDocumentTypes(fetchedTypes);
        if (fetchedTypes.length > 0) {
          setActiveDocumentCategoryId(fetchedTypes[0].id); // Set the first document type as active
        }
      } catch (error) {
        console.error("Failed to fetch document types:", error);
        showNotification("Failed to load document types.", "error");
      } finally {
        setLoadingDocumentTypes(false);
      }
    };
    fetchDocumentTypes();
  }, []); // Run once on mount

  // Fetch Staff Documents based on selected Document Type
  useEffect(() => {
    fetchStaffDocuments();
  }, [fetchStaffDocuments]); // Re-fetch when staffId or activeDocumentCategoryId changes

  useEffect(() => {
    fetchDocumentCounts();
  }, [fetchDocumentCounts]);

  const handleDocumentUploadSuccess = useCallback((documentTypeId: string) => {
    setActiveDocumentCategoryId(documentTypeId);
    fetchStaffDocuments(documentTypeId);
    fetchDocumentCounts();
    setDocumentUploadContext(null);
  }, [fetchDocumentCounts, fetchStaffDocuments]);

  const openDocumentUpload = useCallback((context?: {
    relatedRecordType?: string;
    relatedRecordId?: string;
    title?: string;
  }) => {
    setDocumentUploadContext(context || null);
    setShowUploadModal(true);
  }, []);

  const handleDownloadDocument = useCallback(async (document: any) => {
    if (!document?.id) {
      showNotification("Document id is not available.", "error");
      return;
    }

    setDocumentActionLoading(document.id);
    try {
      const response = await Client.get(`${API_GET_DOCUMENT_SIGNED_URL}${document.id}`);
      const downloadUrl = response?.data?.data?.url || response?.data?.url;

      if (!downloadUrl) {
        showNotification("Download link is not available for this document.", "error");
        return;
      }

      const link = window.document.createElement("a");
      link.href = downloadUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download = response?.data?.data?.fileName || document?.fileName || document?.originalFileName || "document";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download document:", error);
      showNotification("Failed to download document.", "error");
    } finally {
      setDocumentActionLoading(null);
    }
  }, []);

  const handleDeleteDocument = useCallback((document: any) => {
    if (!document?.id) {
      showNotification("Document id is not available.", "error");
      return;
    }

    Modal.confirm({
      title: "Delete Document",
      content: `Are you sure you want to delete ${document?.fileName || "this document"}?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        setDocumentActionLoading(document.id);
        try {
          await Client.delete(`${API_DELETE_DOCUMENT}${document.id}`);
          showNotification("Document deleted successfully.", "success");
          await fetchStaffDocuments(document.documentTypeId || activeDocumentCategoryId);
          await fetchDocumentCounts();
        } catch (error) {
          console.error("Failed to delete document:", error);
          showNotification("Failed to delete document.", "error");
        } finally {
          setDocumentActionLoading(null);
        }
      },
    });
  }, [activeDocumentCategoryId, fetchDocumentCounts, fetchStaffDocuments]);

  const getLinkedDocuments = useCallback(
    (relatedRecordType: string, relatedRecordId: string) =>
      allStaffDocuments.filter(
        (document) =>
          document.relatedRecordType === relatedRecordType &&
          document.relatedRecordId === relatedRecordId
      ),
    [allStaffDocuments]
  );

  const renderLinkedDocumentUpload = useCallback(
    (
      relatedRecordType: string,
      relatedRecordId: string,
      title: string,
      allowUpload = true
    ) => {
      const linkedDocuments = getLinkedDocuments(relatedRecordType, relatedRecordId);
      const latestDocument = linkedDocuments[0];

      if (!allowUpload) {
        return latestDocument ? (
          <Button
            type="link"
            icon={<DownloadOutlined />}
            loading={documentActionLoading === latestDocument.id}
            onClick={() => handleDownloadDocument(latestDocument)}
          >
            {latestDocument.fileName || "Download"}
          </Button>
        ) : (
          "-"
        );
      }

      return (
        <Space size="small" wrap>
          {latestDocument ? (
            <>
              <Button
                type="link"
                icon={<DownloadOutlined />}
                loading={documentActionLoading === latestDocument.id}
                onClick={() => handleDownloadDocument(latestDocument)}
              >
                {latestDocument.fileName || "Download"}
              </Button>
              <Button
                size="small"
                icon={<UploadOutlined />}
                onClick={() =>
                  openDocumentUpload({
                    relatedRecordType,
                    relatedRecordId,
                    title,
                  })
                }
              >
                Replace
              </Button>
            </>
          ) : (
            <Button
              size="small"
              icon={<UploadOutlined />}
              onClick={() =>
                openDocumentUpload({
                  relatedRecordType,
                  relatedRecordId,
                  title,
                })
              }
            >
              Upload Document
            </Button>
          )}
        </Space>
      );
    },
    [documentActionLoading, getLinkedDocuments, handleDownloadDocument, openDocumentUpload]
  );

  const fetchActivityLogs = useCallback(async () => {
    if (!staffId) return;
    try {
      const response = await Client.get(API_GET_ACTIVITY_LOG + `${staffId}`);
      setActivityLogData(response?.data?.data || []);
    } catch {
      setActivityLogData([]);
    }
  }, [staffId]);

  useEffect(() => {
    fetchActivityLogs();
  }, [fetchActivityLogs]);

  useEffect(() => {
    const fetchMasterList = async (module: string, setter: (items: any[]) => void) => {
      try {
        const response = await Client.get(API_COMMON_GET_BY_MODULE(module));
        setter(response?.data?.data?.items || response?.data?.data || []);
      } catch {
        setter([]);
      }
    };

    fetchMasterList("master_employee_type", setEmployeeTypes);
    fetchMasterList("master_employment_mode", setEmploymentModes);
    fetchMasterList("master_gender", setGenders); // Assuming these are for dropdowns
    fetchMasterList("master_marital_status", setMaritalStatuses);
    fetchMasterList("master_blood_group", setBloodGroups);
    fetchMasterList("master_currency", setCurrencies);
    fetchStaffList();
    fetchProjectList();
    fetchAllAccountList();
    fetchVendorList();
    fetchDepartmentList();
    fetchEntityList();
  }, []);

  const fetchStaffList = async () => {
    const response = await Client.get(API_GET_ALL_STAFF_LIST);
    if (response?.data?.data) {
      setLineManagers(response?.data?.data);
    } else {
      setLineManagers([]);
    }
  };

  const fetchProjectList = async () => {
    const response = await Client.get(API_GET_ALL_PROJECT_LIST);
    if (response?.data?.data) {
      setProjects(response?.data?.data);
    } else {
      setProjects([]);
    }
  };

  const fetchAllAccountList = async () => {
    try {
      const response = await Client.get(API_GET_ALL_ACCOUNTS_LIST);
      setAllAccountList(response?.data?.data || []);
    } catch {
      setAllAccountList([]);
    }
  };


  const fetchDepartmentList = async () => {
    const response = await Client.get(API_GET_ALL_DEPARTMENT_LIST);
    if (response?.data?.data) {
      setDepartments(response?.data?.data);
    } else {
      setDepartments([]);
    }
  };

  const fetchEntityList = useCallback(async () => {
    try {
      const response = await Client.get(API_COMMON_GET_BY_MODULE("master_entity"));
      setEntityList(response?.data?.data?.items || []);
    } catch {
      setEntityList([]);
    }
  }, []);

  const fetchVendorList = useCallback(async () => {
    try {
      const response = await Client.get(API_GET_ALL_VENDOR_LIST);
      setVendorList(response?.data?.data || []);
    } catch {
      setVendorList([]);
    }
  }, []);

  const fetchSubDepartmentsList = useCallback(async (departmentId: string) => {
    if (!departmentId) {
      setSubDepartmentList([]);
      return;
    }
    try {
      const response = await Client.get(`${API_GET_SUBDEPARTMENTS_BY_DEPARTMENT}${departmentId}`);
      setSubDepartmentList(response?.data?.data || []);
    } catch {
      setSubDepartmentList([]);
    }
  }, []);

  const fetchAccountListByEntity = useCallback(async (entityId: string) => {
    if (!entityId) {
      setAccountList([]);
      return;
    }
    try {
      const response = await Client.get(`${API_GET_ACCOUNT_LIST_BY_ENTITY}${entityId}`);
      setAccountList(response?.data?.data || []);
    } catch {
      setAccountList([]);
    }
  }, []);

  // Build an employee overview object from real API data (staff + activity logs + projects)
  const buildCareerHistoryFromStaff = (staffData: Record<string, any>, activityLogs: any[] = []): CareerHistoryEvent[] => {
    const events: CareerHistoryEvent[] = [];

    const yearForDate = (d: any) => {
      if (!d) return "";
      const m = moment(d, [moment.ISO_8601, "DD-MMM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY"], true);
      if (m && m.isValid()) return String(m.year());
      const dt = new Date(d);
      return !isNaN(dt.getTime()) ? String(dt.getFullYear()) : "";
    };

    // Joined
    const joinedDate = firstRealValue(staffData?.doj, staffData?.joinedDate, staffData?.dateOfJoining, staffData?.createdDate);
    if (joinedDate) {
      events.push({
        id: createLegacyRecordId("joined", [joinedDate], 0),
        year: yearForDate(joinedDate),
        date: joinedDate,
        eventType: "JOINED",
        role: firstRealValue(staffData?.designation, staffData?.role, ""),
        position: firstRealValue(staffData?.position, staffData?.currentPosition, ""),
        ctc: firstRealValue(staffData?.ctc, staffData?.currentCtc, ""),
        client: firstRealValue(staffData?.currentClient, ""),
        description: "Joined the organization",
      });
    }

    // Salary revisions
    const salaryRows = normalizeSalaryRevisionHistory(parseSalaryRevisionHistory(staffData?.salaryRevisionHistory));
    salaryRows.forEach((row: any, idx: number) => {
      if (!row.effectiveFrom) return;
      events.push({
        id: row.id || createLegacyRecordId("salary-revision", [row.effectiveFrom, row.previousSalary], idx),
        year: yearForDate(row.effectiveFrom),
        date: row.effectiveFrom,
        eventType: "SALARY REVISION",
        role: firstRealValue(staffData?.designation, staffData?.role, ""),
        position: firstRealValue(staffData?.position, staffData?.currentPosition, ""),
        ctc: row.previousSalary || "",
        client: firstRealValue(staffData?.currentClient, ""),
        description: "Salary Revised",
      });
    });

    // Bonus history
    const bonusRows = normalizeBonusHistory(parseBonusHistory(staffData?.bonusHistory));
    bonusRows.forEach((row: any, idx: number) => {
      if (!row.issuedDate) return;
      events.push({
        id: row.id || createLegacyRecordId("bonus", [row.issuedDate, row.bonusAmount], idx),
        year: yearForDate(row.issuedDate),
        date: row.issuedDate,
        eventType: "BONUS",
        role: firstRealValue(staffData?.designation, staffData?.role, ""),
        position: firstRealValue(staffData?.position, staffData?.currentPosition, ""),
        ctc: row.bonusAmount || "",
        client: firstRealValue(staffData?.currentClient, ""),
        description: "Bonus issued",
      });
    });

    // Position / Role history
    const positionRows = normalizePositionHistory(parsePositionHistory(staffData?.positionHistory));
    positionRows.forEach((row: any, idx: number) => {
      if (!row.effectiveFrom) return;
      events.push({
        id: createLegacyRecordId("position", [row.effectiveFrom, row.designation], idx),
        year: yearForDate(row.effectiveFrom),
        date: row.effectiveFrom,
        eventType: "ROLE CHANGE",
        role: row.designation || firstRealValue(staffData?.designation, staffData?.role, ""),
        position: row.designation || firstRealValue(staffData?.position, staffData?.currentPosition, ""),
        ctc: "",
        client: "",
        description: "Role/Position changed",
      });
    });

    // Project / Client timeline
    const projectRows = normalizeProjectTimeline(parseProjectTimeline(staffData?.projectTimeline));
    projectRows.forEach((row: StaffProfileProjectTimeline, idx: number) => {
      if (!row.date) return;
      events.push({
        id: createLegacyRecordId("project", [row.date, row.accountId || row.projectId], idx),
        year: yearForDate(row.date),
        date: row.date,
        eventType: row.projectName ? "PROJECT ASSIGNMENT" : "CLIENT CHANGE",
        role: firstRealValue(staffData?.designation, staffData?.role, ""),
        position: firstRealValue(staffData?.position, staffData?.currentPosition, ""),
        ctc: "",
        client: row.accountName || (row as any).projectName || "",
        description: row.accountName || (row as any).projectName || "",
      });
    });

    // Activity log entries (include legacy audit logs)
    const activityEntries = buildActivityEntries(activityLogs || [], staffData);
    activityEntries.forEach((entry: any, idx: number) => {
      if (!entry.date) return;
      events.push({
        id: createLegacyRecordId("activity", [entry.date, idx], idx),
        year: yearForDate(entry.date),
        date: entry.date,
        eventType: entry.title || "Activity",
        role: "",
        position: "",
        ctc: "",
        client: "",
        description: entry.description || "",
      });
    });

    // Sort by date desc
    return events
      .filter((e) => e.date)
      .sort((a: CareerHistoryEvent, b: CareerHistoryEvent) => getDateSortValue(b.date) - getDateSortValue(a.date));
  };

  const buildEmployeeOverview = (staffData: Record<string, any>, activityLogs: any[] = [], projectsList: any[] = []): EmployeeOverview => {
    if (!staffData || Object.keys(staffData).length === 0) return employeeOverviewMock;

    // Prefer backend-provided normalized timeline when available
    let careerHistory: CareerHistoryEvent[] = [];
    const yearForDate = (d: any) => {
      if (!d) return "";
      const m = moment(d, [moment.ISO_8601, "DD-MMM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY"], true);
      if (m && m.isValid()) return String(m.year());
      const dt = new Date(d);
      return !isNaN(dt.getTime()) ? String(dt.getFullYear()) : "";
    };
    if (Array.isArray(staffData?.timeline) && staffData.timeline.length > 0) {
      careerHistory = (staffData.timeline as any[])
        .map((t: any, idx: number) => {
          const created = t.createdDate || t.createdAt || t.created_on || t.created || t.date;
          const createdBy = t.createdBy || t.created_by || t.createdByName || t.created_by_name || t.updatedBy;
          const changedByName = t.changedByName || t.changed_by_name || "";
          const eventDate = t.date || t.effectiveFrom || t.effectiveDate || created;
          return {
            id: t.id || createLegacyRecordId("timeline", [eventDate, t.eventType || t.type || t.title], idx),
            year: yearForDate(eventDate),
            date: eventDate,
            createdDate: created,
            createdBy: createdBy,
            changedByName,
            eventType: t.eventType || t.type || t.title || "",
            subject: t.subject,
            description: t.description || t.note || "",
            oldValue: t.oldValue,
            newValue: t.newValue,
            updatedBy: t.updatedBy,
            role: t.role || t.designation || t.details?.role || t.details?.designation || t.previousRole,
            position: t.position || t.details?.designation || t.details?.position || t.previousPosition,
            ctc: t.ctc || t.details?.salary || t.details?.ctc || t.amount || t.previousSalary || t.revisedSalary || "",
            client: t.client || t.details?.client || t.accountName || "",
            details: t.details || t.data || t.payload || {},
          } as CareerHistoryEvent;
        })
        .filter((e) => e.date)
        .sort((a: CareerHistoryEvent, b: CareerHistoryEvent) => getDateSortValue(b.date) - getDateSortValue(a.date));
    } else {
      careerHistory = buildCareerHistoryFromStaff(staffData, activityLogs);
    }

    const latestTimelineClient = careerHistory.find((event) => event.client)?.client;
    const latestProject = (normalizeProjectTimeline(parseProjectTimeline(staffData?.projectTimeline)) || [])[0] || ({} as StaffProfileProjectTimeline);
    const ctcNumeric = calculateCtcFromSalaryRevisionHistory(parseSalaryRevisionHistory(staffData?.salaryRevisionHistory));

    return {
      employeeId: firstRealValue(staffData?.employeeId, staffData?.staffId, staffData?.employeeCode) || "",
      employeeName: firstRealValue(staffData?.staffName, staffData?.fullName, staffData?.employeeName) || "",
      status: firstRealValue(staffData?.staffStatusId, staffData?.status, "") || "",
      joinedDate: dateOrFallback(firstRealValue(staffData?.doj, staffData?.joinedDate, staffData?.dateOfJoining)),
      experience: `${calculateTenure(firstRealValue(staffData?.doj, staffData?.joinedDate, staffData?.dateOfJoining))} of Experience`,
      currentRole: firstRealValue(staffData?.role, staffData?.currentRole, staffData?.role) || "",
      currentPosition: firstRealValue(staffData?.designation, staffData?.currentPosition) || "",
      currentCtc: staffData?.ctc || (ctcNumeric ? `₹${ctcNumeric}` : ""),
      currentClient: firstRealValue(latestTimelineClient, latestProject?.accountName, staffData?.currentClient) || "",
      project: valueOrFallback(firstRealValue(staff?.projectName, staff?.currentMonthTopProjectName), "-"),
      careerHistory,
      // Use the timeline length when available; otherwise fall back to API totalChanges
      totalChanges: Array.isArray(staffData?.timeline) ? staffData.timeline.length : (typeof staffData?.totalChanges === "number" ? staffData.totalChanges : undefined),
    };
  };

  const overview: EmployeeOverview = buildEmployeeOverview(staff || {}, activityLogData || [], projects || []);
  const latestOverviewYear = overview.careerHistory.reduce(
    (latest: CareerHistoryEvent["year"] | undefined, event) =>
      !latest || Number(event.year) > Number(latest) ? event.year : latest,
    undefined
  );

  useEffect(() => {
    defaultOverviewYearApplied.current = false;
    setOverviewYear("all");
    setExpandedOverviewYears([]);
  }, [staffId]);

  useEffect(() => {
    if (latestOverviewYear && !defaultOverviewYearApplied.current) {
      defaultOverviewYearApplied.current = true;
      setExpandedOverviewYears([String(latestOverviewYear)]);
    }
  }, [latestOverviewYear]);

  const activityEntries = buildActivityEntries(activityLogData, staff);
  const recentActivities =
    activityEntries.length > 0
      ? activityEntries.slice(0, 4).map((entry) => ({ title: entry.title, date: entry.date }))
      : overview.careerHistory.slice(0, 4).map((ev: CareerHistoryEvent) => ({ title: ev.eventType || ev.subject || ev.description || "", date: ev.date }));
  const timelineItems =
    activityEntries.length > 0
      ? [...activityEntries].reverse().map((entry) => ({ date: entry.date, title: entry.title, description: entry.description }))
      : overview.careerHistory.map((ev: CareerHistoryEvent) => ({ date: ev.date, title: ev.eventType || ev.subject || "", description: ev.description || ev.newValue || "" }));
  const employeeIdLinksOptions = useMemo(
    () =>
      employeeIdLinks.map((link) => ({
        value: link.employeeId,
        label: (
          <span>
            {link.isPrimary ? <StarFilled style={{ color: "#f59e0b", marginRight: 6 }} /> : <LinkOutlined style={{ color: "#64748b", marginRight: 6 }} />}
            {link.employeeId}
            <span style={{ marginLeft: 8, color: link.isPrimary ? "#f59e0b" : "#64748b" }}>
              {link.isPrimary ? "Primary" : "Child"}
            </span>
          </span>
        ),
      })),
    [employeeIdLinks]
  );
  const status = valueOrFallback(staff?.staffStatusId);
  const getStaffStatusColor = (statusValue: string) => {
    if (statusValue === "ACTIVE") return "success";
    if (statusValue === "DRAFT") return "processing";
    if (statusValue === "NOTICE PERIOD") return "warning";
    if (statusValue === "INACTIVE") return "red";
    return "default";
  };
  const isExited = status === "INACTIVE";
  const isExitPreview = activeTab === "exit";
  const staffName = valueOrFallback(staff?.staffName, "Staff Member");
  const employeeId = valueOrFallback(staff?.employeeId, "-");
  const entityName = valueOrFallback(
    firstRealValue(
      resolveMasterName(staff?.entity, entityList) !== "-"
        ? resolveMasterName(staff?.entity, entityList)
        : undefined,
      staff?.entityName
    ),
    "-"
  );
  const statutoryEntityName = useMemo(() => {
    const selectedEntityId = firstRealValue(watchedEntity, staff?.entity);
    const selectedEntity = entityList.find(
      (entity) => String(entity?.id) === String(selectedEntityId)
    );

    return valueOrFallback(
      firstRealValue(
        selectedEntity?.name,
        String(staff?.entity) === String(selectedEntityId) ? staff?.entityName : undefined,
        entityName
      ),
      "-"
    );
  }, [watchedEntity, staff?.entity, staff?.entityName, entityList, entityName]);
  const department = valueOrFallback(staff?.departmentName, "-");
  const subDepartment = valueOrFallback(staff?.subDepartmentName, "-");
  const lineManager = valueOrFallback(firstRealValue(staff?.lineManager, staff?.lineManagerName), "-");
  const clientManager = valueOrFallback(staff?.clientManager, "-");
  const designation = valueOrFallback(staff?.designation, "-");
  const role = valueOrFallback(staff?.role, "-");
  const resolvedEmploymentModeName = resolveMasterName(staff?.employmentMode, employmentModes);
  const employmentMode = valueOrFallback(
    firstRealValue(
      resolvedEmploymentModeName !== "-" ? resolvedEmploymentModeName : undefined,
      staff?.employmentModeName
    ),
    "-"
  );
  const employeeType = valueOrFallback(
    firstRealValue(
      staff?.employeeTypeName,
      resolveMasterName(staff?.employeeType, employeeTypes)
    ),
    "-"
  );
  const currentEmploymentModeForVisibility =
    editingSection === "work" || editingSection === "all"
      ? firstRealValue(watchedEmploymentMode, staff?.employmentMode, staff?.employmentModeName, employmentMode)
      : firstRealValue(staff?.employmentMode, employmentMode, staff?.employmentModeName);
  const currentEmploymentModeFlags = resolveEmploymentModeFlags(currentEmploymentModeForVisibility);
  const shouldShowStaffVendorFields = currentEmploymentModeFlags.isVendor;
  const employmentBaseLocation = getEmploymentBaseLocationValue(staff, employmentModes);
  // Default: show contract fields when employment mode is not permanent
  let shouldShowStaffContractFields = !currentEmploymentModeFlags.isPermanent;
  // Exception: If Employment Mode is Vendor Consultant AND the vendor's employment type is Permanent,
  // hide contract fields (this applies only to the Vendor Consultant -> Vendor Employment Mode -> Permanent flow)
  const currentVendorEmploymentTypeForVisibility =
    editingSection === "work" || editingSection === "all"
      ? firstRealValue(watchedVendorEmploymentType, staff?.vendorEmploymentType)
      : firstRealValue(staff?.vendorEmploymentType, undefined);
  const resolvedVendorEmploymentTypeName = employmentModes.find((m) => String(m?.id) === String(currentVendorEmploymentTypeForVisibility))?.name;
  const isVendorEmploymentPermanent = /permanent/i.test(String(resolvedVendorEmploymentTypeName || ""));
  const currentEmploymentModeName = employmentModes.find((m) => String(m?.id) === String(currentEmploymentModeForVisibility))?.name;
  if (/vendor consultant/i.test(String(currentEmploymentModeName || "")) && isVendorEmploymentPermanent) {
    shouldShowStaffContractFields = false;
  }

  const nextReviewDate = dateOrFallback(staff?.nextReviewDate, "-");
  const joiningDate = dateOrFallback(staff?.doj, "-");
  const probationEndDate = dateOrFallback(staff?.probationEndDate, "-");
  const contractStartDate = dateOrFallback(
    firstRealValue(
      staff?.contractStartDate,
      shouldShowStaffContractFields ? staff?.doj : undefined
    ),
    "-"
  );
  const contractEndDate = dateOrFallback(staff?.contractEndDate, "-");
  const lastWorkingDay = dateOrFallback(staff?.lwd, "-");
  const resignationDate = dateOrFallback(staff?.resignationDate, "-");
  const noticePeriod = formatDays(staff?.noticePeriod);
  const workLocation = valueOrFallback(staff?.workLocation, "-");
  const officeLocation = valueOrFallback(staff?.officeLocation, "-");
  const email = valueOrFallback(staff?.email, "-");
  const client = valueOrFallback(firstRealValue(staff?.accountName, staff?.currentMonthTopAccountName), "-");
  const project = valueOrFallback(firstRealValue(staff?.projectName, staff?.currentMonthTopProjectName), "-");
  const vendorName = valueOrFallback(
    firstRealValue(
      staff?.vendorName,
      resolveMasterName(firstRealValue(staff?.vendorId, staff?.vendor), vendorList)
    ),
    "-"
  );
  const vendorEmpId = valueOrFallback(staff?.vendorEmpId, "-");
  const vendorEmploymentModeOptions = employmentModes
    .filter((mode) => mode?.name !== VENDOR_CONSULTANT_MODE)
    .map((mode) => ({ label: mode.name, value: mode.id }));
  const vendorEmploymentType = valueOrFallback(
    resolveMasterName(staff?.vendorEmploymentType, employmentModes),
    "-"
  );
  const costToRd = formatDecimalAmount(staff?.costToRd);
  const salaryCurrency = valueOrFallback(
    resolveMasterName(staff?.salaryCurrency, currencies),
    "-"
  );
  const effectiveFrom = dateOrFallback(staff?.effectiveFrom, "-");
  const previousSalary = formatDecimalAmount(staff?.previousSalary);
  const bonusAmount = formatDecimalAmount(staff?.bonusAmount);
  const issuedDate = dateOrFallback(staff?.issuedDate, "-");
  const salaryRevisionHistory = buildSalaryRevisionHistory(staff);
  const storedCalculatedCtcAmount = calculateCtcFromSalaryRevisionHistory(salaryRevisionHistory);
  const currentSalaryRevisionHistoryForCtc =
    editingSection === "compensationBanking" || editingSection === "all"
      ? firstRealValue(watchedSalaryRevisionHistory, salaryRevisionHistory)
      : salaryRevisionHistory;
  const calculatedCtcAmount = calculateCtcFromSalaryRevisionHistory(currentSalaryRevisionHistoryForCtc);
  const ctcAmount = firstRealValue(calculatedCtcAmount, staff?.ctc);
  const ctcDisplay = formatDecimalAmount(ctcAmount);
  const storedCtcDisplay = formatDecimalAmount(firstRealValue(storedCalculatedCtcAmount, staff?.ctc));
  const currentCtc = formatMoney(ctcDisplay !== "-" ? ctcDisplay : undefined, salaryCurrency);
  const bonusHistory = buildBonusHistory(staff);
  const getDocumentRelatedToLabel = useCallback(
    (document: any) => {
      if (!document?.relatedRecordType || !document?.relatedRecordId) return "-";

      if (document.relatedRecordType === SALARY_REVISION_DOCUMENT_TYPE) {
        const row = normalizeSalaryRevisionHistory(salaryRevisionHistory).find(
          (item) => item.id === document.relatedRecordId
        );
        return row
          ? `Salary Revision - ${dateOrFallback(row.effectiveFrom)}`
          : "Salary Revision";
      }

      if (document.relatedRecordType === BONUS_DOCUMENT_TYPE) {
        const row = normalizeBonusHistory(bonusHistory).find(
          (item) => item.id === document.relatedRecordId
        );
        return row ? `Bonus - ${dateOrFallback(row.issuedDate)}` : "Bonus";
      }

      if (document.relatedRecordType === EXIT_INFORMATION_DOCUMENT_TYPE) {
        return "Exit Information";
      }

      return "-";
    },
    [bonusHistory, salaryRevisionHistory]
  );
  const gtlCoveredForEmployee = valueOrFallback(staff?.gtlCoveredForEmployee, "-");
  const exitReason = valueOrFallback(staff?.exitReason, "-");
  const exitType = valueOrFallback(staff?.exitType, "-");
  const rehireEligible = valueOrFallback(staff?.rehireEligible, "-");
  const fullAndFinalSettlementStatus = valueOrFallback(staff?.fullAndFinalSettlementStatus, "-");
  const isMalaysiaBankEntity = /malaysia|myr/i.test(String(entityName));
  const isMalaysiaStatutoryEntity = /malaysia|myr/i.test(String(statutoryEntityName));
  const selectedEntityNameForOfficeLocation =
    editingSection === "work" || editingSection === "all" || isEditing
      ? valueOrFallback(
        entityList.find((entity) => String(entity?.id) === String(watchedEntity))?.name,
        entityName
      )
      : entityName;
  const officeLocationOptions = getOfficeLocationOptionsForEntity(selectedEntityNameForOfficeLocation);
  const gradeBand = valueOrFallback(staff?.gradeBand, "-");
  const nextSalaryRevisionDate = dateOrFallback(staff?.nextSalaryRevisionDate, "-");
  const nationality = valueOrFallback(staff?.nationality, "-");
  const pan = valueOrFallback(staff?.pan, "-");
  const pfMemberId = valueOrFallback(staff?.pfMemberId, "-");
  const esicIpNumber = valueOrFallback(staff?.esicIpNumber, "-");
  const professionalTaxApplicableState = valueOrFallback(staff?.professionalTaxApplicableState, "-");
  const passportNumber = valueOrFallback(staff?.passportNumber, "-");
  const myKadNumber = valueOrFallback(staff?.myKadNumber, "-");
  const taxIdentificationNumber = valueOrFallback(staff?.taxIdentificationNumber, "-");
  const epfNumber = valueOrFallback(staff?.epfNumber, "-");
  const socsoNumber = valueOrFallback(staff?.socsoNumber, "-");
  const eisNumber = valueOrFallback(staff?.eisNumber, "-");
  const emergencyContactName = valueOrFallback(staff?.emergencyContactName, "-");
  const emergencyContactNumber = valueOrFallback(
    firstRealValue(staff?.emergencyContactNumber, staff?.emergencyContact),
    "-"
  );
  const emergencyContactRelationship = valueOrFallback(staff?.emergencyContactRelationship, "-");
  const lastAppraisal = valueOrFallback(staff?.lastAppraisal, "-");
  const leaveBalance = valueOrFallback(staff?.leaveBalance, "-");
  const aadhaar = formatAadhaar(staff?.aadhaar);
  const tenure = calculateTenure(staff?.doj, staff?.lwd);
  const currentSalaryEffectiveFrom = dateOrFallback(staff?.effectiveFrom, "-");

  const metrics: StaffProfileMetric[] = [
    { label: "Tenure", value: tenure, note: joiningDate !== "-" ? `since ${joiningDate}` : "-" },
    { label: "Current CTC", value: currentCtc, note: currentSalaryEffectiveFrom !== "-" ? `effective ${currentSalaryEffectiveFrom}` : "-" },
    // No appraisal table exists yet - default to an A+ grade for the current
    // financial year until appraisal data is stored.
    { label: "Last Appraisal", value: lastAppraisal, note: `${getCurrentFinancialYear()} cycle` },
    // Leave data does not exist in the system yet - show zero instead of the
    // mock value until the leave module is built.
    { label: "Leave Balance", value: leaveBalance, note: "across all types" },
  ];

  const MARITAL_STATUS_OPTIONS = [
    { label: "Single", value: "Single" },
    { label: "Married", value: "Married" },
    { label: "Divorced", value: "Divorced" },
    { label: "Widowed", value: "Widowed" },
    { label: "Separated", value: "Separated" },
    { label: "Other", value: "Other" },
  ];

  const BLOOD_GROUP_OPTIONS = [
    { label: "A+", value: "A+" },
    { label: "A-", value: "A-" },
    { label: "A1+", value: "A1+" },
    { label: "A1-", value: "A1-" },
    { label: "A2+", value: "A2+" },
    { label: "A2-", value: "A2-" },
    { label: "B+", value: "B+" },
    { label: "B-", value: "B-" },
    { label: "AB+", value: "AB+" },
    { label: "AB-", value: "AB-" },
    { label: "A1B+", value: "A1B+" },
    { label: "A1B-", value: "A1B-" },
    { label: "A2B+", value: "A2B+" },
    { label: "A2B-", value: "A2B-" },
    { label: "O+", value: "O+" },
    { label: "O-", value: "O-" },
    { label: "Bombay (Oh)+", value: "Bombay (Oh)+" },
    { label: "Bombay (Oh)-", value: "Bombay (Oh)-" },
    { label: "Rh-null", value: "Rh-null" },
  ];

  const HIGHEST_QUALIFICATION_OPTIONS = [
    { label: "High School", value: "High School" },
    { label: "Diploma", value: "Diploma" },
    { label: "Bachelor's Degree", value: "Bachelor's Degree" },
    { label: "Master's Degree", value: "Master's Degree" },
    { label: "Doctorate", value: "Doctorate" },
    { label: "Professional Certification", value: "Professional Certification" },
    { label: "Other", value: "Other" },
  ];

  const SKILL_PROFICIENCY_OPTIONS = [
    { label: "Beginner", value: "Beginner" },
    { label: "Intermediate", value: "Intermediate" },
    { label: "Advanced", value: "Advanced" },
    { label: "Expert", value: "Expert" },
  ];

  const addressCountryOptions = useMemo(
    () =>
      Country.getAllCountries().map((country) => ({
        label: country.name,
        value: country.name,
      })),
    []
  );
  const getAddressStateOptions = useCallback((countryValue: any) => {
    const countryCode = getCountryCode(countryValue);
    if (!countryCode) return [];

    return State.getStatesOfCountry(countryCode).map((state) => ({
      label: state.name,
      value: state.name,
    }));
  }, []);
  const communicationStateOptions = useMemo(
    () =>
      getAddressStateOptions(
        editingSection === "address" || editingSection === "all"
          ? watchedCommunicationCountry
          : staff?.communicationCountry
      ),
    [getAddressStateOptions, editingSection, watchedCommunicationCountry, staff?.communicationCountry]
  );
  const permanentStateOptions = useMemo(
    () =>
      getAddressStateOptions(
        editingSection === "address" || editingSection === "all"
          ? watchedPermanentCountry
          : staff?.permanentCountry
      ),
    [getAddressStateOptions, editingSection, watchedPermanentCountry, staff?.permanentCountry]
  );

  const employmentSnapshot = withRealValues(mock.overview.employmentSnapshot, {
    "Employment Mode": employmentMode,
    "Employee Type": employeeType,
    "Sub Department": subDepartment,
    "Work Mode": workLocation,
    "Work Location": staff?.workLocation === "WFO" ? officeLocation : "-",
    "Notice Period": noticePeriod,
    "Grade / Band": valueOrFallback(staff?.gradeBand, "-"),
    "Next Salary Revision Date": nextSalaryRevisionDate,
  }).filter((field) => staff?.workLocation === "WFO" || field.label !== "Work Location");

  const workDetails = withRealValues([
    ...mock.job.workDetails,
    { label: "Employment Base Location", value: "-" },
    { label: "Next Performance Review Date", value: "-" },
    { label: "Next Appraisal/Salary Revision Date", value: "-" },
  ], {
    Entity: entityName,
    "Employment Mode": employmentMode,
    "Employee Type": employeeType,
    "Staff Status": status,
    Designation: designation,
    Role: role,
    "Date of Joining": joiningDate,
    "Probation End Date": probationEndDate,
    "Last Working Day": lastWorkingDay,
    Department: department,
    "Sub Department": subDepartment,
    "RD Manager": lineManager,
    "Client Manager": clientManager,
    "Notice Period": noticePeriod,
    "Work Mode": workLocation,
    "Work Location": staff?.workLocation === "WFO" ? officeLocation : "-",
    "Employment Base Location": employmentBaseLocation,
    "Grade / Band": gradeBand,
    "Next Review Date": nextReviewDate,
    "Next Performance Review Date": dateOrFallback(staff?.nextPerformanceReviewDate, "-"),
    "Next Appraisal/Salary Revision Date": dateOrFallback(staff?.nextAppraisalSalaryRevisionDate, "-"),
    "Contract Start Date": contractStartDate,
    "Contract End Date": contractEndDate,
    "Vendor Name": vendorName,
    "Vendor Employee ID": vendorEmpId,
    "Vendor Employment Mode": vendorEmploymentType,
    "Cost to RD": costToRd,
  });
  const currentWorkModeForVisibility =
    editingSection === "work" || editingSection === "all"
      ? firstRealValue(watchedWorkLocation, staff?.workLocation)
      : staff?.workLocation;
  const shouldShowOfficeLocation = currentWorkModeForVisibility === "WFO";
  const visibleWorkDetails = shouldShowStaffContractFields
    ? workDetails
    : workDetails.filter(
      (field) =>
        !["Contract Start Date", "Contract End Date"].includes(field.label)
    );
  const visibleVendorWorkDetails = shouldShowStaffVendorFields
    ? visibleWorkDetails
    : visibleWorkDetails.filter(
      (field) =>
        ![
          "Vendor Name",
          "Vendor Employee ID",
          "Vendor Employment Mode",
          "Cost to RD",
        ].includes(field.label)
    );
  const visibleOfficeWorkDetails = shouldShowOfficeLocation
    ? visibleVendorWorkDetails
    : visibleVendorWorkDetails.filter((field) => field.label !== "Work Location");

  const basicInformation = withRealValues([
    ...mock.personal.basicInformation,
    { label: "Local Kannadiga", value: "-" },
    { label: "Native Language", value: "-" },
  ], {
    "Employee ID": employeeId,
    "Full Name": staffName,
    "Date of Birth": dateOrFallback(staff?.dateOfBirth || staff?.dob, "-"),
    Gender: valueOrFallback(
      firstRealValue(staff?.genderName, resolveMasterName(staff?.gender, genders)),
      "-"
    ),
    "Local Kannadiga": valueOrFallback(staff?.localKannadiga, "-"),
    "Native Language": valueOrFallback(staff?.nativeLanguage, "-"),
    Nationality: nationality,
    "Blood Group": valueOrFallback(resolveMasterName(staff?.bloodGroup, bloodGroups), "-"),
    "Photo Upload": staff?.profilePhoto ? "Uploaded" : "-",
    "Marital Status": valueOrFallback(resolveMasterName(staff?.maritalStatus, maritalStatuses), "-"),
  });

  const contactDetails = withRealValues(mock.personal.contactDetails, {
    "Official Email": email,
    "Personal Email": valueOrFallback(staff?.personalEmail, "-"),
    "Mobile Number": valueOrFallback(firstRealValue(staff?.mobile, staff?.phoneNumber), "-"),
    "Emergency Contact Name": emergencyContactName,
    "Emergency Contact Number": emergencyContactNumber,
    "Emergency Contact Relationship": emergencyContactRelationship,
  });
  const presentAddress = valueOrFallback(staff?.presentAddress, "");
  const permanentAddress = valueOrFallback(staff?.permanentAddress, "");
  const communicationAddressLine = valueOrFallback(
    firstRealValue(staff?.communicationAddressLine, staff?.presentAddress),
    "-"
  );
  const communicationCity = valueOrFallback(staff?.communicationCity, "-");
  const communicationCountry = getCountryName(staff?.communicationCountry);
  const communicationState = getStateName(staff?.communicationState, staff?.communicationCountry);
  const communicationPostalCode = valueOrFallback(staff?.communicationPostalCode, "-");
  const sameAsCommunicationAddress = toBooleanValue(staff?.sameAsCommunicationAddress);
  const permanentAddressLine = valueOrFallback(
    firstRealValue(
      staff?.permanentAddressLine,
      sameAsCommunicationAddress ? staff?.communicationAddressLine : undefined,
      staff?.permanentAddress,
      sameAsCommunicationAddress ? staff?.presentAddress : undefined
    ),
    "-"
  );
  const permanentCity = valueOrFallback(
    firstRealValue(staff?.permanentCity, sameAsCommunicationAddress ? staff?.communicationCity : undefined),
    "-"
  );
  const permanentCountry = valueOrFallback(
    getCountryName(firstRealValue(staff?.permanentCountry, sameAsCommunicationAddress ? staff?.communicationCountry : undefined)),
    "-"
  );
  const permanentState = valueOrFallback(
    getStateName(
      firstRealValue(staff?.permanentState, sameAsCommunicationAddress ? staff?.communicationState : undefined),
      firstRealValue(staff?.permanentCountry, sameAsCommunicationAddress ? staff?.communicationCountry : undefined)
    ),
    "-"
  );
  const permanentPostalCode = valueOrFallback(
    firstRealValue(staff?.permanentPostalCode, sameAsCommunicationAddress ? staff?.communicationPostalCode : undefined),
    "-"
  );
  const addressDetails = [
    { label: "Communication Country", value: communicationCountry },
    { label: "Communication State", value: communicationState },
    { label: "Communication Address Line", value: communicationAddressLine },
    { label: "Communication City", value: communicationCity },
    { label: "Communication Postal Code", value: communicationPostalCode },
    { label: "Permanent Country", value: permanentCountry },
    { label: "Permanent State", value: permanentState },
    { label: "Permanent Address Line", value: permanentAddressLine },
    { label: "Permanent City", value: permanentCity },
    { label: "Permanent Postal Code", value: permanentPostalCode },
    {
      label: "Same as Communication Address",
      value: sameAsCommunicationAddress ? "Yes" : "No",
    },
  ];
  const communicationAddressDetails = [
    { label: "Communication Country", value: communicationCountry },
    { label: "Communication State", value: communicationState },
    { label: "Communication City", value: communicationCity },
    { label: "Communication Postal Code", value: communicationPostalCode },
    { label: "Communication Address Line", value: communicationAddressLine },
  ];
  const permanentAddressDetails = [
    { label: "Permanent Country", value: permanentCountry },
    { label: "Permanent State", value: permanentState },
    { label: "Permanent City", value: permanentCity },
    { label: "Permanent Postal Code", value: permanentPostalCode },
    { label: "Permanent Address Line", value: permanentAddressLine },
  ];
  const indiaStatutoryDetails = [
    { label: "Entity", value: statutoryEntityName },
    { label: "PAN", value: pan },
    { label: "Aadhaar Number", value: aadhaar || "-" },
    { label: "UAN", value: valueOrFallback(staff?.uan, "-") },
    { label: "PF Member ID", value: pfMemberId },
    { label: "ESIC IP Number", value: esicIpNumber },
    {
      label: "Professional Tax - Applicable State",
      value: professionalTaxApplicableState,
    },
  ];
  const malaysiaStatutoryDetails = [
    { label: "Entity", value: statutoryEntityName },
    { label: "Passport Number", value: passportNumber },
    { label: "Passport Expiry Date", value: dateOrFallback(staff?.passportExpiryDate) },
    { label: "Visa Type", value: valueOrFallback(staff?.visaType) },
    { label: "Visa Expiry Date", value: dateOrFallback(staff?.visaExpiryDate) },
    { label: "MyKad Number", value: myKadNumber },
    { label: "Tax Identification Number", value: taxIdentificationNumber },
    { label: "EPF Number", value: epfNumber },
    { label: "SOCSO Number", value: socsoNumber },
    { label: "EIS Number", value: eisNumber },
  ];
  const statutoryDetails = isMalaysiaStatutoryEntity
    ? malaysiaStatutoryDetails
    : indiaStatutoryDetails;
  const previousEmploymentHistory = parsePreviousEmploymentHistory(staff?.previousEmploymentHistory);
  const currentPreviousEmploymentHistory =
    editingSection === "priorExperience" || editingSection === "all"
      ? normalizePreviousEmploymentHistory(
        parsePreviousEmploymentHistory(
          firstRealValue(watchedPreviousEmploymentHistory, previousEmploymentHistory)
        )
      )
      : normalizePreviousEmploymentHistory(previousEmploymentHistory);
  const calculatedTotalExperiencePriorToJoining = formatExperienceFromMonths(
    getPreviousEmploymentTotalMonths(currentPreviousEmploymentHistory)
  );
  const totalExperiencePriorToJoining = valueOrFallback(
    calculatedTotalExperiencePriorToJoining,
    "-"
  );
  const overallExperience = formatExperienceFromMonths(
    getTenureMonths(staff?.doj, staff?.lwd) +
    getPreviousEmploymentTotalMonths(currentPreviousEmploymentHistory)
  );
  const displayPreviousEmploymentHistory = normalizePreviousEmploymentHistory(previousEmploymentHistory);
  const highestQualification = valueOrFallback(staff?.highestQualification, "-");
  const institutionUniversity = valueOrFallback(staff?.institutionUniversity, "-");
  const yearOfPassing = valueOrFallback(staff?.yearOfPassing, "-");
  const specialization = valueOrFallback(staff?.specialization, "-");
  const skillDetails = buildSkillDetails(
    staff?.skillDetails,
    staff?.primarySkills,
    staff?.secondarySkills,
    staff?.skillProficiencyLevel
  );
  const priorExperienceDetails = [
    { label: "Total Experience Prior to Joining", value: totalExperiencePriorToJoining },
    { label: "Overall Experience", value: overallExperience },
  ];

  useEffect(() => {
    if (editingSection !== "priorExperience" && editingSection !== "all") return;
    if (form.getFieldValue("totalExperiencePriorToJoining") === calculatedTotalExperiencePriorToJoining) {
      return;
    }
    form.setFieldsValue({
      totalExperiencePriorToJoining: calculatedTotalExperiencePriorToJoining,
    });
  }, [calculatedTotalExperiencePriorToJoining, editingSection, form]);

  useEffect(() => {
    if (editingSection !== "compensationBanking" && editingSection !== "all") return;
    const nextCtc = calculateCtcFromSalaryRevisionHistory(
      firstRealValue(watchedSalaryRevisionHistory, salaryRevisionHistory)
    );
    if (nextCtc === null || Number(form.getFieldValue("ctc")) === nextCtc) return;
    form.setFieldsValue({ ctc: nextCtc });
  }, [editingSection, form, salaryRevisionHistory, watchedSalaryRevisionHistory]);

  const educationalQualificationDetails = [
    { label: "Highest Qualification", value: highestQualification },
    { label: "Institution / University", value: institutionUniversity },
    { label: "Year of Passing", value: yearOfPassing },
    { label: "Specialization", value: specialization },
  ];
  const displaySkillDetails = normalizeSkillDetails(skillDetails);
  const dependents = normalizeDependents(parseDependents(staff?.dependents));
  const displayDependents = dependents;
  const positionHistory = parsePositionHistory(staff?.positionHistory);
  const displayPositionHistory = normalizePositionHistory(positionHistory);
  const projectTimeline = parseProjectTimeline(staff?.projectTimeline);
  const displayProjectTimeline = normalizeProjectTimeline(projectTimeline);
  const isSameAsCommunicationAddressChecked = toBooleanValue(
    firstRealValue(watchedSameAsCommunicationAddress, staff?.sameAsCommunicationAddress)
  );


  const maritalStatusOptions = mergeSelectOptions(
    maritalStatuses.map((ms) => ({ label: ms.name, value: ms.id })),
    MARITAL_STATUS_OPTIONS
  );
  const bloodGroupOptions = mergeSelectOptions(
    bloodGroups.map((bg) => ({ label: bg.name, value: bg.id })),
    BLOOD_GROUP_OPTIONS
  );
  const emergencyContactRelationshipOptions = [
    { label: "Parent", value: "Parent" },
    { label: "Spouse", value: "Spouse" },
    { label: "Sibling", value: "Sibling" },
    { label: "Child", value: "Child" },
    { label: "Friend", value: "Friend" },
    { label: "Other", value: "Other" },
  ];
  const dependentRelationshipOptions = [
    { label: "Father", value: "Father" },
    { label: "Mother", value: "Mother" },
    { label: "Spouse", value: "Spouse" },
    { label: "Son", value: "Son" },
    { label: "Daughter", value: "Daughter" },
    { label: "Brother", value: "Brother" },
    { label: "Sister", value: "Sister" },
    { label: "Father-in-law", value: "Father-in-law" },
    { label: "Mother-in-law", value: "Mother-in-law" },
    { label: "Other", value: "Other" },
  ];
  const getOptionLabel = (
    value: any,
    options: any[],
    fallbackValue: any
  ) => {
    const option = options?.find(
      (opt) =>
        String(opt.value) === String(value) ||
        String(opt.id) === String(value) ||
        String(opt.departmentId) === String(value) ||
        String(opt.staffId) === String(value) ||
        String(opt.accountId) === String(value) ||
        String(opt.projectId) === String(value)
    );

    return option
      ? option.label ||
      option.name ||
      option.departmentName ||
      option.staffName ||
      option.accountName ||
      option.projectName ||
      option.subDepartmentName ||
      option.entityName ||
      option.vendorName
      : fallbackValue;
  };

  const lineManagerOptions = useMemo(
    () =>
      sortSelectOptionsByLabel(
        lineManagers.map((lm) => ({ label: lm.staffName, value: lm.staffId }))
      ),
    [lineManagers]
  );

  const positionHistoryReportingToOptions = useMemo(
    () =>
      sortSelectOptionsByLabel(
        lineManagers.map((staffMember) => ({
          label: staffMember.staffName,
          value: staffMember.staffName,
        }))
      ),
    [lineManagers]
  );

  const editableFieldsConfig = useMemo(
    () => [
      // Entity
      {
        key: "staffName",
        label: "Full Name",
        type: "text",
        getValue: (s: any) => s?.staffName,
        oldValue: staffName,
        newValue: (val: any) => val,
        rules: [{ required: true, message: "Please enter Full Name" },
        { pattern: /^[A-Za-z\s.'/-]+$/, message: "Please enter a valid Full Name. Only letters, spaces, apostrophes ('), dots (.), hyphens (-), and slashes (/) are allowed." }],
      },
      {
        key: "entity",
        label: "Entity",
        type: "select",
        options: entityList.map((e) => ({ label: e.name, value: e.id })),
        getValue: (s: any) => s?.entity,
        oldValue: entityName,
        newValue: (val: any) => getOptionLabel(val, entityList, entityName),
        rules: [{ required: true, message: "Please select Entity" }],
        help: "Select the legal entity. Statutory fields change based on the selected entity.",
      },
      {
        key: "employeeId",
        label: "Employee ID",
        type: "text",
        getValue: (s: any) => s?.employeeId,
        oldValue: employeeId,
        newValue: (val: any) => val,
        rules: [
          { required: true, message: "Please enter Employee ID" },
          {
            validator: (_: any, value: string) => {
              if (!value) return Promise.resolve();
              const regex = /^[A-Za-z]{2,3}[0-9]{5}$/;
              return regex.test(value)
                ? Promise.resolve()
                : Promise.reject(new Error("Invalid format. Please check the Info."));
            },
          },
        ],
      },
      // Employment classification
      {
        key: "employmentMode",
        label: "Employment Mode",
        type: "select",
        options: employmentModes.map((em) => ({ label: em.name, value: em.id })),
        getValue: (s: any) => s?.employmentMode, // Raw value from staff
        oldValue: getOptionLabel(staff?.employmentMode, employmentModes, employmentMode),
        newValue: (val: any) => getOptionLabel(val, employmentModes, employmentMode),
      },
      {
        key: "employeeType",
        label: "Employee Type",
        type: "select",
        options: employeeTypes.map((et) => ({ label: et.name, value: et.id })),
        getValue: (s: any) => s?.employeeType, // Raw value from staff
        oldValue: getOptionLabel(staff?.employeeType, employeeTypes, employeeType),
        newValue: (val: any) => getOptionLabel(val, employeeTypes, employeeType),
      },
      // Staff Status
      {
        key: "staffStatusId",
        label: "Staff Status",
        type: "select",
        options: [
          { value: "DRAFT", label: "DRAFT" }, { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" }, { value: "NOTICE PERIOD", label: "NOTICE PERIOD" },
        ],
        rules: [{ required: true, message: "Please select Staff Status" }],
        getValue: (s: any) => s?.staffStatusId, oldValue: valueOrFallback(staff?.staffStatusId), newValue: (val: any) => valueOrFallback(val),
      },
      { key: "designation", label: "Designation", type: "text", getValue: (s: any) => s?.designation, oldValue: designation, newValue: (val: any) => val },
      { key: "role", label: "Role", type: "text", getValue: (s: any) => s?.role, oldValue: role, newValue: (val: any) => val },
      { key: "doj", label: "Date of Joining", type: "date", getValue: (s: any) => s?.doj, oldValue: joiningDate, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      { key: "probationEndDate", label: "Probation End Date", type: "date", getValue: (s: any) => s?.probationEndDate, oldValue: probationEndDate, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      { key: "contractStartDate", label: "Contract Start Date", type: "date", getValue: (s: any) => firstRealValue(s?.contractStartDate, shouldShowStaffContractFields ? s?.doj : undefined), oldValue: contractStartDate, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      { key: "contractEndDate", label: "Contract End Date", type: "date", getValue: (s: any) => s?.contractEndDate, oldValue: contractEndDate, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      { key: "lwd", label: "Last Working Day", type: "date", getValue: (s: any) => s?.lwd, oldValue: lastWorkingDay, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      { key: "resignationDate", label: "Resignation Date", type: "date", getValue: (s: any) => s?.resignationDate, oldValue: resignationDate, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      {
        key: "departmentId",
        label: "Department",
        type: "select",
        options: departments.map((d) => ({ label: d.departmentName, value: d.departmentId })),
        getValue: (s: any) => s?.departmentId, // Raw value from staff
        oldValue: getOptionLabel(staff?.departmentId, departments, department),
        newValue: (val: any) => getOptionLabel(val, departments, department),
      },
      {
        key: "subDepartmentId",
        label: "Sub Department",
        type: "select",
        options: subDepartmentList.map((sd) => ({ label: sd.subDepartmentName, value: sd.id })),
        getValue: (s: any) => s?.subDepartmentId,
        oldValue: getOptionLabel(staff?.subDepartmentId, subDepartmentList, subDepartment),
        newValue: (val: any) => getOptionLabel(val, subDepartmentList, subDepartment),
      },
      {
        key: "lineManagerId",
        label: "RD Manager",
        type: "select",
        options: lineManagerOptions,
        getValue: (s: any) => s?.lineManagerId, // Raw value from staff
        oldValue: getOptionLabel(staff?.lineManagerId, lineManagers, lineManager),
        newValue: (val: any) => getOptionLabel(val, lineManagers, lineManager),
      },
      { key: "clientManager", label: "Client Manager", type: "text", getValue: (s: any) => s?.clientManager, oldValue: clientManager, newValue: (val: any) => val },
      { key: "noticePeriod", label: "Notice Period", type: "text", getValue: (s: any) => s?.noticePeriod, oldValue: staff?.noticePeriod, newValue: (val: any) => val, rules: [{ pattern: /^\d+$/, message: "Please enter Notice Period" }] },
      {
        key: "workLocation",
        label: "Work Mode",
        type: "select",
        options: WORK_LOCATION_OPTIONS,
        getValue: (s: any) => s?.workLocation,
        oldValue: workLocation,
        newValue: (val: any) => valueOrFallback(val),
        rules: [{ required: true, message: "Please select Work Mode" }],
      },
      {
        key: "employmentBaseLocation",
        label: "Employment Base Location",
        type: "select",
        options: mergeSelectOptions(
          getAddressStateOptions("India"),
          [{ label: "NA", value: "NA" }]
        ),
        disabled: showVendorField,
        getValue: (s: any) => getEmploymentBaseLocationValue(s, employmentModes),
        oldValue: employmentBaseLocation,
        newValue: (val: any) => valueOrFallback(val, "-"),
      },
      {
        key: "officeLocation",
        label: "Work Location",
        type: "select",
        options: officeLocationOptions,
        getValue: (s: any) => s?.officeLocation,
        oldValue: officeLocation,
        newValue: (val: any) => valueOrFallback(val),
        dependencies: ["workLocation"],
        rules: [
          ({ getFieldValue }: any): any => ({
            validator(_: any, value: any): Promise<void> {
              if (getFieldValue("workLocation") !== "WFO" || value) return Promise.resolve();
              return Promise.reject(new Error("Please select Work Location"));
            },
          }),
        ],
      },
      {
        key: "gradeBand",
        label: "Grade / Band",
        type: "select",
        options: GRADE_BAND_OPTIONS,
        getValue: (s: any) => s?.gradeBand,
        oldValue: gradeBand,
        newValue: (val: any) => valueOrFallback(val),
      },
      { key: "nextPerformanceReviewDate", label: "Next Performance Review Date", type: "date", getValue: (s: any) => s?.nextPerformanceReviewDate, oldValue: dateOrFallback(staff?.nextPerformanceReviewDate, "-"), newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      { key: "nextAppraisalSalaryRevisionDate", label: "Next Appraisal/Salary Revision Date", type: "date", getValue: (s: any) => s?.nextAppraisalSalaryRevisionDate, oldValue: dateOrFallback(staff?.nextAppraisalSalaryRevisionDate, "-"), newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      { key: "nextSalaryRevisionDate", label: "Next Salary Revision Date", type: "date", getValue: (s: any) => s?.nextSalaryRevisionDate, oldValue: nextSalaryRevisionDate, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      {
        key: "nextReviewDate", label: "Next Review Date", type: "date", getValue: (s: any) => s?.nextReviewDate, oldValue: nextReviewDate,
        newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-",
        rules: [{ required: true, message: "Please select Salary Currency" }],
      },
      {
        key: "ctc",
        label: "CTC",
        type: "text",
        getValue: (s: any) => calculateCtcFromSalaryRevisionHistory(buildSalaryRevisionHistory(s)) ?? s?.ctc,
        oldValue: storedCtcDisplay,
        newValue: (val: any) => formatDecimalAmount(val),
      },
      {
        key: "salaryCurrency",
        label: "Salary Currency",
        type: "select",
        options: currencies.length
          ? currencies.map((currency) => ({
            label: currency.name || currency.code,
            value: currency.id || currency.value || currency.name,
          }))
          : [
            { label: "INR", value: "INR" },
            { label: "MYR", value: "MYR" },
          ],
        getValue: (s: any) => s?.salaryCurrency,
        oldValue: salaryCurrency,
        newValue: (val: any) => getOptionLabel(val, currencies, valueOrFallback(val)),
        rules: [{ required: true, message: "Please select Salary Currency" }],
      },
      {
        key: "effectiveFrom", label: "Effective From", type: "date", getValue: (s: any) => s?.effectiveFrom, oldValue: effectiveFrom, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-",
        rules: [{ required: true, message: "Please select Effective From date" }]
      },
      {
        key: "previousSalary", label: "Current Salary", type: "text", rules: [{ required: true, message: "Please enter Current Salary" }], getValue: (s: any) => s?.previousSalary, oldValue: previousSalary, newValue: (val: any) => formatDecimalAmount(val)

      },
      {
        key: "salaryRevisionHistory",
        label: "Salary Revision History",
        type: "salaryRevisionHistory",
        getValue: (s: any) => buildSalaryRevisionHistory(s),
        oldValue: formatSalaryRevisionHistoryChange(salaryRevisionHistory),
        newValue: (val: any) => formatSalaryRevisionHistoryChange(val),
      },
      { key: "bonusAmount", label: "Bonus Amount", type: "text", getValue: (s: any) => s?.bonusAmount, oldValue: bonusAmount, newValue: (val: any) => formatDecimalAmount(val), rules: [{ required: true, message: "Please enter Bonus Amount" }] },
      { key: "issuedDate", label: "Issued Date", type: "date", getValue: (s: any) => s?.issuedDate, oldValue: issuedDate, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-", rules: [{ required: true, message: "Please select Issued Date" }] },
      {
        key: "bonusHistory",
        label: "Bonus History",
        type: "bonusHistory",
        getValue: (s: any) => buildBonusHistory(s),
        oldValue: formatBonusHistoryChange(bonusHistory),
        newValue: (val: any) => formatBonusHistoryChange(val),
      },
      {
        key: "gtlCoveredForEmployee",
        label: "GTL Covered for Employee",
        type: "select",
        options: [
          { label: "Yes", value: "Yes" },
          { label: "No", value: "No" },
        ],
        getValue: (s: any) => s?.gtlCoveredForEmployee,
        oldValue: gtlCoveredForEmployee,
        newValue: (val: any) => valueOrFallback(val),
      },
      { key: "indiaBankName", label: "Bank Name", type: "text", getValue: (s: any) => s?.indiaBankName, oldValue: valueOrFallback(staff?.indiaBankName), newValue: (val: any) => val },
      { key: "indiaBankAccountNumber", label: "Bank Account Number", type: "text", inputMode: "numeric", getValue: (s: any) => s?.indiaBankAccountNumber, oldValue: valueOrFallback(staff?.indiaBankAccountNumber), newValue: (val: any) => val, rules: [{ pattern: /^\d+$/, message: "Please enter a valid Bank Account Number" }] },
      { key: "indiaBranchName", label: "Branch Name", type: "text", getValue: (s: any) => s?.indiaBranchName, oldValue: valueOrFallback(staff?.indiaBranchName), newValue: (val: any) => val, },
      { key: "indiaAccountHolderName", label: "Account Holder Name", type: "text", getValue: (s: any) => s?.indiaAccountHolderName, oldValue: valueOrFallback(staff?.indiaAccountHolderName), newValue: (val: any) => val, },
      { key: "indiaIfscCode", label: "IFSC Code", type: "text", getValue: (s: any) => s?.indiaIfscCode, oldValue: valueOrFallback(staff?.indiaIfscCode), newValue: (val: any) => val },
      { key: "malaysiaBankName", label: "Bank Name", type: "text", getValue: (s: any) => s?.malaysiaBankName, oldValue: valueOrFallback(staff?.malaysiaBankName), newValue: (val: any) => val, },
      { key: "malaysiaBankAccountNumber", label: "Bank Account Number", type: "text", inputMode: "numeric", getValue: (s: any) => s?.malaysiaBankAccountNumber, oldValue: valueOrFallback(staff?.malaysiaBankAccountNumber), newValue: (val: any) => val, rules: [{ pattern: /^\d+$/, message: "Please enter a valid Bank Account Number" }] },
      { key: "malaysiaAccountHolderName", label: "Account Holder Name", type: "text", getValue: (s: any) => s?.malaysiaAccountHolderName, oldValue: valueOrFallback(staff?.malaysiaAccountHolderName), newValue: (val: any) => val, },
      { key: "malaysiaSwiftBicCode", label: "Bank SWIFT / BIC Code", type: "text", getValue: (s: any) => s?.malaysiaSwiftBicCode, oldValue: valueOrFallback(staff?.malaysiaSwiftBicCode), newValue: (val: any) => val, },
      {
        key: "exitReason",
        label: "Exit Reason",
        type: "select",
        options: [
          { label: "Better Opportunity", value: "Better Opportunity" },
          { label: "Personal Reasons", value: "Personal Reasons" },
          { label: "Relocation", value: "Relocation" },
          { label: "Higher Studies", value: "Higher Studies" },
          { label: "Health Reasons", value: "Health Reasons" },
          { label: "Performance Issues", value: "Performance Issues" },
          { label: "Contract Ended", value: "Contract Ended" },
          { label: "Retirement", value: "Retirement" },
          { label: "Absconded", value: "Absconded" },
          { label: "Mutual Separation", value: "Mutual Separation" },
          { label: "Transfer", value: "Transfer" },
        ],
        getValue: (s: any) => s?.exitReason,
        oldValue: exitReason,
        newValue: (val: any) => valueOrFallback(val),
        rules: [{ required: true, message: "Please select Exit Reason" }],
      },
      {
        key: "exitType",
        label: "Exit Type",
        type: "select",
        options: [
          { label: "Resignation", value: "Resignation" },
          { label: "Termination", value: "Termination" },
          { label: "Redundancy", value: "Redundancy" },
          { label: "Retirement", value: "Retirement" },
        ],
        getValue: (s: any) => s?.exitType,
        oldValue: exitType,
        newValue: (val: any) => valueOrFallback(val),
        rules: [{ required: true, message: "Please select Exit Type" }],
      },
      {
        key: "rehireEligible",
        label: "Rehire Eligible",
        type: "select",
        options: [
          { label: "Yes", value: "Yes" },
          { label: "No", value: "No" },
          { label: "Not applicable", value: "Not applicable" },
        ],
        getValue: (s: any) => s?.rehireEligible,
        oldValue: rehireEligible,
        newValue: (val: any) => valueOrFallback(val),
      },
      {
        key: "fullAndFinalSettlementStatus",
        label: "Full & Final Settlement Status",
        type: "select",
        options: [
          { label: "Pending", value: "Pending" },
          { label: "Completed", value: "Completed" },
        ],
        getValue: (s: any) => s?.fullAndFinalSettlementStatus,
        oldValue: fullAndFinalSettlementStatus,
        newValue: (val: any) => valueOrFallback(val),
      },
      {
        key: "accountId",
        label: "Client",
        type: "select",
        options: accountList.map((c) => ({ label: c.accountName, value: c.accountId })),
        getValue: (s: any) => s?.accountId, // Raw value from staff
        oldValue: getOptionLabel(staff?.accountId, accountList, client),
        newValue: (val: any) => getOptionLabel(val, accountList, client),
      },
      {
        key: "projectId",
        label: "Project",
        type: "select",
        options: filteredProjectList.map((p) => ({ label: p.projectName, value: p.projectId })),
        getValue: (s: any) => s?.projectId, // Raw value from staff
        oldValue: getOptionLabel(staff?.projectId, filteredProjectList, project),
        newValue: (val: any) => getOptionLabel(val, filteredProjectList, project),
      },
      {
        key: "vendorId",
        label: "Vendor Name",
        type: "select",
        options: vendorList.map((v) => ({ label: v.vendorName, value: v.vendorId })),
        getValue: (s: any) => s?.vendorId,
        oldValue: getOptionLabel(firstRealValue(staff?.vendorId, staff?.vendor), vendorList, valueOrFallback(staff?.vendorName)),
        newValue: (val: any) => getOptionLabel(val, vendorList, valueOrFallback(staff?.vendorName)),
        rules: [{ required: showVendorField }, [textOnlyRule("Vendor Name")]],
      },
      {
        key: "vendorEmpId",
        label: "Vendor Employee ID",
        type: "text",
        getValue: (s: any) => s?.vendorEmpId,
        oldValue: vendorEmpId,
        newValue: (val: any) => val,
        rules: [{ required: showVendorField, message: "Please enter Vendor Employee ID" }],
      },
      {
        key: "vendorEmploymentType",
        label: "Vendor Employment Mode",
        type: "select",
        options: vendorEmploymentModeOptions,
        getValue: (s: any) => s?.vendorEmploymentType,
        oldValue: vendorEmploymentType,
        newValue: (val: any) => getOptionLabel(val, vendorEmploymentModeOptions, vendorEmploymentType),
        rules: [{ required: showVendorField, message: "Please enter Vendor Employment Mode" }],
      },
      {
        key: "costToRd",
        label: "Cost to RD",
        type: "text",
        getValue: (s: any) => s?.costToRd,
        oldValue: costToRd,
        newValue: (val: any) => formatDecimalAmount(val),
        rules: [{ required: showVendorField, message: "Please enter Cost to RD" }],
      },
      // Personal Details / Identity Basics
      {
        key: "dateOfBirth",
        label: "Date of Birth",
        type: "date",
        getValue: (s: any) => s?.dateOfBirth || s?.dob,
        oldValue: dateOrFallback(staff?.dateOfBirth || staff?.dob),
        newValue: (val: any) => val ? moment(val).format("DD-MMM-YYYY") : "-",
        disabledDate: (current: any) => !!(current && current > dayjs().endOf("day")),
        rules: [
          {
            validator: (_: any, value: any) => {
              if (!value) return Promise.resolve();
              return dayjs(value).isAfter(dayjs(), "day")
                ? Promise.reject(new Error("Date of Birth cannot be in the future"))
                : Promise.resolve();
            },
          },
        ],
      },
      {
        key: "gender",
        label: "Gender",
        type: "select",
        options: mergeSelectOptions(
          genders.map((g) => ({ label: g.name, value: g.id })),
          [{ label: "Other", value: "Other" }]
        ),
        rules: [{ required: true, message: "Please select Gender" }],
        getValue: (s: any) => s?.gender, // Raw value from staff
        oldValue: getOptionLabel(
          staff?.gender,
          mergeSelectOptions(genders.map((g) => ({ label: g.name, value: g.id })), [{ label: "Other", value: "Other" }]),
          valueOrFallback(staff?.genderName)
        ),
        newValue: (val: any) => getOptionLabel(
          val,
          mergeSelectOptions(genders.map((g) => ({ label: g.name, value: g.id })), [{ label: "Other", value: "Other" }]),
          valueOrFallback(staff?.genderName)
        ),
      },
      {
        key: "localKannadiga",
        label: "Local Kannadiga",
        type: "select",
        options: [
          { label: "Yes", value: "Yes" },
          { label: "No", value: "No" },
        ],
        getValue: (s: any) => s?.localKannadiga,
        oldValue: valueOrFallback(staff?.localKannadiga, "-"),
        newValue: (val: any) => valueOrFallback(val, "-"),
      },
      {
        key: "nativeLanguage",
        label: "Native Language",
        type: "select",
        options: NATIVE_LANGUAGE_OPTIONS,
        getValue: (s: any) => s?.nativeLanguage,
        oldValue: valueOrFallback(staff?.nativeLanguage, "-"),
        newValue: (val: any) => valueOrFallback(val, "-"),
      },
      {
        key: "nationality",
        label: "Nationality",
        type: "select",
        options: NATIONALITY_OPTIONS,
        rules: [{ required: true, message: "Please select Nationality" }],
        getValue: (s: any) => s?.nationality,
        oldValue: getOptionLabel(staff?.nationality, NATIONALITY_OPTIONS, nationality),
        newValue: (val: any) => getOptionLabel(val, NATIONALITY_OPTIONS, val),
      },
      {
        key: "pan",
        label: "PAN",
        type: "text",
        getValue: (s: any) => s?.pan,
        oldValue: pan?.toUpperCase(),
        newValue: (val: any) => val?.toUpperCase(),
        help: "Format: 5 uppercase letters + 4 digits + 1 uppercase letter. Example: ABCDE1234F.",
        rules: [{ required: true, pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, message: "Format: 5 uppercase letters + 4 digits + 1 uppercase letter. Example: ABCDE1234F." }],
      },
      {
        key: "aadhaar",
        label: "Aadhaar",
        type: "text",
        getValue: (s: any) => unformatAadhaar(s?.aadhaar),
        oldValue: formatAadhaar(aadhaar),
        newValue: (val: any) => formatAadhaar(val),
        help: "Format: 12 digits. It will display as 1234 5678 9012.",
        rules: [
          {
            required: true,
            message: 'Aadhaar number is required',
          },
          {
            validator: async (_: any, value: string) => {
              if (!value) return Promise.resolve();
              return unformatAadhaar(value).length === 12
                ? Promise.resolve()
                : Promise.reject(new Error("Format: 12 digits. It will display as 1234 5678 9012."));
            },
          },
        ],
      },
      {
        key: "bloodGroup",
        label: "Blood Group",
        type: "select",
        options: bloodGroupOptions,
        getValue: (s: any) => s?.bloodGroup,
        oldValue: getOptionLabel(
          staff?.bloodGroup,
          bloodGroupOptions,
          valueOrFallback(staff?.bloodGroupName)
        ),
        newValue: (val: any) =>
          getOptionLabel(
            val,
            bloodGroupOptions,
            valueOrFallback(staff?.bloodGroupName)
          ),
      },
      {
        key: "profilePhoto",
        label: "Photo Upload",
        type: "imageUpload",
        getValue: (s: any) => s?.profilePhoto,
        oldValue: staff?.profilePhoto ? "Uploaded" : "-",
        newValue: (val: any) => (val ? "Uploaded" : "-"),
        help: "JPG, JPEG or PNG • Max 2 MB • Recommended: 500 × 500 px",
      },
      {
        key: "maritalStatus",
        label: "Marital Status",
        type: "select",
        options: maritalStatusOptions,
        getValue: (s: any) => s?.maritalStatus,
        oldValue: getOptionLabel(
          staff?.maritalStatus,
          maritalStatusOptions,
          valueOrFallback(staff?.maritalStatusName),
        ),
        newValue: (val: any) =>
          getOptionLabel(
            val,
            maritalStatusOptions,
            valueOrFallback(staff?.maritalStatusName)
          ),
      },
      {
        key: "uan",
        label: "UAN",
        type: "text",
        getValue: (s: any) => s?.uan,
        oldValue: valueOrFallback(staff?.uan),
        newValue: (val: any) => val,
        help: "Format: 12 digits.",
        // rules: [
        //   {required: true, pattern: /^\d{12}$/, message: "Format: 12 digits." }
        // ],
      },
      {
        key: "pfMemberId",
        label: "PF Member ID",
        type: "text",
        maxLength: 27,
        getValue: (s: any) => s?.pfMemberId,
        oldValue: pfMemberId,
        newValue: (val: any) => valueOrFallback(val),
        help: "Format: Alphanumeric PF Member ID as issued. Example: XX/XXX/1234567/000/1234567.",
        rules: [
          {
            pattern: /^[A-Z]{2}\/[A-Z]{3}\/\d{7}\/\d{3}\/\d{7}$/,
            message: "PF Member ID must be in the format TN/MAS/1234567/000/0001234",
          },
          {
            maxLength: 27,
            message: "PF Member ID must not exceed 27 characters",
          }
        ],
      },
      {
        key: "esicIpNumber",
        label: "ESIC IP Number",
        type: "text",
        maxLength: 10,
        getValue: (s: any) => s?.esicIpNumber,
        oldValue: esicIpNumber,
        newValue: (val: any) => valueOrFallback(val),
        help: "Format: 10 digits.",
        rules: [
          {
            pattern: /^\d{10}$/,
            message: "ESIC IP Number must contain exactly 10 digits",
          },
        ],
      },
      {
        key: "professionalTaxApplicableState",
        label: "Professional Tax - Applicable State",
        type: "select",
        options: getAddressStateOptions("India"),
        getValue: (s: any) => s?.professionalTaxApplicableState,
        oldValue: professionalTaxApplicableState,
        newValue: (val: any) => valueOrFallback(val),
        help: "Format: Select the Indian state where Professional Tax is applicable.",
      },
      {
        key: "passportNumber",
        label: "Passport Number",
        type: "text",
        getValue: (s: any) => s?.passportNumber,
        oldValue: passportNumber,
        newValue: (val: any) => valueOrFallback(val),
        help: "Format: Alphanumeric passport number. Example: A1234567.",
        rules: [
          { required: true, pattern: /^[A-Za-z0-9]{6,12}$/, message: "Format: Alphanumeric passport number. Example: A1234567." },
        ],
      },
      {
        key: "passportExpiryDate",
        label: "Passport Expiry Date",
        type: "date",
        getValue: (s: any) => s?.passportExpiryDate,
        oldValue: dateOrFallback(staff?.passportExpiryDate),
        newValue: (val: any) => dateOrFallback(val),
        rules: [{ required: isMalaysiaStatutoryEntity, message: "Please enter Passport Expiry Date" }],
      },
      {
        key: "visaType",
        label: "Visa Type",
        type: "select",
        options: VISA_TYPE_OPTIONS,
        getValue: (s: any) => s?.visaType,
        oldValue: valueOrFallback(staff?.visaType),
        newValue: (val: any) => valueOrFallback(val),
        rules: [{ required: isMalaysiaStatutoryEntity, message: "Please enter Visa Type" }],
      },
      {
        key: "visaExpiryDate",
        label: "Visa Expiry Date",
        type: "date",
        getValue: (s: any) => s?.visaExpiryDate,
        oldValue: dateOrFallback(staff?.visaExpiryDate),
        newValue: (val: any) => dateOrFallback(val),
        rules: [
          { required: isMalaysiaStatutoryEntity, message: "Please enter Visa Expiry Date" }
          ,
          ({ getFieldValue }: any) => ({
            validator(_: any, value: any) {
              const passportExpiryDate = getFieldValue("passportExpiryDate");

              if (
                value &&
                passportExpiryDate &&
                dayjs(value).isAfter(dayjs(passportExpiryDate), "day")
              ) {
                return Promise.reject(
                  new Error("Visa Expiry Date cannot be later than Passport Expiry Date")
                );
              }

              return Promise.resolve();
            },
          }),
        ],
      },
      {
        key: "myKadNumber",
        label: "MyKad Number",
        type: "text",
        getValue: (s: any) => s?.myKadNumber,
        oldValue: myKadNumber,
        newValue: (val: any) => valueOrFallback(val),
        help: "Format: 6 digits-2 uppercase letters-4 digits. Example: 780822-EF-1159.",
        rules: [
          { required: true, pattern: /^\d{6}-[A-Z]{2}-\d{4}$/, message: "Format: 780822-EF-1159." },
        ],
      },
      {
        key: "taxIdentificationNumber",
        label: "Tax Identification Number",
        type: "text",
        getValue: (s: any) => s?.taxIdentificationNumber,
        oldValue: taxIdentificationNumber,
        newValue: (val: any) => valueOrFallback(val),
        help: "Format: LHDN/TIN alphanumeric number as issued. Example: IG1234567890.",
        rules: [
          { required: true, pattern: /^[A-Za-z0-9-]{8,15}$/, message: "Format: LHDN/TIN alphanumeric number as issued. Example: IG1234567890." },
        ],
      },
      {
        key: "epfNumber",
        label: "EPF Number",
        type: "text",
        getValue: (s: any) => s?.epfNumber,
        oldValue: epfNumber,
        newValue: (val: any) => valueOrFallback(val),
        help: "Format: KWSP/EPF number as issued, numeric or alphanumeric.",
        rules: [
          { required: true, pattern: /^[A-Za-z0-9/-]+$/, message: "Format: KWSP/EPF number as issued, numeric or alphanumeric." },
        ],
      },
      {
        key: "socsoNumber",
        label: "SOCSO Number",
        type: "text",
        getValue: (s: any) => s?.socsoNumber,
        oldValue: socsoNumber,
        newValue: (val: any) => valueOrFallback(val),
        help: "Format: PERKESO/SOCSO number as issued, usually 12 digits.",
        rules: [
          { required: true, pattern: /^\d{12}$/, message: "Format: PERKESO/SOCSO number as issued, usually 12 digits." },
        ],
      },
      {
        key: "eisNumber",
        label: "EIS Number",
        type: "text",
        getValue: (s: any) => s?.eisNumber,
        oldValue: eisNumber,
        newValue: (val: any) => valueOrFallback(val),
        help: "Format: SIP/EIS number as issued, numeric or alphanumeric.",
        rules: [
          { pattern: /^[A-Za-z0-9/-]+$/, message: "Format: SIP/EIS number as issued, numeric or alphanumeric." },
        ],
      },
      // Personal Details / Contact Details
      {
        key: "email",
        label: "Official Email",
        type: "text",
        getValue: (s: any) => s?.email,
        oldValue: email,
        newValue: (val: any) => val,
        rules: [
          { required: true, message: "Please enter Email" },
          { type: "email", message: "Please enter a valid Email" },
          ({ getFieldValue }: any) => ({
            validator(_: any, value: string) {
              if (!value) return Promise.resolve();

              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(value)) return Promise.resolve();

              const requiredDomain = getRequiredEmailDomain(getFieldValue("entity"));

              if (value.toLowerCase().endsWith(requiredDomain)) return Promise.resolve();

              return Promise.reject(new Error(`Email must end with ${requiredDomain}`));
            },
          }),
        ],
      },

      {
        key: "personalEmail",
        label: "Personal Email",
        type: "text",
        getValue: (s: any) => s?.personalEmail,
        oldValue: valueOrFallback(staff?.personalEmail),
        newValue: (val: any) => val,
        rules: [
          {
            validator: (_: any, value: string) => {
              if (!value) return Promise.resolve();
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              return emailRegex.test(value.trim())
                ? Promise.resolve()
                : Promise.reject(new Error("Please enter a valid Personal Email"));
            },
          },
        ],
      },
      {
        key: "mobile",
        label: "Mobile Number",
        type: "phone",
        getValue: (s: any) => s?.mobile || s?.phoneNumber,
        oldValue: valueOrFallback(staff?.mobile || staff?.phoneNumber),
        newValue: (val: any) => val,
        rules: [
          { required: true, message: "Please enter Mobile Number" },
          ({ getFieldValue }: any) => ({
            validator(_: any, value: string) {
              if (!value) return Promise.resolve();
              if (isValidPhoneNumberForDialCode(value)) {
                return Promise.resolve();
              }
              return Promise.reject(new Error("Please enter a valid mobile number."));
            },
          }),
        ],
      },
      {
        key: "emergencyContactName",
        label: "Emergency Contact Name",
        type: "text",
        getValue: (s: any) => s?.emergencyContactName,
        oldValue: emergencyContactName,
        newValue: (val: any) => val,
        rules: [{ required: true, message: "Please enter Emergency Contact Name" }],
      },
      {
        key: "emergencyContactNumber",
        label: "Emergency Contact Number",
        type: "phone",
        getValue: (s: any) => s?.emergencyContactNumber || s?.emergencyContact,
        oldValue: emergencyContactNumber,
        newValue: (val: any) => val,
        rules: [
          { required: true, message: "Please enter Emergency Contact Number" },
          ({ getFieldValue }: any) => ({
            validator(_: any, value: string) {
              if (!value) return Promise.resolve();
              if (isValidPhoneNumberForDialCode(value)) return Promise.resolve();
              return Promise.reject(new Error("Please enter a valid emergency contact number."));
            },
          }),
        ],
      },
      {
        key: "emergencyContactRelationship",
        label: "Emergency Contact Relationship",
        type: "select",
        rules: [{ required: true, message: "Please select Emergency Contact Relationship" }],
        options: emergencyContactRelationshipOptions,
        getValue: (s: any) => s?.emergencyContactRelationship,
        oldValue: emergencyContactRelationship,
        newValue: (val: any) => valueOrFallback(val),
      },

      {
        key: "communicationCity",
        label: "City",
        type: "text",
        maxLength: 25,
        getValue: (s: any) => s?.communicationCity,
        oldValue: communicationCity,
        newValue: (val: any) => valueOrFallback(val),
        rules: [{ required: true }, textOnlyRule("City")],
      },
      {
        key: "communicationCountry",
        label: "Country",
        type: "select",
        options: addressCountryOptions,
        getValue: (s: any) => getCountryName(s?.communicationCountry),
        oldValue: communicationCountry,
        newValue: (val: any) => valueOrFallback(val),
        rules: [{ required: true, message: "Please select Country" }],
      },
      {
        key: "communicationState",
        label: "State",
        type: "select",
        options: communicationStateOptions,
        disabled: communicationStateOptions.length === 0,
        getValue: (s: any) => getStateName(s?.communicationState, s?.communicationCountry),
        oldValue: communicationState,
        newValue: (val: any) => valueOrFallback(val),
        rules: [{ required: true, message: "Please select State" }],
      },
      {
        key: "communicationPostalCode",
        label: "Postal Code",
        type: "text",
        maxLength: 7,
        getValue: (s: any) => s?.communicationPostalCode,
        oldValue: communicationPostalCode,
        newValue: (val: any) => valueOrFallback(val),
        rules: [{ required: true, message: "Please enter Postal Code" },
        { pattern: /^[0-9]+$/, message: "Please enter a valid Postal Code" }
        ],
      },
      {
        key: "communicationAddressLine",
        label: "Address Line",
        type: "text",
        maxLength: 150,
        rules: [{ required: true, message: "Please enter Address Line" }],
        getValue: (s: any) => s?.communicationAddressLine || s?.presentAddress,
        oldValue: communicationAddressLine,
        newValue: (val: any) => valueOrFallback(val),
      },
      {
        key: "permanentAddressLine",
        label: "Address Line",
        type: "text",
        maxLength: 150,
        disabled: isSameAsCommunicationAddressChecked,
        getValue: (s: any) => s?.permanentAddressLine || s?.permanentAddress,
        oldValue: permanentAddressLine,
        newValue: (val: any) => valueOrFallback(val),
      },
      {
        key: "permanentCity",
        label: "City",
        type: "text",
        maxLength: 25,
        disabled: isSameAsCommunicationAddressChecked,
        getValue: (s: any) => s?.permanentCity,
        oldValue: permanentCity,
        newValue: (val: any) => valueOrFallback(val),
        rules: [textOnlyRule("City")],
      },
      {
        key: "permanentCountry",
        label: "Country",
        type: "select",
        options: addressCountryOptions,
        disabled: isSameAsCommunicationAddressChecked,
        getValue: (s: any) => getCountryName(s?.permanentCountry),
        oldValue: permanentCountry,
        newValue: (val: any) => valueOrFallback(val),
      },
      {
        key: "permanentState",
        label: "State",
        type: "select",
        options: permanentStateOptions,
        disabled: isSameAsCommunicationAddressChecked || permanentStateOptions.length === 0,
        getValue: (s: any) => getStateName(s?.permanentState, s?.permanentCountry),
        oldValue: permanentState,
        newValue: (val: any) => valueOrFallback(val),
      },
      {
        key: "permanentPostalCode",
        label: "Postal Code",
        type: "text",
        maxLength: 7,
        disabled: isSameAsCommunicationAddressChecked,
        getValue: (s: any) => s?.permanentPostalCode,
        oldValue: permanentPostalCode,
        newValue: (val: any) => valueOrFallback(val),
        rules: [numberOnlyRule("Postal Code")],
      },
      {
        key: "sameAsCommunicationAddress",
        label: "Same as Communication Address",
        type: "checkbox",
        getValue: (s: any) => toBooleanValue(s?.sameAsCommunicationAddress),
        oldValue: sameAsCommunicationAddress ? "Yes" : "No",
        newValue: (val: any) => (toBooleanValue(val) ? "Yes" : "No"),
      },
      {
        key: "totalExperiencePriorToJoining",
        label: "Total Experience Prior to Joining",
        type: "text",
        getValue: (s: any) => s?.totalExperiencePriorToJoining,
        oldValue: totalExperiencePriorToJoining,
        newValue: (val: any) => valueOrFallback(val),
        disabled: true,
      },
      {
        key: "highestQualification",
        label: "Highest Qualification",
        type: "select",
        options: HIGHEST_QUALIFICATION_OPTIONS,
        getValue: (s: any) => s?.highestQualification,
        oldValue: highestQualification,
        newValue: (val: any) => valueOrFallback(val),
      },
      {
        key: "institutionUniversity",
        label: "Institution / University",
        type: "text",
        getValue: (s: any) => s?.institutionUniversity,
        oldValue: institutionUniversity,
        newValue: (val: any) => valueOrFallback(val),
      },
      {
        key: "yearOfPassing",
        label: "Year of Passing",
        type: "text",
        getValue: (s: any) => s?.yearOfPassing,
        oldValue: yearOfPassing,
        newValue: (val: any) => valueOrFallback(val),
        rules: [
          { pattern: /^\d{4}$/, message: "Please enter a valid 4-digit year." },
        ],
      },
      {
        key: "specialization",
        label: "Specialization",
        type: "text",
        getValue: (s: any) => s?.specialization,
        oldValue: specialization,
        newValue: (val: any) => valueOrFallback(val),
      },
      {
        key: "skillDetails",
        label: "Skill Details",
        type: "skillDetails",
        getValue: (s: any) =>
          buildSkillDetails(
            s?.skillDetails,
            s?.primarySkills,
            s?.secondarySkills,
            s?.skillProficiencyLevel
          ),
        oldValue: formatSkillDetailsSummary(skillDetails),
        newValue: (val: any) => formatSkillDetailsSummary(val),
      },
      {
        key: "previousEmploymentHistory",
        label: "Previous Employment History",
        type: "previousEmploymentHistory",
        getValue: (s: any) => parsePreviousEmploymentHistory(s?.previousEmploymentHistory),
        oldValue: formatPreviousEmploymentSummary(previousEmploymentHistory),
        newValue: (val: any) => formatPreviousEmploymentSummary(val),
      },
      {
        key: "dependents",
        label: "Dependents",
        type: "json",
        getValue: (s: any) => parseDependents(s?.dependents),
        oldValue: formatDependentsSummary(dependents),
        newValue: (val: any) => formatDependentsSummary(val),
      },
      {
        key: "positionHistory",
        label: "Position History",
        type: "positionHistory",
        getValue: (s: any) => parsePositionHistory(s?.positionHistory),
        oldValue: formatPositionHistorySummary(positionHistory),
        newValue: (val: any) => formatPositionHistorySummary(val),
      },
      {
        key: "projectTimeline",
        label: "Project Timeline",
        type: "projectTimeline",
        getValue: (s: any) => parseProjectTimeline(s?.projectTimeline),
        oldValue: formatProjectTimelineSummary(projectTimeline),
        newValue: (val: any) => formatProjectTimelineSummary(val),
      },
    ],
    [
      staff,
      staffName,
      departments,
      employeeTypes,
      employmentModes,
      currencies,
      lineManagers,
      lineManagerOptions,
      clients,
      projects,
      genders,
      entityList,
      vendorList,
      subDepartmentList,
      accountList,
      maritalStatuses,
      bloodGroups,
      department,
      employeeType,
      subDepartment,
      workLocation,
      officeLocation,
      officeLocationOptions,
      gradeBand,
      noticePeriod,
      lineManager,
      clientManager,
      nextSalaryRevisionDate,
      client,
      project,
      vendorName,
      vendorEmpId,
      vendorEmploymentType,
      costToRd,
      salaryCurrency,
      ctcDisplay,
      storedCtcDisplay,
      effectiveFrom,
      previousSalary,
      salaryRevisionHistory,
      bonusAmount,
      issuedDate,
      bonusHistory,
      gtlCoveredForEmployee,
      exitReason,
      exitType,
      resignationDate,
      rehireEligible,
      fullAndFinalSettlementStatus,
      showVendorField,
      joiningDate,
      probationEndDate,
      contractStartDate,
      contractEndDate,
      lastWorkingDay,
      nationality,
      pan,
      aadhaar,
      pfMemberId,
      esicIpNumber,
      professionalTaxApplicableState,
      passportNumber,
      myKadNumber,
      taxIdentificationNumber,
      epfNumber,
      socsoNumber,
      eisNumber,
      emergencyContactName,
      emergencyContactNumber,
      emergencyContactRelationship,
      email,
      presentAddress,
      permanentAddress,
      communicationAddressLine,
      communicationCity,
      communicationState,
      communicationCountry,
      addressCountryOptions,
      communicationStateOptions,
      permanentStateOptions,
      getAddressStateOptions,
      communicationPostalCode,
      permanentAddressLine,
      permanentCity,
      permanentState,
      permanentCountry,
      permanentPostalCode,
      sameAsCommunicationAddress,
      totalExperiencePriorToJoining,
      highestQualification,
      institutionUniversity,
      yearOfPassing,
      specialization,
      skillDetails,
      previousEmploymentHistory,
      dependents,
      positionHistory,
      projectTimeline,
      filteredProjectList,
      fetchAccountListByEntity, fetchSubDepartmentsList // Added as dependencies for dynamic options
    ]
  );

  const onEditStaffDetails = () => {
    setIsEditing(true);
    // Set form fields with current staff data, converting values for DatePicker
    const formValues: any = sanitizeInitialStaffFormDates({ ...staff });
    formValues.communicationCountry = getCountryName(formValues.communicationCountry) !== "-"
      ? getCountryName(formValues.communicationCountry)
      : undefined;
    formValues.communicationState = getStateName(formValues.communicationState, formValues.communicationCountry) !== "-"
      ? getStateName(formValues.communicationState, formValues.communicationCountry)
      : undefined;
    formValues.permanentCountry = getCountryName(formValues.permanentCountry) !== "-"
      ? getCountryName(formValues.permanentCountry)
      : undefined;
    formValues.permanentState = getStateName(formValues.permanentState, formValues.permanentCountry) !== "-"
      ? getStateName(formValues.permanentState, formValues.permanentCountry)
      : undefined;
    formValues.workLocation = getValidOptionValue(formValues.workLocation, WORK_LOCATION_OPTIONS);
    formValues.employmentBaseLocation = getEmploymentBaseLocationValue(formValues, employmentModes);
    formValues.officeLocation =
      formValues.workLocation === "WFO"
        ? getValidOptionValue(formValues.officeLocation, officeLocationOptions)
        : undefined;
    formValues.gradeBand = getValidOptionValue(formValues.gradeBand, GRADE_BAND_OPTIONS);
    formValues.primarySkills = parseStringArray(formValues.primarySkills);
    formValues.secondarySkills = parseStringArray(formValues.secondarySkills);
    formValues.salaryRevisionHistory = toSalaryRevisionFormValues(buildSalaryRevisionHistory(formValues));
    formValues.bonusHistory = toBonusFormValues(buildBonusHistory(formValues));
    form.setFieldsValue(formValues);
  };

  const onCancelEdit = () => {
    setEditingSection(null);
    setTempFormData({});
    setHasUnsavedChanges(false);
    form.resetFields(); // Reset form to initial values
  };

  const handleCancelClick = () => {
    if (hasPendingStaffChanges) {
      setShowDiscardChangesModal(true);
    } else {
      onCancelEdit();
      setEditingSection(null);
    }
  };




  const handleTabChange = (key: StaffProfileTabKey) => {
    if (hasUnsavedChanges) {
      setPendingTab(key);
      setShowUnsavedChangesModal(true);
    } else {
      switchProfileTab(key);
    }
  };

  const handleSaveAndContinue = () => {
    const currentValues = getCurrentVisibleFormValues();
    setTempFormData((prev: any) => ({ ...prev, ...currentValues }));
    setHasUnsavedChanges(false); // Changes are now "saved" in temp state
    setShowUnsavedChangesModal(false);
    if (pendingTab) {
      switchProfileTab(pendingTab);
      setPendingTab(null);
    }

  };
  const handleFormValuesChange = useCallback((changedValues: any, allValues: any) => {
    // Dynamic dropdown logic
    if ("entity" in changedValues) {
      fetchAccountListByEntity(changedValues.entity);
      form.setFieldsValue({ accountId: undefined, projectId: undefined }); // Clear dependent fields
      setFilteredProjectList([]); // Clear filtered projects
    }
    if ("accountId" in changedValues) {
      // Filter projects based on selected client
      const selectedClient = changedValues.accountId;
      if (selectedClient) {
        const projectsForClient = projects.filter(p => p.accountId === selectedClient);
        setFilteredProjectList(projectsForClient);
      } else {
        setFilteredProjectList([]);
      }
      form.setFieldsValue({ projectId: undefined }); // Clear project
    }
    if ("departmentId" in changedValues) {
      fetchSubDepartmentsList(changedValues.departmentId);
      form.setFieldsValue({ subDepartmentId: undefined }); // Clear sub-department
    }

    if ("communicationCountry" in changedValues) {
      form.setFieldsValue({
        communicationState: undefined,
        ...(allValues.sameAsCommunicationAddress
          ? { permanentCountry: changedValues.communicationCountry, permanentState: undefined }
          : {}),
      });
    }
    if ("permanentCountry" in changedValues) {
      form.setFieldsValue({ permanentState: undefined });
    }

    const communicationToPermanentAddress = {
      permanentAddressLine: allValues.communicationAddressLine,
      permanentCity: allValues.communicationCity,
      permanentCountry: allValues.communicationCountry,
      permanentState: "communicationCountry" in changedValues ? undefined : allValues.communicationState,
      permanentPostalCode: allValues.communicationPostalCode,
    };
    const communicationAddressFields = [
      "communicationAddressLine",
      "communicationCity",
      "communicationState",
      "communicationCountry",
      "communicationPostalCode",
    ];
    if (changedValues.sameAsCommunicationAddress) {
      form.setFieldsValue(communicationToPermanentAddress);
    } else if (
      allValues.sameAsCommunicationAddress &&
      communicationAddressFields.some((fieldKey) => fieldKey in changedValues)
    ) {
      form.setFieldsValue(communicationToPermanentAddress);
    }

    if ("doj" in changedValues) {
      if (
        allValues.employmentMode &&
        !resolveEmploymentModeFlags(allValues.employmentMode).isPermanent
      ) {
        form.setFieldsValue({ contractStartDate: allValues.doj || null });
      }
    }

    if ("workLocation" in changedValues && changedValues.workLocation !== "WFO") {
      form.setFieldsValue({ officeLocation: undefined });
    }

    if ("entity" in changedValues) {
      form.setFieldsValue({ officeLocation: undefined });
    }

    // If employmentMode changes, clear dependent fields based on the selected mode.
    if ("employmentMode" in changedValues) {
      const selectedEmploymentMode = employmentModes.find(em => em.id === changedValues.employmentMode);
      const selectedModeFlags = resolveEmploymentModeFlags(changedValues.employmentMode);
      const isVendor = selectedModeFlags.isVendor;
      setShowVendorField(isVendor);
      const currentBaseLocation = allValues.employmentBaseLocation;
      form.setFieldsValue({
        employmentBaseLocation: isVendor
          ? "NA"
          : !currentBaseLocation || currentBaseLocation === "NA"
            ? "Karnataka"
            : currentBaseLocation,
      });
      if (selectedEmploymentMode && !selectedModeFlags.isVendor) {
        form.setFieldsValue({
          vendorId: undefined,
          vendorEmpId: undefined,
          vendorEmploymentType: undefined,
          costToRd: undefined,
        });
      }
      const isPermanent = selectedModeFlags.isPermanent;
      setShowContractFields(!isPermanent);
      form.setFieldsValue({
        contractStartDate: isPermanent ? null : allValues.doj || null,
        contractEndDate: null,
      });
    }

    let changesDetected = false;
    for (const config of getVisibleEditableFieldsConfig()) {
      const originalRawValue = config.getValue(staff);
      let currentFormValue = allValues[config.key];

      const normalizedOriginal = normalizeComparableValue(originalRawValue, config.type, config.key);
      const normalizedCurrent = normalizeComparableValue(currentFormValue, config.type, config.key);

      if (normalizedOriginal !== normalizedCurrent) {
        changesDetected = true;
        break;
      }
    }
    setHasUnsavedChanges(changesDetected);

    // Preserve existing onValuesChange logic if any, or integrate it here.
    // For now, assuming no other side effects are needed directly from form value changes
    // other than unsaved changes detection and dynamic dropdown updates.
  }, [staff, editableFieldsConfig]);

  // Override the global onValuesChange for the Form component
  const onFormValuesChange = (changedValues: any, allValues: any) => {
    handleFormValuesChange(changedValues, allValues);
    // Any other global onValuesChange logic can go here if needed
  };

  const getVisibleEditableFieldKeys = () => {
    const workFields = [
      "nextPerformanceReviewDate",
      "nextAppraisalSalaryRevisionDate",
      "entity",
      "employmentMode",
      "employeeType",
      "staffStatusId",
      ...(shouldShowStaffVendorFields ? ["vendorId", "vendorEmpId", "vendorEmploymentType", "costToRd"] : []),
      "doj",
      "probationEndDate",
      ...(shouldShowStaffContractFields ? ["contractStartDate", "contractEndDate"] : []),
      "lwd",
      "noticePeriod",
      "designation",
      "role",
      "departmentId",
      "subDepartmentId",
      "lineManagerId",
      "clientManager",
      "workLocation",
      "employmentBaseLocation",
      "officeLocation",
      "gradeBand",
      // Show Next Review Date only when selected Employment Mode's name is Trainee or Intern
      ...(() => {
        const selectedEmploymentModeId = watchedEmploymentMode || staff?.employmentMode;
        const selectedEmploymentMode = employmentModes.find(
          (em) => String(em?.id) === String(selectedEmploymentModeId) || String(em?.name) === String(selectedEmploymentModeId)
        );
        const employmentModeIsTraineeOrIntern = /trainee|intern/i.test(String(selectedEmploymentMode?.name || ""));

        // Also show Next Review Date when Employment Mode is Vendor Consultant
        // and the selected Vendor Employment Type is Trainee/Intern.
        const vendorEmploymentTypeId = watchedVendorEmploymentType || staff?.vendorEmploymentType;
        const vendorEmploymentType = employmentModes.find(
          (em) => String(em?.id) === String(vendorEmploymentTypeId) || String(em?.name) === String(vendorEmploymentTypeId)
        );
        const vendorEmpIsTraineeOrIntern = /trainee|intern/i.test(String(vendorEmploymentType?.name || ""));

        const isVendorConsultant = /vendor consultant/i.test(String(selectedEmploymentMode?.name || ""));

        return employmentModeIsTraineeOrIntern || (isVendorConsultant && vendorEmpIsTraineeOrIntern)
          ? ["nextReviewDate"]
          : [];
      })(),
    ];
    const basicFields = [
      "employeeId",
      "staffName",
      "dateOfBirth",
      "gender",
      "nationality",
      "maritalStatus",
      "bloodGroup",
      "profilePhoto",
      "localKannadiga",
      "nativeLanguage",
    ];
    const contactFields = [
      "email",
      "personalEmail",
      "mobile",
      "emergencyContactName",
      "emergencyContactNumber",
      "emergencyContactRelationship",
    ];
    const addressFields = [
      "communicationCountry",
      "communicationState",
      "communicationAddressLine",
      "communicationCity",
      "communicationPostalCode",
      "permanentCountry",
      "permanentState",
      "permanentAddressLine",
      "permanentCity",
      "permanentPostalCode",
      "sameAsCommunicationAddress",
    ];
    const indiaStatutoryFields = [
      "entity",
      "pan",
      "aadhaar",
      "uan",
      "pfMemberId",
      "esicIpNumber",
      "professionalTaxApplicableState",
    ];
    const malaysiaStatutoryFields = [
      "entity",
      "passportNumber",
      "passportExpiryDate",
      "visaType",
      "visaExpiryDate",
      "myKadNumber",
      "taxIdentificationNumber",
      "epfNumber",
      "socsoNumber",
      "eisNumber",
    ];
    const statutoryFields = isMalaysiaStatutoryEntity
      ? malaysiaStatutoryFields
      : indiaStatutoryFields;
    const exitInformationFields = [
      "staffStatusId",
      "lwd",
      "resignationDate",
      "exitReason",
      "exitType",
      "rehireEligible",
      "fullAndFinalSettlementStatus",
    ];
    const experienceQualificationFields = [
      "totalExperiencePriorToJoining",
      "previousEmploymentHistory",
      "highestQualification",
      "institutionUniversity",
      "yearOfPassing",
      "specialization",
      "skillDetails",
    ];
    const compensationFields = [
      "ctc",
      "salaryCurrency",
      "salaryRevisionHistory",
      "bonusHistory",
      ...(isMalaysiaBankEntity
        ? [
          "malaysiaBankName",
          "malaysiaBankAccountNumber",
          "malaysiaAccountHolderName",
          "malaysiaSwiftBicCode",
        ]
        : [
          "indiaBankName",
          "indiaBankAccountNumber",
          "indiaBranchName",
          "indiaAccountHolderName",
          "indiaIfscCode",
        ]),
    ];

    if (editingSection === "work") return workFields;
    if (editingSection === "compensationBanking") return compensationFields;
    if (editingSection === "exitInformation") return exitInformationFields;
    if (editingSection === "basic") return basicFields;
    if (editingSection === "contact") return contactFields;
    if (editingSection === "address") return addressFields;
    if (editingSection === "statutoryCompliance") return statutoryFields;
    if (editingSection === "insurance") return ["gtlCoveredForEmployee"];
    if (editingSection === "priorExperience") {
      return ["totalExperiencePriorToJoining", "previousEmploymentHistory"];
    }
    if (editingSection === "educationalQualifications") {
      return ["highestQualification", "institutionUniversity", "yearOfPassing", "specialization"];
    }
    if (editingSection === "skills") {
      return ["skillDetails"];
    }
    if (editingSection === "dependents") return ["dependents"];
    if (editingSection === "positionHistory") return ["positionHistory"];
    if (editingSection === "projectTimeline") return ["projectTimeline"];

    if (editingSection === "all") {
      if (activeTab === "overview") return [];
      if (activeTab === "job") return [...workFields, "positionHistory"];
      if (activeTab === "compensation") return compensationFields;
      if (activeTab === "experienceQualifications") return experienceQualificationFields;
      if (activeTab === "exit") return exitInformationFields;
      if (activeTab === "timeline") return ["projectTimeline"];
      if (activeTab === "personal") {
        return [
          ...basicFields,
          ...contactFields,
          ...addressFields,
          ...statutoryFields,
          "gtlCoveredForEmployee",
          "dependents",
        ];
      }
    }

    return editableFieldsConfig.map((config) => config.key);
  };

  const getVisibleEditableFieldKeySet = () =>
    new Set(getVisibleEditableFieldKeys());

  const getVisibleEditableFieldsConfig = () => {
    const visibleKeys = getVisibleEditableFieldKeySet();
    const usedKeys = new Set<string>();

    return editableFieldsConfig.filter((config) => {
      if (!visibleKeys.has(config.key) || usedKeys.has(config.key)) {
        return false;
      }

      usedKeys.add(config.key);
      return true;
    });
  };

  const getSaveEditableFieldsConfig = () => {
    const saveKeys = new Set([
      ...getVisibleEditableFieldKeys(),
      ...Object.keys(tempFormData || {}),
    ]);
    const usedKeys = new Set<string>();

    return editableFieldsConfig.filter((config) => {
      if (!saveKeys.has(config.key) || usedKeys.has(config.key)) {
        return false;
      }

      usedKeys.add(config.key);
      return true;
    });
  };

  const getCurrentVisibleFormValues = () =>
    form.getFieldsValue(getVisibleEditableFieldKeys());

  const validateVisibleEditableFields = async (fieldKeys: string[]) => {
    try {
      await form.validateFields(fieldKeys);
      return true;
    } catch (errorInfo: any) {
      const visibleErrors = form
        .getFieldsError(fieldKeys)
        .filter((field) => field.errors.length > 0);
      const firstError = errorInfo?.errorFields?.[0] || visibleErrors[0];

      if (!firstError && errorInfo?.outOfDate) {
        return true;
      }

      if (firstError?.name) {
        form.scrollToField(firstError.name, { block: "center" });
      }

      showNotification(
        firstError?.errors?.[0] ||
        (firstError?.name ? `Please check ${firstError.name.join(" > ")}.` : "Please review the current tab and save again."),
        "warning"
      );
      return false;
    }
  };

  const onSaveEdit = async () => {
    const visibleEditableFieldKeys = getVisibleEditableFieldKeys();
    if (visibleEditableFieldKeys.length === 0) {
      showNotification("No editable fields on this tab.", "info");
      return;
    }

    const isValid = await validateVisibleEditableFields(visibleEditableFieldKeys);
    if (!isValid) return;

    try {
      // Combine original staff data with all temporary changes from all tabs
      const allTempData = tempFormData;
      const finalFormState = { ...staff, ...allTempData, ...getCurrentVisibleFormValues() };
      if (finalFormState.workLocation !== "WFO") {
        finalFormState.officeLocation = null;
      }
      finalFormState.totalExperiencePriorToJoining = formatExperienceFromMonths(
        getPreviousEmploymentTotalMonths(finalFormState.previousEmploymentHistory)
      );
      finalFormState.skillDetails = normalizeSkillDetails(finalFormState.skillDetails);
      finalFormState.primarySkills = finalFormState.skillDetails.primarySkills
        .map((row: StaffProfileSkillDetail) => row.skill)
        .filter(Boolean);
      finalFormState.secondarySkills = finalFormState.skillDetails.secondarySkills
        .map((row: StaffProfileSkillDetail) => row.skill)
        .filter(Boolean);
      syncCompensationLegacyFields(finalFormState);

      const changedFields: any[] = [];

      for (const config of getSaveEditableFieldsConfig()) {
        const originalValue = config.getValue(staff);
        let currentValue = finalFormState[config.key];

        if (config.type === "date") {
          currentValue = normalizeDatePayloadValue(currentValue);
        }

        const normalizedOriginal = normalizeComparableValue(originalValue, config.type, config.key);
        const normalizedCurrent = normalizeComparableValue(currentValue, config.type, config.key);

        if (normalizedOriginal !== normalizedCurrent) {
          const oldDisplayValue = config.oldValue;
          const newDisplayValue = config.newValue ? config.newValue(currentValue) : valueOrFallback(currentValue);

          changedFields.push({
            fieldName: config.label,
            oldValue: oldDisplayValue,
            newValue: newDisplayValue,
          });
        }
      }

      if (changedFields.length > 0) {
        setChangesToConfirm(changedFields);
        setShowConfirmationModal(true);
      } else {
        showNotification("No changes detected.", "info");
        // Keep user in edit mode as requested
      }
    } catch (error: any) {
      showNotification(error?.message || "Could not prepare staff changes. Please try again.", "error");
    }
  };

  const handleUpdateStaff = async () => {
    setIsUpdating(true);
    const allTempData = tempFormData;
    const finalFormState = { ...staff, ...allTempData, ...getCurrentVisibleFormValues() };
    if (finalFormState.workLocation !== "WFO") {
      finalFormState.officeLocation = null;
    }
    finalFormState.totalExperiencePriorToJoining = formatExperienceFromMonths(
      getPreviousEmploymentTotalMonths(finalFormState.previousEmploymentHistory)
    );
    finalFormState.skillDetails = normalizeSkillDetails(finalFormState.skillDetails);
    finalFormState.primarySkills = finalFormState.skillDetails.primarySkills
      .map((row: StaffProfileSkillDetail) => row.skill)
      .filter(Boolean);
    finalFormState.secondarySkills = finalFormState.skillDetails.secondarySkills
      .map((row: StaffProfileSkillDetail) => row.skill)
      .filter(Boolean);
    finalFormState.skillProficiencyLevel =
      finalFormState.skillDetails.primarySkills.find(
        (row: StaffProfileSkillDetail) => row.proficiencyLevel
      )?.proficiencyLevel ||
      finalFormState.skillDetails.secondarySkills.find(
        (row: StaffProfileSkillDetail) => row.proficiencyLevel
      )?.proficiencyLevel ||
      null;
    syncCompensationLegacyFields(finalFormState);

    try {
      const payload: any = {};
      const formData = new FormData();
      let isProfilePhotoChanged = false;

      const profilePhotoConfig = editableFieldsConfig.find(c => c.key === "profilePhoto");
      const originalProfilePhoto = profilePhotoConfig?.getValue(staff);
      for (const config of getSaveEditableFieldsConfig()) {
        const originalValue = config.getValue(staff);
        let currentValue = finalFormState[config.key];

        if (config.type === "date") {
          currentValue = normalizeDatePayloadValue(currentValue);
        }

        const normalizedOriginal = normalizeComparableValue(originalValue, config.type, config.key);
        const normalizedCurrent = normalizeComparableValue(currentValue, config.type, config.key);

        if (normalizedOriginal !== normalizedCurrent) {
          if (config.key === "aadhaar") {
            currentValue = unformatAadhaar(currentValue);
          }

          payload[config.key] =
            config.type === "json"
              ? normalizeDependents(currentValue)
              : config.type === "positionHistory"
                ? normalizePositionHistory(currentValue)
                : config.type === "projectTimeline"
                  ? normalizeProjectTimeline(currentValue)
                  : config.type === "previousEmploymentHistory"
                    ? normalizePreviousEmploymentHistory(currentValue)
                    : config.type === "skillDetails"
                      ? normalizeSkillDetails(currentValue)
                      : config.type === "salaryRevisionHistory"
                        ? normalizeSalaryRevisionHistory(currentValue)
                        : config.type === "bonusHistory"
                          ? normalizeBonusHistory(currentValue)
                          : config.key === "ctc" && currentValue !== null && currentValue !== undefined
                            ? String(currentValue)
                            : currentValue;

          if (config.type === "skillDetails") {
            payload.primarySkills = finalFormState.primarySkills;
            payload.secondarySkills = finalFormState.secondarySkills;
            payload.skillProficiencyLevel = finalFormState.skillProficiencyLevel;
            payload.createdDate = new Date().toISOString();
          }

          if (config.type === "salaryRevisionHistory") {
            payload.previousSalaryRevisionDate = finalFormState.previousSalaryRevisionDate;
            payload.previousSalaryRevisionAmount = finalFormState.previousSalaryRevisionAmount;
            payload.effectiveFrom = finalFormState.effectiveFrom;
            payload.previousSalary = finalFormState.previousSalary;
            payload.createdDate = new Date().toISOString();
          }

          if (config.type === "bonusHistory") {
            payload.bonusAmount = finalFormState.bonusAmount;
            payload.issuedDate = finalFormState.issuedDate;
            payload.createdDate = new Date().toISOString();

          }
        }
      }

      if (Object.keys(payload).length > 0) {
        const response: any = await dispatch(updateStaff({ id: staffId!, body: payload })).unwrap();
        const result = response?.data || response;
        if (result?.statusCode < 400) {
          const locationToNavigate = pendingLocation;
          await fetchStaff(); // Re-fetch staff data to get the latest updates
          setTempFormData({}); // Clear temporary data
          setHasUnsavedChanges(false);
          fetchActivityLogs(); // Re-fetch activity logs
          showNotification("Staff details updated successfully.", "success");
          window.dispatchEvent(new Event("rp:staff-updated"));
          setShowConfirmationModal(false);
          setEditingSection(null);
          setPendingLocation(null);

          if (locationToNavigate) {
            navigate(
              locationToNavigate.pathname +
              (locationToNavigate.search || "")
            );
          } else if (missingFieldsReturnTo) {
            navigate(missingFieldsReturnTo);
          }
        } else {
          showNotification(response?.message || "Failed to update staff details.", "error");
        }
      } else {
        showNotification("No changes to update.", "info");
      }
    } catch (error: any) {
      showNotification(error?.message || "Failed to update staff details.", "error");
    } finally {
      setIsUpdating(false);
    }
  };


  const renderCompensation = () => {
    const compensationFieldKeys = [
      "ctc",
      "salaryCurrency",
    ];
    const indiaBankFieldKeys = [
      "indiaBankName",
      "indiaBankAccountNumber",
      "indiaBranchName",
      "indiaAccountHolderName",
      "indiaIfscCode",
    ];
    const malaysiaBankFieldKeys = [
      "malaysiaBankName",
      "malaysiaBankAccountNumber",
      "malaysiaAccountHolderName",
      "malaysiaSwiftBicCode",
    ];
    const activeBankFieldKeys = isMalaysiaBankEntity ? malaysiaBankFieldKeys : indiaBankFieldKeys;
    const editableFieldByKey = new Map(
      editableFieldsConfig.map((config) => [config.key, config])
    );
    const getFields = (fieldKeys: string[]) =>
      fieldKeys
        .map((fieldKey) => editableFieldByKey.get(fieldKey))
        .filter(
          (
            config
          ): config is NonNullable<ReturnType<typeof editableFieldByKey.get>> =>
            Boolean(config)
        );
    const compensationFields = getFields(compensationFieldKeys);
    const bankFields = getFields(activeBankFieldKeys);
    const isCompensationEditing =
      editingSection === "compensationBanking" || editingSection === "all";
    const toFieldGrid = (fields: ReturnType<typeof getFields>) =>
      fields.map((field) => ({
        label: field.label,
        value: field.oldValue,
      }));

    return (
      <Form form={form} layout="vertical" initialValues={staff} onValuesChange={onFormValuesChange}>
        <Section
          title="Compensation"
          canEdit={false}
          onEdit={() => handleSectionEdit("compensationBanking")}
          isEditing={editingSection === "compensationBanking"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {isCompensationEditing ? (
            <Row gutter={16}>
              {compensationFields.map((config) => (
                <Col span={12} key={config.key}>
                  {config.key === "ctc" ? (
                    <div className="staff-profile-field">
                      <span>{config.label}</span>
                      <strong>{ctcDisplay}</strong>
                    </div>
                  ) : (
                    renderEditableField(config)
                  )}
                </Col>
              ))}
            </Row>
          ) : (
            <FieldGrid fields={toFieldGrid(compensationFields)} />
          )}
        </Section>
        <Section
          title="Salary Revision History"
          canEdit={false}
          onEdit={() => handleSectionEdit("compensationBanking")}
          isEditing={editingSection === "compensationBanking"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {isCompensationEditing ? (
            <Form.List name="salaryRevisionHistory">
              {(fields, { add, remove }) => (
                <div className="staff-profile-dependents-form">
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={12} key={key} align="middle">
                      <Form.Item {...restField} name={[name, "id"]} hidden>
                        <Input />
                      </Form.Item>
                      <Col xs={24} md={6}>
                        <Form.Item
                          {...restField}
                          name={[name, "previousSalary"]}
                          label="Salary"
                          normalize={(value) => sanitizeStaffInput(value, "amount")}
                          rules={[{ required: true, message: "Please enter Salary" }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={7}>
                        <Form.Item
                          {...restField}
                          name={[name, "effectiveFrom"]}
                          label="Effective From"
                          rules={[
                            { required: true, message: "Please select Effective From date" },
                            ({ getFieldValue }: any) => ({
                              validator(_: any, value: any) {
                                const doj = getFieldValue("doj") || staff?.doj;

                                if (
                                  value &&
                                  doj &&
                                  dayjs(value).isBefore(dayjs(doj), "day")
                                ) {
                                  return Promise.reject(
                                    new Error("Effective From cannot be earlier than Date of Joining")
                                  );
                                }

                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <DatePicker
                            style={{ width: "100%" }}
                            format="DD-MMM-YYYY"
                            disabledDate={(current) =>
                              !!(
                                current &&
                                (form.getFieldValue("doj") || staff?.doj) &&
                                current < dayjs(form.getFieldValue("doj") || staff?.doj).startOf("day")
                              )
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={20} md={9}>
                        <Form.Item label="Upload Document">
                          {renderLinkedDocumentUpload(
                            SALARY_REVISION_DOCUMENT_TYPE,
                            form.getFieldValue(["salaryRevisionHistory", name, "id"]) ||
                            createLegacyRecordId("salary-revision", [], name),
                            "Upload Salary Revision Document"
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={4} md={2}>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ id: createLocalRecordId("salary-revision") })}
                  >
                    Add Salary Revision
                  </Button>
                </div>
              )}
            </Form.List>
          ) : (
            <Table
              pagination={false}
              size="small"
              rowKey={(_, index) => `salary-revision-${index}`}
              dataSource={normalizeSalaryRevisionHistory(salaryRevisionHistory)}
              columns={[
                {
                  title: "Salary",
                  dataIndex: "previousSalary",
                  render: (value: any) => formatDecimalAmount(value),
                },
                {
                  title: "Effective From",
                  dataIndex: "effectiveFrom",
                  render: (value: any) => dateOrFallback(value, "-"),
                },
                {
                  title: "Document",
                  render: (_: any, record: any) =>
                    renderLinkedDocumentUpload(
                      SALARY_REVISION_DOCUMENT_TYPE,
                      record.id,
                      "Upload Salary Revision Document",
                      false
                    ),
                },
              ]}
              locale={{ emptyText: <Empty description="No salary revisions added" /> }}
            />
          )}
        </Section>
        <Section
          title="Bonus History"
          canEdit={false}
          onEdit={() => handleSectionEdit("compensationBanking")}
          isEditing={editingSection === "compensationBanking"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {isCompensationEditing ? (
            <Form.List name="bonusHistory">
              {(fields, { add, remove }) => (
                <div className="staff-profile-dependents-form">
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={12} key={key} align="middle">
                      <Form.Item {...restField} name={[name, "id"]} hidden>
                        <Input />
                      </Form.Item>
                      <Col xs={24} md={6}>
                        <Form.Item {...restField} name={[name, "bonusAmount"]} label="Bonus Amount"
                          normalize={(value) => sanitizeStaffInput(value, "amount")}
                          rules={[
                            {
                              required: true,
                              message: "Please enter Bonus Amount",
                            },
                          ]}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={7}>
                        <Form.Item {...restField} name={[name, "issuedDate"]} label="Issued Date"
                          rules={[
                            {
                              required: true,
                              message: "Please select Issued Date",
                            },
                          ]}>
                          <DatePicker style={{ width: "100%" }} format="DD-MMM-YYYY" />
                        </Form.Item>
                      </Col>
                      <Col xs={20} md={9}>
                        <Form.Item label="Upload Document">
                          {renderLinkedDocumentUpload(
                            BONUS_DOCUMENT_TYPE,
                            form.getFieldValue(["bonusHistory", name, "id"]) ||
                            createLegacyRecordId("bonus", [], name),
                            "Upload Bonus Document"
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={4} md={2}>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ id: createLocalRecordId("bonus") })}
                  >
                    Add Bonus
                  </Button>
                </div>
              )}
            </Form.List>
          ) : (
            <Table
              pagination={false}
              size="small"
              rowKey={(_, index) => `bonus-${index}`}
              dataSource={normalizeBonusHistory(bonusHistory)}
              columns={[
                {
                  title: "Bonus Amount",
                  dataIndex: "bonusAmount",
                  render: (value: any) => formatDecimalAmount(value),
                },
                {
                  title: "Issued Date",
                  dataIndex: "issuedDate",
                  render: (value: any) => dateOrFallback(value, "-"),
                },
                {
                  title: "Document",
                  render: (_: any, record: any) =>
                    renderLinkedDocumentUpload(
                      BONUS_DOCUMENT_TYPE,
                      record.id,
                      "Upload Bonus Document",
                      false
                    ),
                },
              ]}
              locale={{ emptyText: <Empty description="No bonuses added" /> }}
            />
          )}
        </Section>
        <Section
          title={isMalaysiaBankEntity ? "Bank Details - Malaysia" : "Bank Details - India"}
          canEdit={false}
          onEdit={() => handleSectionEdit("compensationBanking")}
          isEditing={editingSection === "compensationBanking"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {isCompensationEditing ? (
            <Row gutter={16}>
              {bankFields.map((config) => (
                <Col span={12} key={config.key}>
                  {renderEditableField(config)}
                </Col>
              ))}
            </Row>
          ) : (
            <FieldGrid fields={toFieldGrid(bankFields)} />
          )}
        </Section>
      </Form>
    );
  };

  const activeDocument = useMemo(() => {
    return documentTypes.find((dt) => dt.id === activeDocumentCategoryId) || { id: "", documentTypeName: "Select a Document Type" };
  }, [activeDocumentCategoryId, documentTypes]);

  //  const renderOverview = () => (
  //   <>
  //     <Row gutter={[16, 16]} className="staff-profile-metrics">
  //       {metrics.map((metric) => (
  //         <Col xs={24} sm={12} lg={6} key={metric.label}>
  //           <div className="staff-profile-metric-card">
  //             <span>{metric.label}</span>
  //             <strong>{metric.value}</strong>
  //             <small>{metric.note}</small>
  //           </div>
  //         </Col>
  //       ))}
  //     </Row>
  //     <Row gutter={[16, 16]}>
  //       <Col xs={24} lg={15}>
  //         <Section title="Employment Snapshot">
  //           <FieldGrid fields={employmentSnapshot} />
  //         </Section>
  //       </Col>
  //       <Col xs={24} lg={9}>
  //       {accessAllowed(myPermissions, allPermissions.STAFF_ACTIVITY_LOGS) && (
  //         <Section title="Recent Activity">
  //           <div className="staff-profile-activity-list">
  //             {recentActivities.map((activity) => (
  //               <div className="staff-profile-activity" key={`${activity.title}-${activity.date}`}>
  //                 <span />
  //                 <div>
  //                   <strong>{activity.title}</strong>
  //                   <small>{activity.date}</small>
  //                 </div>
  //               </div>
  //             ))}
  //           </div>
  //         </Section>
  //       )}
  //       </Col>
  //     </Row>
  //   </>
  // );

  const renderOverview = () => {
    const normalizeCareerEventType = (value?: string) =>
      (value || "").toString().toUpperCase().replace(/[-\s]+/g, "_");
    const careerEventTypeMap: Record<string, string> = {
      JOINED: "JOINED",
      BONUS: "BONUS_UPDATE",
      BONUS_UPDATE: "BONUS_UPDATE",
      "SALARY REVISION": "SALARY_REVISION",
      SALARY_REVISION: "SALARY_REVISION",
      "SALARY-REVISION": "SALARY_REVISION",
      PROMOTION: "PROMOTION",
      "ROLE CHANGE": "ROLE_UPDATE",
      ROLE_CHANGE: "ROLE_UPDATE",
      ROLE_UPDATE: "ROLE_UPDATE",
      "POSITION CHANGE": "POSITION_UPDATE",
      POSITION_CHANGE: "POSITION_UPDATE",
      POSITION_UPDATE: "POSITION_UPDATE",
      "CLIENT CHANGE": "CLIENT_UPDATE",
      CLIENT_CHANGE: "CLIENT_UPDATE",
      CLIENT_UPDATE: "CLIENT_UPDATE",
      "PROJECT ASSIGNMENT": "CLIENT_UPDATE",
      PROJECT_ASSIGNMENT: "CLIENT_UPDATE",
      PROJECT_CHANGE: "CLIENT_UPDATE",
      PROJECT_UPDATE: "CLIENT_UPDATE",
    };
    const careerEventLabelMap: Record<string, string> = {
      JOINED: "NEW JOIN",
      SALARY_REVISION: "SALARY REVISION",
      PROMOTION: "PROMOTION",
      ROLE_UPDATE: "DESIGNATION CHANGE",
      POSITION_UPDATE: "DESIGNATION CHANGE",
      CLIENT_UPDATE: "CLIENT CHANGE",
      BONUS_UPDATE: "BONUS",
    };
    const getCareerEventType = (eventType?: string) =>
      careerEventTypeMap[normalizeCareerEventType(eventType)];
    const getCareerEventLabel = (eventType?: string) => {
      const canonicalType = getCareerEventType(eventType);
      return canonicalType ? careerEventLabelMap[canonicalType] || eventType || "" : "";
    };
    const getCareerEventBadgeClass = (eventType?: string) => {
      const canonicalType = getCareerEventType(eventType);
      return canonicalType
        ? `career-event-badge career-event-badge-${canonicalType.toLowerCase().replace(/_/g, "-")}`
        : "career-event-badge";
    };
    const recognizedCareerHistory = overview.careerHistory.filter((event) =>
      Boolean(getCareerEventType(event.eventType))
    );

    // Build available years dynamically from the API-backed career history
    const availableYears = Array.from(
      new Set(
        recognizedCareerHistory
          .map((e) => e.year)
          .filter((y) => !!y)
      )
    ).sort((a, b) => Number(b) - Number(a));

    // Filter history based on selected year
    const visibleHistory = recognizedCareerHistory.filter(
      (event) => overviewYear === "all" || event.year === overviewYear
    );

    // Years to render in the timeline (descending)
    const years = availableYears.filter((year) =>
      visibleHistory.some((event) => event.year === year)
    );

    const summaryCards = [
      {
        label: "Current Role",
        value: overview.currentRole,
        icon: <UserOutlined />,
      },
      {
        label: "Current Position",
        value: overview.currentPosition,
        icon: <TeamOutlined />,
      },
      {
        label: "Current CTC",
        value: overview.currentCtc,
        note: "Per Annum",
        icon: <DollarOutlined />,
      },
      {
        label: "Current Client",
        value: overview.currentClient,
        icon: <BankOutlined />,
      },
    ];

    const tableColumns = [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
      },
      {
        title: "Event Type",
        dataIndex: "eventType",
        key: "eventType",
        render: (value: string) => (
          <Tag className="career-event-badge">
            {value}
          </Tag>
        ),
      },
      {
        title: "Role",
        dataIndex: "role",
        key: "role",
      },
      {
        title: "Position",
        dataIndex: "position",
        key: "position",
      },
      {
        title: "CTC",
        dataIndex: "ctc",
        key: "ctc",
      },
      {
        title: "Client",
        dataIndex: "client",
        key: "client",
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
      },
    ];

    return (
      <div className="employee-overview">

        {/* Summary Section hidden for now. */}
        {false && (
          <section className="employee-overview-header">

            {/* Profile Section */}
            {/* 
        <div className="employee-overview-profile">
          <div className="employee-overview-avatar">
            {staff?.profilePhoto ? (
              <img
                src={staff.profilePhoto}
                alt={`${overview.employeeName} profile`}
              />
            ) : (
              initialsFor(overview.employeeName)
            )}
          </div>

          <div>
            <div className="employee-overview-name">
              <h2>{overview.employeeName}</h2>
              <Tag color="success">Active</Tag>
            </div>

            <strong className="employee-overview-id">
              {overview.employeeId}
            </strong>

            <p>Joined on {overview.joinedDate}</p>
            <p>{overview.experience}</p>
          </div>
        </div>
        */}

            {/* Summary Cards */}
            <div className="employee-overview-summary-cards">
              {summaryCards.map((card) => (
                <div
                  className="employee-overview-summary-card"
                  key={card.label}
                >
                  <span className="employee-overview-summary-icon">
                    {card.icon}
                  </span>

                  <span className="employee-overview-summary-label">
                    {card.label}
                  </span>

                  <strong>{card.value}</strong>

                  {card.note && (
                    <small>{card.note}</small>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Career Timeline Section */}
        <section className="career-timeline-section">

          {/* Toolbar */}
          <div className="career-timeline-toolbar">

            <h3>Career Timeline</h3>

            <div className="career-timeline-controls">

              {/* Timeline / Table Toggle */}
              <div
                className="career-view-toggle"
                aria-label="Career history view"
              >
                {/* <Button
                type={
                  overviewView === "timeline"
                    ? "primary"
                    : "default"
                }
                onClick={() =>
                  setOverviewView("timeline")
                }
              >
                Timeline View
              </Button> */}

                {/* <Button
                type={
                  overviewView === "table"
                    ? "primary"
                    : "default"
                }
                onClick={() =>
                  setOverviewView("table")
                }
              >
                Table View
              </Button> */}
              </div>

              {/* Year Filter */}
              <label className="career-year-filter">
                Year Filter

                <Select
                  value={overviewYear}
                  onChange={(value) =>
                    setOverviewYear(
                      value as
                      | "all"
                      | CareerHistoryEvent["year"]
                    )
                  }
                  options={[
                    { label: "All Years", value: "all" },
                    ...availableYears.map((year) => ({ label: year, value: year })),
                  ]}
                />
              </label>

              {/* Export hidden for now. */}
              {false && (
                <Button icon={<ExportOutlined />}>
                  Export
                </Button>
              )}
            </div>
          </div>

          {/* Table View */}
          {overviewView === "table" ? (
            <Table<CareerHistoryEvent>
              columns={tableColumns}
              dataSource={visibleHistory}
              rowKey="id"
              pagination={false}
              scroll={{ x: 900 }}
            />
          ) : (

            /* Timeline View */
            <>
              <div className="career-year-list">

                {years.map((year) => {

                  // Events for selected year
                  // Latest date first
                  const events = visibleHistory
                    .filter(
                      (event) => event.year === year
                    )
                    .sort((a: CareerHistoryEvent, b: CareerHistoryEvent) => {
                      const dateA = new Date(a.date).getTime();
                      const dateB = new Date(b.date).getTime();

                      return dateB - dateA;
                    });

                  const isExpanded =
                    expandedOverviewYears.includes(String(year));

                  return (
                    <div
                      className="career-year"
                      key={year}
                    >

                      {/* Year Heading */}
                      <button
                        type="button"
                        className="career-year-heading"
                        onClick={() =>
                          setExpandedOverviewYears((current) =>
                            isExpanded
                              ? current.filter((item) => item !== String(year))
                              : [...current, String(year)]
                          )
                        }
                      >
                        <span className="career-year-heading-main">
                          <h4>{year}</h4>
                          <span className="career-year-count">{events.length}</span>
                          <span className="career-year-count-text">
                            {events.length === 1 ? "Change" : "Changes"}
                          </span>
                        </span>
                        <span className="career-year-toggle-icon">
                          {isExpanded ? <UpOutlined /> : <DownOutlined />}
                        </span>
                      </button>

                      {/* Events */}
                      {isExpanded ? (
                        <div className="career-events">

                          {events.map((event: CareerHistoryEvent) => {
                            const canonicalType = getCareerEventType(event.eventType);
                            if (!canonicalType) return null; // skip unrelated events
                            const eventLabel = getCareerEventLabel(event.eventType);

                            const renderTimelineEvent = (evt: CareerHistoryEvent) => {
                              const detailsSource: Record<string, any> = evt.details || {};
                              const salaryValue = firstRealValue(detailsSource.salary, detailsSource.ctc, evt.ctc);
                              const bonusValue = firstRealValue(detailsSource.bonus, detailsSource.bonusAmount);
                              const snapshotCards = [
                                {
                                  label: "Designation",
                                  value: valueOrFallback(firstRealValue(detailsSource.designation, detailsSource.position), "-"),
                                  icon: <TeamOutlined />,
                                  tone: "designation",
                                },
                                {
                                  label: "Client",
                                  value: valueOrFallback(firstRealValue(detailsSource.client, evt.client), "-"),
                                  icon: <BankOutlined />,
                                  tone: "client",
                                },
                                {
                                  label: "Salary (CTC)",
                                  value: salaryValue ? formatDecimalAmount(salaryValue) : "-",
                                  note: "Per Annum",
                                  icon: <DollarOutlined />,
                                  tone: "salary",
                                },
                                {
                                  label: "Bonus",
                                  value: bonusValue ? formatDecimalAmount(bonusValue) : "-",
                                  note: bonusValue ? "For this month" : "",
                                  icon: <GiftOutlined />,
                                  tone: "bonus",
                                },
                              ];

                              if (snapshotCards.length === 0) return null;

                              return (
                                <div className="career-event-snapshot">
                                  {snapshotCards.map((item) => (
                                    <div
                                      className={`career-event-snapshot-card career-event-snapshot-card-${item.tone}`}
                                      key={item.label}
                                    >
                                      <span>{item.icon}</span>
                                      <small>{item.label}</small>
                                      <strong>{item.value}</strong>
                                      {item.note ? <em>{item.note}</em> : null}
                                    </div>
                                  ))}
                                </div>
                              );
                            };

                            const rendered = renderTimelineEvent(event);
                            if (!rendered) return null;

                            return (
                              <div className="career-event" key={event.id}>
                                <span className="career-event-marker" />
                                <article className="career-event-card">
                                  <div className="career-event-card-header">
                                    <div className="career-event-date">
                                      <strong>{formatDate(event.date, "MMMM YYYY")}</strong>
                                      <small>{formatDate(event.date, "DD MMM YYYY")}</small>
                                    </div>
                                    <div className="career-event-latest-change">
                                      <span>Latest change:</span>
                                      <Tag className={getCareerEventBadgeClass(event.eventType)}>{eventLabel}</Tag>
                                      {event.changedByName ? (
                                        <small>by {event.changedByName}</small>
                                      ) : null}
                                    </div>
                                  </div>
                                  {rendered}
                                </article>
                              </div>
                            );
                          })}

                        </div>
                      ) : null}

                      {/* View More / View Less */}
                      {false && events.length > 3 && (
                        <Button
                          type="link"
                          className="career-view-more"
                          onClick={() =>
                            setExpandedOverviewYears(
                              (current) =>
                                isExpanded
                                  ? current.filter(
                                    (item) =>
                                      item !== year
                                  )
                                  : [
                                    ...current,
                                    year,
                                  ]
                            )
                          }
                        >
                          {isExpanded
                            ? "View Less ↑"
                            : "View More ↓"}
                        </Button>
                      )}

                    </div>
                  );
                })}

              </div>
              <div className="career-event-legend">
                <span><i className="career-event-legend-dot career-event-legend-position-update" />Designation Change</span>
                <span><i className="career-event-legend-dot career-event-legend-client-update" />Client Change</span>
                <span><i className="career-event-legend-dot career-event-legend-salary-revision" />Salary Revision</span>
                <span><i className="career-event-legend-dot career-event-legend-bonus-update" />Bonus</span>
                <span><i className="career-event-legend-dot career-event-legend-joined" />New Join</span>
              </div>
            </>
          )}
        </section>
      </div>
    );
  };

  const handleSectionEdit = (section: string) => {
    setEditingSection(section);
    const formValues: any = { ...staff };
    const staffSameAsCommunicationAddress = toBooleanValue(staff?.sameAsCommunicationAddress);
    formValues.presentAddress = staff?.presentAddress || "";
    formValues.permanentAddress = staff?.permanentAddress || "";
    formValues.communicationAddressLine =
      staff?.communicationAddressLine || staff?.presentAddress || "";
    formValues.communicationCountry = getCountryName(staff?.communicationCountry) !== "-"
      ? getCountryName(staff?.communicationCountry)
      : undefined;
    formValues.communicationState = getStateName(staff?.communicationState, staff?.communicationCountry) !== "-"
      ? getStateName(staff?.communicationState, staff?.communicationCountry)
      : undefined;
    formValues.permanentAddressLine =
      staff?.permanentAddressLine ||
      (staffSameAsCommunicationAddress ? staff?.communicationAddressLine || staff?.presentAddress : "") ||
      staff?.permanentAddress ||
      "";
    formValues.permanentCity =
      staff?.permanentCity ||
      (staffSameAsCommunicationAddress ? staff?.communicationCity : "") ||
      "";
    formValues.permanentState =
      getStateName(
        firstRealValue(staff?.permanentState, staffSameAsCommunicationAddress ? staff?.communicationState : undefined),
        firstRealValue(staff?.permanentCountry, staffSameAsCommunicationAddress ? staff?.communicationCountry : undefined)
      ) !== "-"
        ? getStateName(
          firstRealValue(staff?.permanentState, staffSameAsCommunicationAddress ? staff?.communicationState : undefined),
          firstRealValue(staff?.permanentCountry, staffSameAsCommunicationAddress ? staff?.communicationCountry : undefined)
        )
        : undefined;
    formValues.permanentCountry =
      getCountryName(firstRealValue(staff?.permanentCountry, staffSameAsCommunicationAddress ? staff?.communicationCountry : undefined)) !== "-"
        ? getCountryName(firstRealValue(staff?.permanentCountry, staffSameAsCommunicationAddress ? staff?.communicationCountry : undefined))
        : undefined;
    formValues.permanentPostalCode =
      staff?.permanentPostalCode ||
      (staffSameAsCommunicationAddress ? staff?.communicationPostalCode : "") ||
      "";
    formValues.workLocation = getValidOptionValue(staff?.workLocation, WORK_LOCATION_OPTIONS);
    formValues.employmentBaseLocation = getEmploymentBaseLocationValue(
      staff,
      employmentModes
    );
    formValues.officeLocation =
      staff?.workLocation === "WFO"
        ? getValidOptionValue(staff?.officeLocation, officeLocationOptions)
        : undefined;
    formValues.gradeBand = getValidOptionValue(staff?.gradeBand, GRADE_BAND_OPTIONS);
    formValues.sameAsCommunicationAddress = staffSameAsCommunicationAddress;
    formValues.dependents = toDependentsFormValues(parseDependents(staff?.dependents));
    formValues.previousEmploymentHistory = normalizePreviousEmploymentHistory(
      parsePreviousEmploymentHistory(staff?.previousEmploymentHistory)
    );
    formValues.totalExperiencePriorToJoining = formatExperienceFromMonths(
      getPreviousEmploymentTotalMonths(formValues.previousEmploymentHistory)
    );
    formValues.skillDetails = buildSkillDetails(
      staff?.skillDetails,
      staff?.primarySkills,
      staff?.secondarySkills,
      staff?.skillProficiencyLevel
    );
    formValues.primarySkills = parseStringArray(staff?.primarySkills);
    formValues.secondarySkills = parseStringArray(staff?.secondarySkills);
    formValues.positionHistory = toPositionHistoryFormValues(parsePositionHistory(staff?.positionHistory));
    formValues.projectTimeline = toProjectTimelineFormValues(parseProjectTimeline(staff?.projectTimeline));
    if (formValues.doj) formValues.doj = dayjs(formValues.doj);
    if (formValues.probationEndDate) {
      formValues.probationEndDate = dayjs(formValues.probationEndDate);
    }
    formValues.contractStartDate = firstRealValue(formValues.contractStartDate, staff?.doj)
      ? dayjs(firstRealValue(formValues.contractStartDate, staff?.doj))
      : null;
    if (formValues.contractEndDate) {
      formValues.contractEndDate = dayjs(formValues.contractEndDate);
    }
    if (formValues.dateOfBirth) formValues.dateOfBirth = dayjs(formValues.dateOfBirth);
    if (formValues.lwd) formValues.lwd = dayjs(formValues.lwd);
    if (formValues.resignationDate) formValues.resignationDate = dayjs(formValues.resignationDate);

    if (formValues.previousSalaryRevisionDate) {
      formValues.previousSalaryRevisionDate = dayjs(formValues.previousSalaryRevisionDate);
    }
    if (formValues.nextSalaryRevisionDate) {
      formValues.nextSalaryRevisionDate = dayjs(formValues.nextSalaryRevisionDate);
    }
    if (formValues.nextAppraisalSalaryRevisionDate) {
      formValues.nextAppraisalSalaryRevisionDate = dayjs(formValues.nextAppraisalSalaryRevisionDate);
    }
    if (formValues.nextPerformanceReviewDate) {
      formValues.nextPerformanceReviewDate = dayjs(formValues.nextPerformanceReviewDate);
    }
    if (formValues.nextReviewDate) {
      formValues.nextReviewDate = dayjs(formValues.nextReviewDate);
    }
    formValues.passportExpiryDate = toFormDate(formValues.passportExpiryDate);
    formValues.visaExpiryDate = toFormDate(formValues.visaExpiryDate);
    if (formValues.effectiveFrom) {
      formValues.effectiveFrom = dayjs(formValues.effectiveFrom);
    }
    if (formValues.issuedDate) {
      formValues.issuedDate = dayjs(formValues.issuedDate);
    }
    formValues.salaryRevisionHistory = toSalaryRevisionFormValues(buildSalaryRevisionHistory(staff));
    formValues.bonusHistory = toBonusFormValues(buildBonusHistory(staff));
    const currentModeFlags = resolveEmploymentModeFlags(
      firstRealValue(staff?.employmentMode, staff?.employmentModeName, employmentMode)
    );
    setShowContractFields(!currentModeFlags.isPermanent);
    setShowVendorField(currentModeFlags.isVendor);
    // Pre-fetch dynamic dropdown options if parent is already set
    if (formValues.entity) {
      fetchAccountListByEntity(formValues.entity);
    }
    if (formValues.accountId) {
      const projectsForClient = projects.filter(p => p.accountId === formValues.accountId);
      setFilteredProjectList(projectsForClient);
    }
    if (formValues.departmentId) {
      fetchSubDepartmentsList(formValues.departmentId);
    }
    form.setFieldsValue(formValues);
  };

  useEffect(() => {
    const key = `${staffId}:${missingField}`;
    if (!missingFieldConfig || loading || staff?.staffId !== staffId || !canUpdateStaff || missingFieldOpened.current === key) return;
    missingFieldOpened.current = key;
    setActiveTab(missingFieldConfig.tab);
    handleSectionEdit(missingFieldConfig.section);
    const timer = window.setTimeout(() => {
      form.scrollToField(missingField!, { block: "center" });
      form.getFieldInstance(missingField!)?.focus?.();
    }, 200);
    return () => window.clearTimeout(timer);
    // Open once per employee/field; lookup option updates must not restart editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId, missingField, loading, staff?.staffId, canUpdateStaff, form]);

  const handleSectionCancel = () => {
    setEditingSection(null);
    form.resetFields();
  };

  const handleSectionSave = () => {
    onSaveEdit();
  };

  const getEditableFieldsByOrder = (fieldKeys: string[]) => {
    const editableFieldByKey = new Map(
      editableFieldsConfig.map((config) => [config.key, config])
    );
    return fieldKeys
      .map((fieldKey) => editableFieldByKey.get(fieldKey))
      .filter(
        (
          config
        ): config is NonNullable<ReturnType<typeof editableFieldByKey.get>> =>
          Boolean(config)
      );
  };
  const getEditableFieldByKey = (fieldKey: string) =>
    editableFieldsConfig.find((config) => config.key === fieldKey);

  const identityBasicsFieldOrder = [
    "employeeId",
    "staffName",
    "gender",
    "localKannadiga",
    "nativeLanguage",
    "dateOfBirth",
    "nationality",
    "maritalStatus",
    "bloodGroup",
    "profilePhoto",
  ];
  const contactDetailsFieldOrder = [
    "email",
    "personalEmail",
    "mobile",
    "emergencyContactName",
    "emergencyContactNumber",
    "emergencyContactRelationship",
  ];
  const addressFieldOrder = [
    "communicationCountry",
    "communicationState",
    "communicationAddressLine",
    "communicationCity",
    "communicationPostalCode",
    "permanentCountry",
    "permanentState",
    "permanentAddressLine",
    "permanentCity",
    "permanentPostalCode",
    "sameAsCommunicationAddress",
  ];
  const communicationAddressFieldOrder = [
    "communicationCountry",
    "communicationState",
    "communicationCity",
    "communicationPostalCode",
    "communicationAddressLine",
  ];
  const permanentAddressFieldOrder = [
    "permanentCountry",
    "permanentState",
    "permanentCity",
    "permanentPostalCode",
    "permanentAddressLine",
  ];
  const statutoryComplianceFieldOrder = isMalaysiaStatutoryEntity
    ? [
      "entity",
      "passportNumber",
      "passportExpiryDate",
      "visaType",
      "visaExpiryDate",
      "myKadNumber",
      "taxIdentificationNumber",
      "epfNumber",
      "socsoNumber",
      "eisNumber",
    ]
    : [
      "entity",
      "pan",
      "aadhaar",
      "uan",
      "pfMemberId",
      "esicIpNumber",
      "professionalTaxApplicableState",
    ];

  const renderPersonal = () => (
    <>
      <Form form={form} layout="vertical" initialValues={staff} onValuesChange={onFormValuesChange}>
        <Section
          title="Identity Basics"
          canEdit={false}
          onEdit={() => handleSectionEdit("basic")}
          isEditing={editingSection === "basic"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {editingSection === "basic" || editingSection === "all" ? (
            <Row gutter={16}>
              {getEditableFieldsByOrder(identityBasicsFieldOrder).map(config => (
                <Col span={12} key={config.key}>
                  {renderEditableField(config)}
                </Col>
              ))}
            </Row>
          ) : (
            <FieldGrid fields={basicInformation} />
          )}
        </Section>
        <Section
          title="Contact Details"
          canEdit={false}
          onEdit={() => handleSectionEdit("contact")}
          isEditing={editingSection === "contact"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {editingSection === "contact" || editingSection === "all" ? (
            <Row gutter={16}>
              {getEditableFieldsByOrder(contactDetailsFieldOrder).map(config => (
                <Col span={12} key={config.key}>
                  {renderEditableField(config)}
                </Col>
              ))}
            </Row>
          ) : (
            <FieldGrid fields={contactDetails} />
          )}
        </Section>
        <Section
          title="Address"
          canEdit={false}
          isEditing={editingSection === "address"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {editingSection === "address" || editingSection === "all" ? (
            <>
              <Row gutter={16}>
                <Col span={12} />
                <Col span={12}>
                  <div className="staff-profile-address-checkbox-row">
                    {(() => {
                      const sameAddressConfig = getEditableFieldByKey("sameAsCommunicationAddress");
                      return sameAddressConfig ? renderEditableField(sameAddressConfig) : null;
                    })()}
                  </div>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12} className="staff-profile-address-column staff-profile-address-column-communication">
                  <h4 className="staff-profile-address-column-title">Communication Address</h4>
                  {communicationAddressFieldOrder.map((fieldKey) => {
                    const config = getEditableFieldByKey(fieldKey);
                    return config ? renderEditableField(config) : null;
                  })}
                </Col>
                <Col span={12} className="staff-profile-address-column">
                  <h4 className="staff-profile-address-column-title">Permanent Address</h4>
                  {permanentAddressFieldOrder.map((fieldKey) => {
                    const config = getEditableFieldByKey(fieldKey);
                    return config ? renderEditableField(config) : null;
                  })}
                </Col>
              </Row>
            </>
          ) : (
            <AddressViewGrid
              communicationFields={communicationAddressDetails}
              permanentFields={permanentAddressDetails}
              sameAsCommunicationAddress={sameAsCommunicationAddress ? "Yes" : "No"}
            />
          )}
        </Section>
        <Section
          title="Statutory & Compliance"
          canEdit={false}
          onEdit={() => handleSectionEdit("statutoryCompliance")}
          isEditing={editingSection === "statutoryCompliance"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {editingSection === "statutoryCompliance" || editingSection === "all" ? (
            <>
              <Row gutter={16}>
                {getEditableFieldsByOrder(statutoryComplianceFieldOrder).map(config => (
                  <Col span={12} key={config.key}>
                    {renderEditableField(config)}
                  </Col>
                ))}
              </Row>
              {isMalaysiaStatutoryEntity && (
                <Row gutter={16}>
                  <Col span={12}><Form.Item label="Passport Copy">{renderLinkedDocumentUpload(PASSPORT_COPY_DOCUMENT_TYPE, staffId!, "Upload Passport Copy")}</Form.Item></Col>
                  <Col span={12}><Form.Item label="Visa Copy">{renderLinkedDocumentUpload(VISA_COPY_DOCUMENT_TYPE, staffId!, "Upload Visa Copy")}</Form.Item></Col>
                </Row>
              )}
            </>
          ) : (
            <>
              <FieldGrid fields={statutoryDetails} />
              {isMalaysiaStatutoryEntity && (
                <Row gutter={16}>
                  <Col span={12}><Form.Item label="Passport Copy">{renderLinkedDocumentUpload(PASSPORT_COPY_DOCUMENT_TYPE, staffId!, "Passport Copy", false)}</Form.Item></Col>
                  <Col span={12}><Form.Item label="Visa Copy">{renderLinkedDocumentUpload(VISA_COPY_DOCUMENT_TYPE, staffId!, "Visa Copy", false)}</Form.Item></Col>
                </Row>
              )}
            </>
          )}
        </Section>
        <Section
          title="Insurance"
          canEdit={false}
          onEdit={() => handleSectionEdit("insurance")}
          isEditing={editingSection === "insurance"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {editingSection === "insurance" || editingSection === "all" ? (
            <Row gutter={16}>
              {getEditableFieldsByOrder(["gtlCoveredForEmployee"]).map(config => (
                <Col span={12} key={config.key}>
                  {renderEditableField(config)}
                </Col>
              ))}
            </Row>
          ) : (
            <FieldGrid fields={[{ label: "GTL Covered for Employee", value: gtlCoveredForEmployee }]} />
          )}
        </Section>
        <Section
          title="Dependents"
          canEdit={false}
          isEditing={editingSection === "dependents"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {editingSection === "dependents" || editingSection === "all" ? (
            <Form.List name="dependents">
              {(fields, { add, remove }) => (
                <div className="staff-profile-dependents-form">
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={12} key={key} align="middle">
                      <Col xs={24} md={6}>
                        <Form.Item
                          {...restField}
                          name={[name, "name"]}
                          label="Dependent Name"
                          normalize={(value) => sanitizeStaffInput(value, "alpha")}
                          rules={[
                            { required: isMalaysiaStatutoryEntity, message: "Please enter Dependent Name" },
                            textOnlyRule("Dependent's Name"),
                          ]}
                        >
                          <Input maxLength={DEFAULT_TEXT_MAX_LENGTH} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={6}>
                        <Form.Item
                          {...restField}
                          name={[name, "relationship"]}
                          label="Dependent Relationship"
                          rules={[{ required: isMalaysiaStatutoryEntity, message: "Please select Dependent Relationship" }]}
                        >
                          <Select
                            placeholder="Select Dependent Relationship"
                            allowClear
                            options={dependentRelationshipOptions}
                          />
                        </Form.Item>
                      </Col>
                      {isMalaysiaStatutoryEntity && (
                        <>
                          <Col xs={24} md={6}>
                            <Form.Item {...restField} name={[name, "passportNumber"]} label="Dependent Passport Number" rules={[{ required: true, message: "Please enter Dependent Passport Number" }, { pattern: /^[A-Za-z0-9]{6,12}$/, message: "Enter a valid passport number" }]}>
                              <Input maxLength={12} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={6}>
                            <Form.Item {...restField} name={[name, "passportExpiryDate"]} label="Dependent Passport Expiry Date" rules={[{ required: true, message: "Please enter Dependent Passport Expiry Date" }]}>
                              <DatePicker format="DD-MMM-YYYY" style={{ width: "100%" }} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={6}>
                            <Form.Item {...restField} name={[name, "visaType"]} label="Dependent Visa Type" rules={[{ required: true, message: "Please select Dependent Visa Type" }]}>
                              <Select placeholder="Select Dependent Visa Type" allowClear options={VISA_TYPE_OPTIONS} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={6}>
                            <Form.Item {...restField} name={[name, "visaExpiryDate"]} label="Dependent Visa Expiry Date" rules={[{ required: true, message: "Please enter Dependent Visa Expiry Date" },
                            ({ getFieldValue }: any) => ({
                              validator(_: any, value: any) {
                                const passportExpiryDate = getFieldValue([
                                  "dependents",
                                  name,
                                  "passportExpiryDate",
                                ]);

                                if (
                                  value &&
                                  passportExpiryDate &&
                                  dayjs(value).isAfter(dayjs(passportExpiryDate), "day")
                                ) {
                                  return Promise.reject(
                                    new Error(
                                      "Dependent Visa Expiry Date cannot be later than Dependent Passport Expiry Date"
                                    )
                                  );
                                }

                                return Promise.resolve();
                              },
                            }),
                            ]}>
                              <DatePicker format="DD-MMM-YYYY" style={{ width: "100%" }} />
                            </Form.Item>
                          </Col>
                        </>
                      )}
                      <Col xs={24} md={6}>
                        <Form.Item
                          {...restField}
                          name={[name, "dateOfBirth"]}
                          label="Dependent Date of Birth"
                          rules={[
                            {
                              validator: async (_, value) => {
                                if (value && dayjs(value).isAfter(dayjs(), "day")) {
                                  return Promise.reject(new Error("Date of Birth cannot be in the future"));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <DatePicker format="DD-MMM-YYYY" style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col xs={20} md={5}>
                        <Form.Item {...restField} name={[name, "coveredInInsurance"]} label="Dependent Covered in Health Insurance">
                          <Select
                            options={[
                              { label: "Yes", value: "Yes" },
                              { label: "No", value: "No" },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={4} md={1}>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ id: createLegacyRecordId("dependent", [Date.now()], fields.length) })}
                  >
                    Add Dependent
                  </Button>
                </div>
              )}
            </Form.List>
          ) : (
            <Table
              pagination={false}
              size="small"
              rowKey={(row) => `${row.name}-${row.relationship}`}
              dataSource={displayDependents}
              columns={[
                { title: "Dependent Name", dataIndex: "name" },
                { title: "Dependent Relationship", dataIndex: "relationship" },
                { title: "Dependent Date of Birth", dataIndex: "dateOfBirth", render: (date) => dateOrFallback(date) },
                { title: "Dependent Covered in Health Insurance", dataIndex: "coveredInInsurance" },
                ...(isMalaysiaStatutoryEntity ? [
                  { title: "Passport Number", dataIndex: "passportNumber" },
                  { title: "Passport Expiry Date", dataIndex: "passportExpiryDate", render: (date: any) => dateOrFallback(date) },
                  { title: "Visa Type", dataIndex: "visaType", render: (value: any) => valueOrFallback(VISA_TYPE_OPTIONS.find((option) => option.value === value)?.label || value) },
                  { title: "Visa Expiry Date", dataIndex: "visaExpiryDate", render: (date: any) => dateOrFallback(date) },
                ] : []),
              ]}
            />
          )}
        </Section>
      </Form>
    </>
  );

  const renderExperienceQualifications = () => {
    const educationalQualificationFieldOrder = [
      "highestQualification",
      "institutionUniversity",
      "yearOfPassing",
      "specialization",
    ];
    return (
      <Form form={form} layout="vertical" initialValues={staff} onValuesChange={onFormValuesChange}>
        <Section
          title="Prior Experience"
          canEdit={false}
          isEditing={editingSection === "priorExperience"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {editingSection === "priorExperience" || editingSection === "all" ? (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <div className="staff-profile-field">
                    <span>Total Experience Prior to Joining</span>
                    <strong>{totalExperiencePriorToJoining}</strong>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="staff-profile-field">
                    <span>Overall Experience</span>
                    <strong>{overallExperience}</strong>
                  </div>
                </Col>
              </Row>
              <div className="staff-profile-prior-history">
                <Form.List name="previousEmploymentHistory">
                  {(fields, { add, remove }) => (
                    <div className="staff-profile-dependents-form">
                      {fields.map(({ key, name, ...restField }) => (
                        <Row gutter={12} key={key} align="middle">
                          <Col xs={24} md={6}>
                            <Form.Item
                              {...restField}
                              name={[name, "previousEmployerName"]}
                              label="Employer Name"
                              normalize={(value) => sanitizeStaffInput(value, "alpha")}
                            >
                              <Input maxLength={DEFAULT_TEXT_MAX_LENGTH} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={6}>
                            <Form.Item
                              {...restField}
                              name={[name, "previousDesignation"]}
                              label="Designation"
                              normalize={(value) => sanitizeStaffInput(value, "alpha")}
                            >
                              <Input maxLength={DEFAULT_TEXT_MAX_LENGTH} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={5}>
                            <Form.Item
                              {...restField}
                              name={[name, "durationYears"]}
                              label="Years"
                              dependencies={["dateOfBirth"]}
                              normalize={(value) => sanitizeStaffInput(value, "number")}
                              rules={[experienceYearsRule]}
                            >
                              <Input inputMode="numeric" maxLength={2} />

                            </Form.Item>
                          </Col>
                          <Col xs={20} md={5}>
                            <Form.Item
                              {...restField}
                              name={[name, "durationMonths"]}
                              label="Months"
                              normalize={(value) => sanitizeStaffInput(value, "number")}
                              rules={[
                                numberOnlyRule("Duration Months"),
                                {
                                  validator: async (_, value) => {
                                    if (!value || Number(value) <= 11) return Promise.resolve();
                                    return Promise.reject(new Error("Months must be between 0 and 11."));
                                  },
                                },
                              ]}
                            >
                              <Input inputMode="numeric" maxLength={2} />
                            </Form.Item>
                          </Col>
                          <Col xs={4} md={1}>
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                            />
                          </Col>
                        </Row>
                      ))}
                      <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})}>
                        Add Previous Employment
                      </Button>
                    </div>
                  )}
                </Form.List>
              </div>
            </>
          ) : (
            <>
              <FieldGrid fields={priorExperienceDetails} />
              <div className="staff-profile-prior-history">
                <Table
                  pagination={false}
                  size="small"
                  rowKey={(row) => `${row.previousEmployerName}-${row.previousDesignation}`}
                  dataSource={displayPreviousEmploymentHistory}
                  columns={[
                    { title: "Employer Name", dataIndex: "previousEmployerName" },
                    { title: "Designation", dataIndex: "previousDesignation" },
                    {
                      title: "Employment Duration",
                      render: (_, row) =>
                        formatExperienceFromMonths(
                          Number(row.durationYears || 0) * 12 +
                          Number(row.durationMonths || 0)
                        ),
                    },
                  ]}
                />
              </div>
            </>
          )}
        </Section>
        <Section
          title="Educational Qualifications"
          canEdit={false}
          isEditing={editingSection === "educationalQualifications"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {editingSection === "educationalQualifications" || editingSection === "all" ? (
            <Row gutter={16}>
              {getEditableFieldsByOrder(educationalQualificationFieldOrder).map((config) => (
                <Col span={12} key={config.key}>
                  {renderEditableField(config)}
                </Col>
              ))}
            </Row>
          ) : (
            <FieldGrid fields={educationalQualificationDetails} />
          )}
        </Section>
        <Section
          title="Skills"
          canEdit={false}
          isEditing={editingSection === "skills"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {editingSection === "skills" || editingSection === "all" ? (
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <h4 className="staff-profile-address-column-title">Primary Skills</h4>
                <Form.List
                  name={["skillDetails", "primarySkills"]}
                  rules={[
                    {
                      validator: async (_, skills) => {
                        if (!skills || skills.length <= MAX_SKILLS_PER_CATEGORY) return Promise.resolve();
                        return Promise.reject(
                          new Error(`Primary Skills cannot exceed ${MAX_SKILLS_PER_CATEGORY}.`)
                        );
                      },
                    },
                  ]}
                >
                  {(fields, { add, remove }, { errors }) => (
                    <div className="staff-profile-dependents-form">
                      {fields.map(({ key, name, ...restField }) => (
                        <Row gutter={12} key={key} align="middle">
                          <Col xs={24} md={11}>
                            <Form.Item
                              {...restField}
                              name={[name, "skill"]}
                              label="Skill"
                              normalize={(value) => sanitizeStaffInput(value, "freeText")}
                            >
                              <Input maxLength={DEFAULT_TEXT_MAX_LENGTH} />
                            </Form.Item>
                          </Col>
                          <Col xs={20} md={11}>
                            <Form.Item
                              {...restField}
                              name={[name, "proficiencyLevel"]}
                              label="Proficiency"
                            >
                              <Select
                                placeholder="Select Proficiency"
                                allowClear
                                options={SKILL_PROFICIENCY_OPTIONS}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={4} md={2}>
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                            />
                          </Col>
                        </Row>
                      ))}
                      <Form.ErrorList errors={errors} />
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        disabled={fields.length >= MAX_SKILLS_PER_CATEGORY}
                        onClick={() => {
                          if (fields.length < MAX_SKILLS_PER_CATEGORY) add({});
                        }}
                      >
                        Add Primary Skill
                      </Button>
                    </div>
                  )}
                </Form.List>
              </Col>
              <Col xs={24} md={12}>
                <h4 className="staff-profile-address-column-title">Secondary Skills</h4>
                <Form.List
                  name={["skillDetails", "secondarySkills"]}
                  rules={[
                    {
                      validator: async (_, skills) => {
                        if (!skills || skills.length <= MAX_SKILLS_PER_CATEGORY) return Promise.resolve();
                        return Promise.reject(
                          new Error(`Secondary Skills cannot exceed ${MAX_SKILLS_PER_CATEGORY}.`)
                        );
                      },
                    },
                  ]}
                >
                  {(fields, { add, remove }, { errors }) => (
                    <div className="staff-profile-dependents-form">
                      {fields.map(({ key, name, ...restField }) => (
                        <Row gutter={12} key={key} align="middle">
                          <Col xs={24} md={11}>
                            <Form.Item
                              {...restField}
                              name={[name, "skill"]}
                              label="Skill"
                              normalize={(value) => sanitizeStaffInput(value, "freeText")}
                            >
                              <Input maxLength={DEFAULT_TEXT_MAX_LENGTH} />
                            </Form.Item>
                          </Col>
                          <Col xs={20} md={11}>
                            <Form.Item
                              {...restField}
                              name={[name, "proficiencyLevel"]}
                              label="Proficiency"
                            >
                              <Select
                                placeholder="Select Proficiency"
                                allowClear
                                options={SKILL_PROFICIENCY_OPTIONS}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={4} md={2}>
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                            />
                          </Col>
                        </Row>
                      ))}
                      <Form.ErrorList errors={errors} />
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        disabled={fields.length >= MAX_SKILLS_PER_CATEGORY}
                        onClick={() => {
                          if (fields.length < MAX_SKILLS_PER_CATEGORY) add({});
                        }}
                      >
                        Add Secondary Skill
                      </Button>
                    </div>
                  )}
                </Form.List>
              </Col>
            </Row>
          ) : (
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <h4 className="staff-profile-address-column-title">Primary Skills</h4>
                <Table
                  pagination={false}
                  size="small"
                  rowKey={(_, index) => `primary-skill-${index}`}
                  dataSource={displaySkillDetails.primarySkills}
                  columns={[
                    { title: "Skill", dataIndex: "skill", render: (value: any) => valueOrFallback(value) },
                    {
                      title: "Proficiency",
                      dataIndex: "proficiencyLevel",
                      render: (value: any) => valueOrFallback(value),
                    },
                  ]}
                  locale={{ emptyText: <Empty description="No primary skills added" /> }}
                />
              </Col>
              <Col xs={24} md={12}>
                <h4 className="staff-profile-address-column-title">Secondary Skills</h4>
                <Table
                  pagination={false}
                  size="small"
                  rowKey={(_, index) => `secondary-skill-${index}`}
                  dataSource={displaySkillDetails.secondarySkills}
                  columns={[
                    { title: "Skill", dataIndex: "skill", render: (value: any) => valueOrFallback(value) },
                    {
                      title: "Proficiency",
                      dataIndex: "proficiencyLevel",
                      render: (value: any) => valueOrFallback(value),
                    },
                  ]}
                  locale={{ emptyText: <Empty description="No secondary skills added" /> }}
                />
              </Col>
            </Row>
          )}
        </Section>
      </Form>
    );
  };

  const renderJob = () => {
    const employmentClassificationFieldOrder = [
      "entity",
      "employmentMode",
      "employeeType",
      "staffStatusId",
    ];
    const vendorContractorFieldOrder = shouldShowStaffVendorFields
      ? ["vendorId", "vendorEmpId", "vendorEmploymentType", "costToRd"]
      : [];
    const selectedEmploymentModeId = form.getFieldValue("employmentMode") || staff?.employmentMode || "";

    const selectedEmploymentMode = employmentModes.find(
      (mode: any) =>
        String(mode.id) === String(selectedEmploymentModeId) ||
        String(mode.name) === String(selectedEmploymentModeId)
    );

    const employmentModeName = selectedEmploymentMode?.name || "";
    const employmentModeIsTraineeOrIntern = /trainee|intern/i.test(String(employmentModeName));

    // Determine vendor employment type (form value when editing, fallback to staff)
    const vendorEmploymentTypeId = form.getFieldValue("vendorEmploymentType") || staff?.vendorEmploymentType || "";
    const vendorEmploymentType = employmentModes.find(
      (mode: any) =>
        String(mode.id) === String(vendorEmploymentTypeId) ||
        String(mode.name) === String(vendorEmploymentTypeId)
    );
    const vendorEmpIsTraineeOrIntern = /trainee|intern/i.test(String(vendorEmploymentType?.name || ""));

    const isVendorConsultant = /vendor consultant/i.test(String(employmentModeName || ""));

    const shouldShowNextReviewDate =
      employmentModeIsTraineeOrIntern ||
      (isVendorConsultant && vendorEmpIsTraineeOrIntern) ||
      !["Direct Consultant", "Permanent", "Vendor Consultant"].includes(employmentModeName);

    const keyDateFieldOrder = [
      "doj",
      "probationEndDate",
      ...(shouldShowNextReviewDate ? ["nextReviewDate"] : []),
      ...(shouldShowStaffContractFields ? ["contractStartDate", "contractEndDate"] : []),
      "nextPerformanceReviewDate",
      "nextAppraisalSalaryRevisionDate",
      "lwd",
      "noticePeriod",
    ];
    const roleDetailFieldOrder = [
      "designation",
      "role",
      "departmentId",
      "subDepartmentId",
      "lineManagerId",
      "clientManager",
      "workLocation",
      "employmentBaseLocation",
      "officeLocation",
      "gradeBand",
    ];
    const editableFieldByKey = new Map(
      editableFieldsConfig.map((config) => [config.key, config])
    );
    const getEditableFields = (fieldOrder: string[]) =>
      fieldOrder
        .map((fieldKey) => editableFieldByKey.get(fieldKey))
        .filter(
          (
            config
          ): config is NonNullable<ReturnType<typeof editableFieldByKey.get>> =>
            Boolean(config)
        );
    const employmentClassificationFields = getEditableFields(employmentClassificationFieldOrder);
    const vendorContractorFields = getEditableFields(vendorContractorFieldOrder);
    const keyDateFields = getEditableFields(keyDateFieldOrder);
    const roleDetailFields = getEditableFields(roleDetailFieldOrder);

    const employmentClassificationLabels = [
      "Entity",
      "Employment Mode",
      "Employee Type",
      "Staff Status",
    ];
    const vendorContractorLabels = [
      "Vendor Name",
      "Vendor Employee ID",
      "Vendor Employment Mode",
      "Cost to RD",
    ];
    const contractDateLabels = ["Contract Start Date", "Contract End Date"];

    const shouldHideNextReviewDate =
      employmentModeIsTraineeOrIntern ||
      (isVendorConsultant && vendorEmpIsTraineeOrIntern);

    //return shouldHideNextReviewDate ? [] : ["Next Review Date"];
    const shoulNextReviewDate = shouldHideNextReviewDate ? ["Next Review Date"] : [];
    const showInternDates = /^intern$/i.test(employmentModeName) ||
      (isVendorConsultant && /^intern$/i.test(String(vendorEmploymentType?.name || vendorEmploymentTypeId)));
    const showTraineeDates = /^trainee$/i.test(employmentModeName) ||
      (isVendorConsultant && /^trainee$/i.test(String(vendorEmploymentType?.name || vendorEmploymentTypeId)));
    const isEditingKeyDates = editingSection === "work" || editingSection === "all";
    const joiningDateValue = isEditingKeyDates
      ? (watchedJoiningDate !== undefined ? watchedJoiningDate : form.getFieldValue("doj") ?? staff?.doj)
      : staff?.doj;
    const periodStart = joiningDateValue && dayjs(joiningDateValue).isValid()
      ? dayjs(joiningDateValue)
      : null;
    const employmentPeriodFields = showInternDates
      ? [
        { key: "internStartDate", label: "Intern Start Date", months: 0 },
        { key: "internEndDate", label: "Intern End Date", months: 6 },
      ]
      : showTraineeDates
        ? [
          { key: "traineeStartDate", label: "Trainee Start Date", months: 0 },
          { key: "traineeEndDate", label: "Trainee End Date", months: 11 },
        ]
        : [];
    const employmentPeriodDetails = employmentPeriodFields.map((field) => {
      const calculatedDate = periodStart?.add(field.months, "month") ?? null;
      const dateValue = !isEditingKeyDates && staff?.[field.key]
        ? dayjs(staff[field.key])
        : calculatedDate;
      return { ...field, dateValue, value: dateValue ? dateValue.format("DD-MMM-YYYY") : "-" };
    });
    const keyDateLabels = [
      "Date of Joining",
      "Probation End Date",
      ...employmentPeriodDetails.map((field) => field.label),
      ...shoulNextReviewDate,
      ...contractDateLabels,
      "Next Performance Review Date",
      "Next Appraisal/Salary Revision Date",
      "Last Working Day",
      "Notice Period",

    ];
    const employmentClassificationDetails = visibleOfficeWorkDetails.filter((field) =>
      employmentClassificationLabels.includes(field.label)
    );
    const vendorContractorDetails = shouldShowStaffVendorFields
      ? visibleOfficeWorkDetails.filter((field) =>
        vendorContractorLabels.includes(field.label)
      )
      : [];


    const keyDateDetails = [...visibleOfficeWorkDetails, ...employmentPeriodDetails].filter(
      (field) =>
        keyDateLabels.includes(field.label) &&
        (
          shouldShowStaffContractFields ||
          !contractDateLabels.includes(field.label)
        )
    ).sort((first, second) => keyDateLabels.indexOf(first.label) - keyDateLabels.indexOf(second.label));

    const roleDetails = visibleOfficeWorkDetails.filter(
      (field) =>
        !employmentClassificationLabels.includes(field.label) &&
        !vendorContractorLabels.includes(field.label) &&
        !keyDateLabels.includes(field.label) &&
        field.label !== "Next Review Date"
    );

    return (
      <>
        <Form form={form} layout="vertical" initialValues={staff} onValuesChange={onFormValuesChange}>
          <Section
            title="Employment Classification"
            canEdit={false}
            onEdit={() => handleSectionEdit("work")}
            isEditing={editingSection === "work"}
            onSave={handleSectionSave} // This will trigger the global onSaveEdit
            onCancel={handleCancelClick}
            loading={loading}
          >
            {editingSection === "work" || editingSection === "all" ? (
              <Row gutter={16}>
                {employmentClassificationFields.map(config => (
                  <Col span={12} key={config.key}>
                    {renderEditableField(config)}
                  </Col>
                ))}
              </Row>
            ) : (
              <FieldGrid fields={employmentClassificationDetails} />
            )}
          </Section>
          {shouldShowStaffVendorFields && (
            <Section
              title="Vendor / Contractor Details"
              canEdit={false}
              onEdit={() => handleSectionEdit("work")}
              isEditing={editingSection === "work"}
              onSave={handleSectionSave}
              onCancel={handleCancelClick}
              loading={loading}
            >
              {editingSection === "work" || editingSection === "all" ? (
                <Row gutter={16}>
                  {vendorContractorFields.map(config => (
                    <Col span={12} key={config.key}>
                      {renderEditableField(config)}
                    </Col>
                  ))}
                </Row>
              ) : (
                <FieldGrid fields={vendorContractorDetails} />
              )}
            </Section>
          )}
          <Section
            title="Key Dates"
            canEdit={false}
            onEdit={() => handleSectionEdit("work")}
            isEditing={editingSection === "work"}
            onSave={handleSectionSave}
            onCancel={handleCancelClick}
            loading={loading}
          >
            {editingSection === "work" || editingSection === "all" ? (
              <Row gutter={16}>
                {keyDateFields.slice(0, 2).map(config => (
                  <Col span={12} key={config.key}>
                    {renderEditableField(config)}
                  </Col>
                ))}
                {employmentPeriodDetails.map((field) => (
                  <Col span={12} key={field.key}>
                    <Form.Item label={field.label}>
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD-MMM-YYYY"
                        value={field.dateValue}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                ))}
                {keyDateFields.slice(2).map(config => (
                  <Col span={12} key={config.key}>
                    {renderEditableField(config)}
                  </Col>
                ))}
              </Row>
            ) : (
              <FieldGrid fields={keyDateDetails} />
            )}
          </Section>
          <Section
            title="Organization Structure"
            canEdit={false}
            onEdit={() => handleSectionEdit("work")}
            isEditing={editingSection === "work"}
            onSave={handleSectionSave}
            onCancel={handleCancelClick}
            loading={loading}
          >
            {editingSection === "work" || editingSection === "all" ? (
              <Row gutter={16}>
                {roleDetailFields.map(config =>
                  config.key === "officeLocation" ? (
                    <Form.Item
                      noStyle
                      shouldUpdate={(previousValues, currentValues) =>
                        previousValues.workLocation !== currentValues.workLocation ||
                        previousValues.entity !== currentValues.entity
                      }
                      key={config.key}
                    >
                      {({ getFieldValue }) =>
                        getFieldValue("workLocation") === "WFO" ? (
                          <Col span={12}>
                            {renderEditableField(config)}
                          </Col>
                        ) : null
                      }
                    </Form.Item>
                  ) : (
                    <Col span={12} key={config.key}>
                      {renderEditableField(config)}
                    </Col>
                  )
                )}
              </Row>
            ) : (
              <FieldGrid fields={roleDetails} />
            )}
          </Section>
        </Form>
        <Form form={form} layout="vertical" initialValues={staff} onValuesChange={onFormValuesChange}>
          <Section
            title="Position History"
            canEdit={false}
            isEditing={editingSection === "positionHistory"}
            onSave={handleSectionSave}
            onCancel={handleCancelClick}
            loading={loading}
          >
            {editingSection === "positionHistory" || editingSection === "all" ? (
              <Form.List
                name="positionHistory"
                rules={[
                  {
                    validator: async (_, rows) => {
                      const incompleteRow = toArray(rows).find(
                        (row) =>
                          row &&
                          (row.effectiveFrom || row.designation || row.reportingTo) &&
                          (!row.effectiveFrom || !row.designation || !row.reportingTo)
                      );

                      if (!incompleteRow) return Promise.resolve();
                      return Promise.reject(
                        new Error("Please complete Effective From, Designation, and Reporting To for every Position History row.")
                      );
                    },
                  },
                ]}
              >
                {(fields, { add, remove }, { errors }) => (
                  <div className="staff-profile-dependents-form">
                    {fields.map(({ key, name, ...restField }) => (
                      <Row gutter={12} key={key} align="middle">
                        <Col xs={24} md={5}>
                          <Form.Item
                            {...restField}
                            name={[name, "effectiveFrom"]}
                            label="Effective From"
                            rules={[
                              { required: true, message: "Please select Effective From" },
                              ({ getFieldValue }: any) => ({
                                validator(_: any, value: any) {
                                  const doj = getFieldValue("doj") || staff?.doj;

                                  if (
                                    value &&
                                    doj &&
                                    dayjs(value).isBefore(dayjs(doj), "day")
                                  ) {
                                    return Promise.reject(
                                      new Error("Effective From cannot be earlier than Date of Joining")
                                    );
                                  }

                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <DatePicker
                              style={{ width: "100%" }}
                              format="DD-MMM-YYYY"
                              disabledDate={(current) =>
                                !!(
                                  current &&
                                  (form.getFieldValue("doj") || staff?.doj) &&
                                  current < dayjs(form.getFieldValue("doj") || staff?.doj).startOf("day")
                                )
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, "designation"]}
                            label="Designation"
                            normalize={(value) => sanitizeStaffInput(value, "alpha")}
                            rules={[
                              { required: true, message: "Please enter Designation" },
                              textOnlyRule("Designation"),
                            ]}
                          >
                            <Input maxLength={DEFAULT_TEXT_MAX_LENGTH} />
                          </Form.Item>
                        </Col>
                        <Col xs={20} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, "reportingTo"]}
                            label="Reporting To"
                            rules={[{ required: true, message: "Please select Reporting To" }]}
                          >
                            <Select
                              placeholder="Select Reporting To"
                              allowClear
                              showSearch
                              optionFilterProp="label"
                              options={positionHistoryReportingToOptions}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={4} md={1}>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                          />
                        </Col>
                      </Row>
                    ))}
                    <Form.ErrorList errors={errors} />
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add({})}
                    >
                      Add Position
                    </Button>
                  </div>
                )}
              </Form.List>
            ) : (
              <Table
                pagination={false}
                size="small"
                rowKey={(row) => `${row.effectiveFrom}-${row.designation}-${row.reportingTo}`}
                dataSource={displayPositionHistory}
                columns={[
                  {
                    title: "Effective From",
                    dataIndex: "effectiveFrom",
                    render: (value: string) => dateOrFallback(value),
                  },
                  { title: "Designation", dataIndex: "designation" },
                  { title: "Reporting To", dataIndex: "reportingTo" },
                ]}
              />
            )}
          </Section>
        </Form>
      </>
    );
  };

  const renderOnboarding = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={13}>
        <Section title="Onboarding Checklist">
          <Tag color="success" className="staff-profile-inline-tag">
            Completed {mock.onboarding.completedOn}
          </Tag>
          <div className="staff-profile-checklist">
            {mock.onboarding.checklist.map((item) => (
              <div className="staff-profile-checklist-item" key={item.title}>
                <CheckCircleFilled />
                <span>{item.title}</span>
                <small>{item.date}</small>
              </div>
            ))}
          </div>
        </Section>
      </Col>
      <Col xs={24} lg={11}>
        <Section title="Offer & Pre-joining">
          <FieldGrid fields={mock.onboarding.offerPreJoining} />
        </Section>
        <Section title="Background Verification">
          <FieldGrid fields={mock.onboarding.backgroundVerification} />
        </Section>
        <Section title="Induction">
          <FieldGrid fields={mock.onboarding.induction} />
        </Section>
      </Col>
    </Row>
  );

  const renderDocuments = () => (
    <Row gutter={[16, 16]} className="staff-profile-documents">
      <Col xs={24} md={7} lg={5}>
        <div className="staff-profile-document-nav">
          {loadingDocumentTypes ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : documentTypes.length > 0 ? (
            documentTypes.map((docType) => (
              <button
                type="button"
                key={docType.id}
                className={docType.id === activeDocument.id ? "active" : ""}
                onClick={() => setActiveDocumentCategoryId(docType.id)}
              >
                <FolderOpenOutlined />
                <span>{docType.documentTypeName}</span>
                <small>{documentCounts[docType.id] || 0}</small>
              </button>
            ))
          ) : ( // Corrected closing parenthesis for the ternary operator
            <Empty description="No document types found" />
          )}
        </div>
      </Col>
      <Col xs={24} md={17} lg={19}>
        <Section title={activeDocument.documentTypeName}> {/* Changed to documentTypeName */}
          {accessAllowed(myPermissions, allPermissions.FILE_UPLOAD_STAFF) && ( // Assuming a permission for uploading documents */}
            <Button
              size="small"
              icon={<UploadOutlined />}
              className="staff-profile-section-action"
              onClick={() => openDocumentUpload()}
            >
              Upload File
            </Button>
          )}
          <Table
            className="staff-profile-documents-table"
            pagination={false}
            size="small"
            rowKey="id" // Assuming each document has a unique 'id'
            dataSource={staffDocuments}
            loading={loadingStaffDocuments}
            scroll={{ x: 1060 }}
            columns={[
              {
                title: "File Name",
                dataIndex: "fileName",
                width: 330,
                render: (fileName: string) => (
                  <Space className="staff-profile-document-file-name">
                    <FileTextOutlined />
                    <span>{fileName}</span>
                  </Space>
                ),
              },
              { title: "Document Type", dataIndex: "documentTypeName", width: 150 },
              { title: "Document Sub Type", dataIndex: "documentSubTypeName", width: 160 },
              { title: "Related To", width: 110, render: (_: any, record: any) => getDocumentRelatedToLabel(record) },
              // API aliases this as `uploadedDate` (documents.service.ts select list).
              { title: "Uploaded Date", dataIndex: "uploadedDate", width: 150, render: (date: string) => formatDate(date, "DD-MMM-YYYY") },
              { title: "Uploaded By", dataIndex: "uploadedBy", width: 150 },
              {
                title: "Action",
                width: 150,
                render: (_: any, record: any) => (
                  <Space size="small">
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      loading={documentActionLoading === record.id}
                      onClick={() => handleDownloadDocument(record)}
                    >

                    </Button>
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      loading={documentActionLoading === record.id}
                      onClick={() => handleDeleteDocument(record)}
                    >

                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        </Section>
      </Col>
    </Row>
  );

  const renderLetters = () => (
    <Section title="Letters Issued">
      <Button type="primary" size="small" className="staff-profile-section-action">
        Generate Letter
      </Button>
      <Table
        pagination={false}
        size="small"
        rowKey="referenceNo"
        dataSource={mock.letters.issued}
        columns={[
          {
            title: "Letter",
            dataIndex: "letter",
            render: (letter: string) => (
              <Space>
                <span className="staff-profile-file-icon">
                  <FileTextOutlined />
                </span>
                {letter}
              </Space>
            ),
          },
          { title: "Reference No.", dataIndex: "referenceNo" },
          { title: "Issued On", dataIndex: "issuedOn" },
          { title: "Issued By", dataIndex: "issuedBy" },
          {
            title: "Action",
            align: "right",
            render: () => <Button type="link">Download</Button>,
          },
        ]}
      />
    </Section>
  );


  const renderAssets = () => (
    <Section title="Assets Issued">
      <Button type="primary" size="small" className="staff-profile-section-action">
        Assign Asset
      </Button>
      <Table
        pagination={false}
        size="small"
        rowKey="referenceNo"
        dataSource={mock.assets.issued}
        columns={[
          {
            title: "Asset",
            dataIndex: "asset",
          },
          { title: "Asset Tag.", dataIndex: "assetTag" },
          { title: "Serial No", dataIndex: "serialNo" },
          { title: "Issued On", dataIndex: "issuedOn" },
          { title: "Returned On", dataIndex: "returnOn" },
          {
            title: "status",
            dataIndex: "status"
            ,
          },
        ]}
      />
    </Section>
  );

  const renderLeaveAndAttendance = () => (
    <>
      <Row gutter={[16, 16]} className="staff-profile-metrics">
        {mock.leave.balance.map((leave) => (
          <Col xs={24} sm={12} lg={6} key={leave.type}>
            <div className="staff-profile-leave-card">
              <div className="staff-profile-leave-card-header">
                <span>{leave.type}</span>
                <strong>
                  {leave.used} / {leave.total}
                </strong>
              </div>
              <Progress
                percent={(leave.used / leave.total) * 100}
                showInfo={false}
                strokeColor="var(--primary)"
              />
            </div>
          </Col>
        ))}
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={15}>
          <Section title="Recent Leave Requests">
            <Table
              pagination={false}
              size="small"
              rowKey={(row) => `${row.type}-${row.dates}`}
              dataSource={mock.leave.recentRequests}
              columns={[
                { title: "Type", dataIndex: "type" },
                { title: "Dates", dataIndex: "dates" },
                { title: "Days", dataIndex: "days" },
                {
                  title: "Status",
                  dataIndex: "status",
                  render: (status: string) => (
                    <Tag color={status === "Pending" ? "warning" : "success"}>
                      {status}
                    </Tag>
                  ),
                },
              ]}
            />
          </Section>
        </Col>
        <Col xs={24} lg={9}>
          <Section title="Attendance Summary — FY 2025-26">
            <div className="staff-profile-field-grid staff-profile-field-grid-dense">
              {mock.leave.attendanceSummary.map((item) => (
                <div
                  className="staff-profile-field"
                  key={item.label}
                  style={{
                    borderBottom: "1px solid var(--border-color)",
                    paddingBottom: "0.75rem",
                  }}
                >
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </Section>
        </Col>
      </Row>
    </>
  );

  const renderPerformance = () => (
    <>
      <Section title="Appraisal History">
        <Table
          pagination={false}
          size="small"
          rowKey="cycle"
          dataSource={mock.performance.appraisalHistory}
          columns={[
            { title: "Cycle", dataIndex: "cycle" },
            { title: "Reviewed By", dataIndex: "reviewedBy" },
            {
              title: "Rating",
              dataIndex: "rating",
              render: (rating: string) => (
                <strong className="staff-profile-rating">{rating}</strong>
              ),
            },
            { title: "Remarks", dataIndex: "remarks" },
            { title: "Outcome", dataIndex: "outcome" },
          ]}
        />
      </Section>
      <Section title="Goals — FY 2025 Cycle">
        <div className="staff-profile-goals-grid">
          {mock.performance.goals.map((goal) => (
            <Row align="middle">
              <Col flex="auto">
                <div className="goal-title">{goal.title}</div>
                <div className="goal-description">
                  {goal.description}
                </div>
              </Col>
              <Col style={{ width: 180 }}>
                <Progress
                  percent={goal.progress}
                  size="small"
                />
              </Col>
            </Row>

          ))}
        </div>
      </Section>
    </>
  );
  const renderTimeline = () => {
    const accountNameById = new Map<string, string>();
    const timelineAccountOptions = new Map<string, { label: string; value: string }>();
    const addTimelineAccount = (account: any) => {
      if (!account?.accountId || !account?.accountName) return;

      const accountId = String(account.accountId);
      const accountName = String(account.accountName);
      accountNameById.set(accountId, accountName);
      timelineAccountOptions.set(accountId, {
        label: accountName,
        value: accountId,
      });
    };
    allAccountList.forEach(addTimelineAccount);
    accountList.forEach((account: any) => {
      addTimelineAccount(account);
    });
    projects.forEach((projectItem: any) => {
      if (projectItem?.accountId && projectItem?.accountName) {
        accountNameById.set(String(projectItem.accountId), projectItem.accountName);
        if (!timelineAccountOptions.has(String(projectItem.accountId))) {
          timelineAccountOptions.set(String(projectItem.accountId), {
            label: projectItem.accountName,
            value: String(projectItem.accountId),
          });
        }
      }
    });
    displayProjectTimeline.forEach((timelineItem: any) => {
      if (timelineItem?.accountId && timelineItem?.accountName) {
        accountNameById.set(String(timelineItem.accountId), timelineItem.accountName);
        if (!timelineAccountOptions.has(String(timelineItem.accountId))) {
          timelineAccountOptions.set(String(timelineItem.accountId), {
            label: timelineItem.accountName,
            value: String(timelineItem.accountId),
          });
        }
      }
    });
    const accountOptions = Array.from(timelineAccountOptions.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    const openProjectTimelineCreate = () => {
      handleSectionEdit("projectTimeline");
      form.setFieldsValue({ projectTimeline: [{}] });
    };

    return (
      <Form form={form} layout="vertical" initialValues={staff} onValuesChange={onFormValuesChange}>
        <Section
          title="Project Timeline"
          canEdit={false}
          isEditing={false}
          loading={loading || isUpdating}
        >
          {editingSection === "projectTimeline" || editingSection === "all" ? (
            <Form.List name="projectTimeline">
              {(fields, { add, remove }) => (
                <div className="staff-profile-dependents-form">
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={12} key={key} align="middle">
                      <Col xs={24} md={6}>
                        <Form.Item
                          {...restField}
                          name={[name, "date"]}
                          label="Date"
                          rules={[
                            { required: true, message: "Please select date" },
                            ({ getFieldValue }: any) => ({
                              validator(_: any, value: any) {
                                const doj = getFieldValue("doj") || staff?.doj;

                                if (
                                  value &&
                                  doj &&
                                  dayjs(value).isBefore(dayjs(doj), "day")
                                ) {
                                  return Promise.reject(
                                    new Error("Date cannot be earlier than Date of Joining")
                                  );
                                }

                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <DatePicker
                            style={{ width: "100%" }}
                            format="DD-MMM-YYYY"
                            disabledDate={(current) =>
                              !!(
                                current &&
                                (form.getFieldValue("doj") || staff?.doj) &&
                                current < dayjs(form.getFieldValue("doj") || staff?.doj).startOf("day")
                              )
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={20} md={8}>
                        <Form.Item
                          {...restField}
                          name={[name, "accountId"]}
                          label="Client"
                          rules={[{ required: true, message: "Please select client" }]}
                        >
                          <Select
                            placeholder="Select Client"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={accountOptions}
                            onChange={(value) => {
                              const rows = form.getFieldValue("projectTimeline") || [];
                              rows[name] = {
                                ...rows[name],
                                accountId: value,
                                accountName: accountNameById.get(String(value)) || "",
                              };
                              form.setFieldsValue({ projectTimeline: rows });
                              handleFormValuesChange({ projectTimeline: rows }, form.getFieldsValue());
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={4} md={1}>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({}, 0)}
                  >
                    Add Timeline
                  </Button>
                </div>
              )}
            </Form.List>
          ) : displayProjectTimeline.length > 0 ? (
            <div className="project-timeline-list">
              {displayProjectTimeline.map((item, index) => {
                const accountName = item.accountName || accountNameById.get(String(item.accountId || "")) || "";
                return (
                  <div
                    key={`${item.date}-${item.accountId}-${index}`}
                    className="project-timeline-item"
                  >
                    <div className="project-timeline-date">{dateOrFallback(item.date)}</div>
                    <div className="project-timeline-marker" />
                    <div className="project-timeline-content">
                      {accountName || "-"}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <Empty description="No project timeline added">
              {canUpdateStaff && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openProjectTimelineCreate}
                >
                  Add Project Timeline
                </Button>
              )}
            </Empty>
          )}
        </Section>
      </Form>
    );
  };

  const renderExit = () => {
    const exitInformationFieldKeys = [
      "staffStatusId",
      "lwd",
      "resignationDate",
      "exitReason",
      "exitType",
      "rehireEligible",
      "fullAndFinalSettlementStatus",
    ];
    const editableFieldByKey = new Map(
      editableFieldsConfig.map((config) => [config.key, config])
    );
    const exitInformationFields = exitInformationFieldKeys
      .map((fieldKey) => editableFieldByKey.get(fieldKey))
      .filter(
        (
          config
        ): config is NonNullable<ReturnType<typeof editableFieldByKey.get>> =>
          Boolean(config)
      );
    const isExitEditing =
      editingSection === "exitInformation" || editingSection === "all";
    const readOnlyExitFieldKeys = new Set(["staffStatusId", "lwd"]);
    const exitInformationDetails = exitInformationFields.map((field) => ({
      label: field.label,
      value: field.oldValue,
    }));

    return (
      <Form form={form} layout="vertical" initialValues={staff} onValuesChange={onFormValuesChange}>
        <Section
          title="Exit Information"
          canEdit={false}
          onEdit={() => handleSectionEdit("exitInformation")}
          isEditing={editingSection === "exitInformation"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {isExitEditing ? (
            <>
              <Row gutter={16}>
                {exitInformationFields.map((config) => (
                  <Col span={12} key={config.key}>
                    {renderEditableField(
                      readOnlyExitFieldKeys.has(config.key)
                        ? { ...config, disabled: true }
                        : config
                    )}
                  </Col>
                ))}
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Upload Document">
                    {renderLinkedDocumentUpload(
                      EXIT_INFORMATION_DOCUMENT_TYPE,
                      EXIT_INFORMATION_RECORD_ID,
                      "Upload Exit Information Document"
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </>
          ) : (
            <>
              <FieldGrid fields={exitInformationDetails} />
              <Row gutter={16}>
                <Col span={12}>
                  <div className="staff-profile-field">
                    <span>Document</span>
                    <strong>
                      {renderLinkedDocumentUpload(
                        EXIT_INFORMATION_DOCUMENT_TYPE,
                        EXIT_INFORMATION_RECORD_ID,
                        "Upload Exit Information Document",
                        false
                      )}
                    </strong>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Section>
      </Form>
    );
  };

  const renderTab = (key: StaffProfileTabKey) => {
    switch (key) {
      case "overview":
        return renderOverview();
      case "personal":
        return renderPersonal();
      case "experienceQualifications":
        return renderExperienceQualifications();
      case "job":
        return renderJob();
      case "onboarding":
        return renderOnboarding();
      case "documents":
        return renderDocuments();
      case "letters":
        return renderLetters();
      case "compensation":
        return renderCompensation();
      case "assets":
        return renderAssets();
      case "leave":
        return renderLeaveAndAttendance();
      case "performance":
        return renderPerformance();
      case "timeline":
        return renderTimeline();
      case "exit":
        return renderExit();
      default:
        return <Empty />;
    }
  };

  return (
    <div className="staff-profile-page">
      {missingFieldsReturnTo && <p>
        Complete {missingFieldConfig?.label}{missingField === "lineManagerId" ? " (RD Manager)" : ""} below. Saving returns to Missing Information.
      </p>}
      <div className="staff-profile-topbar">
        <Button
          type="link"
          icon={<LeftOutlined />}
          onClick={() => navigate(missingFieldsReturnTo || ROUTE_MANAGE_STAFF)}
        >
          {missingFieldsReturnTo ? "Back to Missing Information" : "Back to Manage Staff"}
        </Button>
        {/* <div className="staff-profile-preview-state">
          <span>Preview State</span>
          <button
            type="button"
            className={`staff-profile-preview-state-button staff-profile-preview-state-button-${getStaffStatusColor("ACTIVE")}${!isExitPreview ? " active" : ""}`}
            onClick={() => switchProfileTab("overview")}
          >
            Active
          </button>
          <button
            type="button"
            className={`staff-profile-preview-state-button staff-profile-preview-state-button-${getStaffStatusColor("INACTIVE")}${isExitPreview ? " active" : ""}`}
            onClick={() => switchProfileTab("exit")}
          >
            Exited
          </button>
        </div> */}
      </div>

      {loading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          <div className="staff-profile-header-card">
            <div className="staff-profile-avatar">
              {staff?.profilePhoto ? (
                <img src={staff.profilePhoto} alt={`${staffName} profile`} />
              ) : (
                initialsFor(staffName)
              )}
            </div>
            <div className="staff-profile-identity">
              <div className="staff-profile-title-row">
                <h1>{staffName}</h1>
                <Tag color={getStaffStatusColor(status)}>{status}</Tag>
              </div>
              <p>
                {[designation, role].filter((value) => value && value !== "-").join(" - ") || "-"}
              </p>
              <div className="staff-profile-meta">
                <span>
                  Employee ID
                  {/*  {employeeIdLinksOptions.length > 1 ? (
                    <Select
                      value={employeeId}
                      style={{ width: 220, marginLeft: 8 }}
                      options={employeeIdLinksOptions}
                      onChange={(nextEmployeeId: string) => {
                        const selectedLink = employeeIdLinks.find((link) => link.employeeId === nextEmployeeId);
                        if (!selectedLink || !selectedLink.staffId) return;
                        const nextStaffId = String(selectedLink.staffId);
                        if (nextStaffId === String(staff?.staffId)) return;
                        navigate(ROUTE_MANAGE_STAFF.replace(":staffId", nextStaffId), {
                          replace: true,
                          state: routedStaff || {},
                        });
                      }}
                    />
                  ) : (
                    <strong> {employeeId}</strong>
                  )} */}
                  {employeeIdLinksOptions.length > 1 ? (
                    <Dropdown
                      trigger={["hover"]}
                      menu={{
                        items: employeeIdLinks.map((link) => ({
                          key: link.employeeId,
                          label: (
                            <Space>
                              {link.isPrimary ? (
                                <StarFilled style={{ color: "#f59e0b" }} />
                              ) : (
                                <LinkOutlined style={{ color: "#64748b" }} />
                              )}
                              <span>{link.employeeId}</span>
                              <Tag color={link.isPrimary ? "gold" : "default"}>
                                {link.isPrimary ? "Primary" : "Child"}
                              </Tag>
                            </Space>
                          ),
                          onClick: () => {
                            const nextStaffId = String(link.staffId);

                            if (!link.staffId || nextStaffId === String(staff?.staffId)) {
                              return;
                            }

                            navigate(
                              ROUTE_STAFF_PROFILE.replace(":staffId", nextStaffId),
                              {
                                replace: true,
                                state: routedStaff || {},
                              }
                            );
                          },
                        })),
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          marginLeft: 8,
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        {employeeId}
                        <DownOutlined style={{ fontSize: 10 }} />
                      </span>
                    </Dropdown>
                  ) : (
                    <strong> {employeeId}</strong>
                  )}
                </span>
                <span>Department <strong>{department}</strong></span>
                <span>RD Manager <strong>{lineManager}</strong></span>
                <span>Date of Joining <strong>{joiningDate}</strong></span>
                <span>Tenure <strong>{tenure}</strong></span>
                <span>Overall Experience <strong>{overallExperience}</strong></span>
                <span>Client <strong>{client}</strong></span>
                <span>Project <strong>{project}</strong></span>
              </div>
            </div>
            {canUpdateStaff && (
              <div className="staff-profile-actions">
                {/* <Button icon={<DownloadOutlined />}>Download Profile</Button> */}
                {editingSection ? (
                  <Space>
                    <Button onClick={handleCancelClick}>Cancel</Button>
                    <Button type="primary" onClick={onSaveEdit} loading={loading} disabled={!hasPendingStaffChanges}>
                      Save Details
                    </Button>
                  </Space>
                ) : (
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => handleSectionEdit("all")}
                  >
                    Edit Staff Details
                  </Button>
                )}
              </div>
            )}
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={(key) => handleTabChange(key as StaffProfileTabKey)}
            className="staff-profile-tabs"
          >
            {profileTabs.map((tab) => (
              <TabPane tab={tab.label} key={tab.key}>
                <div className="staff-profile-tab-panel">
                  {tab.key !== activeTab ? null : tabLoading ? (
                    <div className="staff-profile-tab-loader">
                      <Skeleton active paragraph={{ rows: 6 }} />
                    </div>
                  ) : (
                    renderTab(tab.key)
                  )}
                </div>
              </TabPane>
            ))}
          </Tabs>
        </>
      )}

      <Modal
        title="Confirm Changes"
        open={showConfirmationModal}
        width={720}
        onCancel={() => {
          setShowConfirmationModal(false);
          setPendingLocation(null); // Clear pending navigation on cancel
        }}
        footer={[
          <Button key="cancel" onClick={() => setShowConfirmationModal(false)}>
            Cancel
          </Button>,
          <Button
            key="update"
            type="primary"
            loading={isUpdating}
            onClick={handleUpdateStaff}
          >
            Update
          </Button>,
        ]}
      >
        <p>Please review the changes before updating:</p>
        <Table
          rowKey="fieldName"
          dataSource={changesToConfirm}
          columns={[
            { title: "Field Name", dataIndex: "fieldName", width: 170 },
            {
              title: "Old Value",
              dataIndex: "oldValue",
              render: (value: any) => <span className="staff-profile-change-cell">{valueOrFallback(value)}</span>,
            },
            {
              title: "New Value",
              dataIndex: "newValue",
              render: (value: any) => <span className="staff-profile-change-cell">{valueOrFallback(value)}</span>,
            },
          ]}
          pagination={false}
        />
      </Modal>

      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <ExclamationCircleOutlined style={{ fontSize: '22px', color: 'var(--warning)', marginRight: 8 }} />
            Save and Continue
          </div>
        }
        open={showUnsavedChangesModal}
        onCancel={() => setShowUnsavedChangesModal(false)}
        centered
        footer={[
          <Button key="cancel" onClick={() => setShowUnsavedChangesModal(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSaveAndContinue}
          >
            Save & Continue
          </Button>,
        ]}
      >
        <p style={{ textAlign: 'center', marginTop: 8 }}>You have unsaved changes. Do you want to save your changes and continue?</p>
      </Modal>

      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <ExclamationCircleOutlined style={{ fontSize: '22px', color: 'var(--warning)', marginRight: 8 }} />
            Discard Changes
          </div>
        }
        open={showDiscardChangesModal}
        onCancel={() => setShowDiscardChangesModal(false)}
        centered
        footer={[
          <Button key="cancel" onClick={() => setShowDiscardChangesModal(false)}>
            Cancel
          </Button>,
          <Button
            key="discard"
            type="primary"
            danger
            onClick={() => { onCancelEdit(); setShowDiscardChangesModal(false); setEditingSection(null); }}
          >
            Discard Changes
          </Button>,
        ]}
      >
        <p style={{ textAlign: 'center', marginTop: 8 }}>You have unsaved changes. Are you sure you want to discard them?</p>
      </Modal>

      <UploadDocumentModal
        visible={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setDocumentUploadContext(null);
        }}
        staffId={staffId!}
        documentTypes={documentTypes}
        onSubmit={handleDocumentUploadSuccess}
        relatedRecordType={documentUploadContext?.relatedRecordType}
        relatedRecordId={documentUploadContext?.relatedRecordId}
        title={documentUploadContext?.title}
      />

    </div>
  );
};

export default StaffProfilePage;
